import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Loader2, Calendar, Search, ChevronDown, Undo2 } from 'lucide-react';
import { Button } from '../ui/button';
import { api } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { isSectorTransaction, getSectorTransactionCode } from '../../helpers/SectorTransactionHelper';
import { menuPreferencesService } from '../../services/menuPreferences';
import { STOCK_LIST } from '../../data/stockList';
import { useProfile } from '../../contexts/ProfileContext';
import { hasBootcampAccess } from '../../utils/subscriptionAccess';
import { hasAdminPrivileges, NEW_BROKER_PRIVILEGED_EMAILS } from '../../utils/role';
import { type BrokerTransactionData, getBrokerTransactionBlobPath, parseBrokerTransactionCSV, fetchBrokerTransactionCsv } from '../../utils/brokerTransactionBlob';
import { fetchStockMetadata, fetchSectorMapping } from '../../utils/stockMetadataBlob';
import { fetchBrokerList } from '../../utils/brokerMetadataBlob';

// Cache expiration time: 30 minutes (increased for better performance)
const CACHE_EXPIRY_MS = 30 * 60 * 1000;

// Earliest date available in Azure (Stockbit migration data)
const MIN_DATE = '2020-02-10';

// Foreign brokers (red background)
// Foreign brokers (red background)
const FOREIGN_BROKERS = [
  "AG", "AH", "AI", "AK", "BK", "BQ", "CG", "CS", "DP", "DR", "DU", "FS", "GW", "HD", "KK",
  "KZ", "LH", "LG", "LS", "MS", "RB", "RX", "TX", "YP", "YU", "ZP"
];

// Government brokers (green background)
const GOVERNMENT_BROKERS = ['CC', 'NI', 'OD', 'DX'];


const formatNumber = (value: number): string => {
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  if (absValue >= 1000000000) {
    return `${sign}${(absValue / 1000000000).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}B`;
  } else if (absValue >= 1000000) {
    return `${sign}${(absValue / 1000000).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}M`;
  } else if (absValue >= 1000) {
    return `${sign}${(absValue / 1000).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}K`;
  }
  return value.toLocaleString('en-US');
};

const formatValue = (value: any): string => {
  // Handle null, undefined, or non-numeric values
  if (value === null || value === undefined || isNaN(Number(value))) {
    return '0';
  }
  return formatNumber(Number(value));
};

const formatLot = (value: number): string => {
  const rounded = Math.round(value);
  const absValue = Math.abs(rounded);
  const sign = value < 0 ? '-' : '';

  // Format: < 1,000,000 → full number with comma (100,000)
  // Format: >= 1,000,000 → format with 'M' (Million) with 1 decimal (1.3M)
  if (absValue >= 1000000) {
    // Convert to millions and format with 1 decimal place
    const millions = absValue / 1000000;
    return `${sign}${millions.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}M`;
  } else {
    // < 1,000,000: Show full number with comma separator
    // Example: 100,000 → 100,000 (not 100K)
    return rounded.toLocaleString('en-US');
  }
};

const formatAverage = (value: number | undefined | null): string => {
  // Handle invalid values
  if (value === undefined || value === null || isNaN(value)) {
    return '-';
  }

  // Handle zero
  if (value === 0) {
    return '0.0';
  }

  // Format: ribuan pakai ',' (koma), desimal pakai '.' (titik)
  // Pastikan selalu 1 angka di belakang koma
  // Contoh: 1335.0, 10,000.5, -3.0, -197.6
  // Tampilkan nilai minus jika value < 0 (tidak perlu parameter allowNegative karena selalu tampilkan nilai asli)
  const rounded = Math.round(value * 10) / 10; // Round to 1 decimal place
  return rounded.toLocaleString('en-US', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  });
};

const formatLotPerFreqOrOrdNum = (value: number): string => {
  // Format: dibulatkan tanpa angka di belakang koma
  // Contoh: 64.6 → 65, 290.3 → 290
  const rounded = Math.round(value);
  const str = rounded.toString();

  // Jika data 6 karakter atau lebih (misal 381,173 -> 6 karakter numeric), ganti 3 karakter terakhir jadi K
  if (str.length >= 6) {
    return str.slice(0, -3) + 'K';
  }

  return rounded.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
};

// Note: getFilteredAndSortedStocks function removed - filtering and sorting now handled in useMemo

// Get trading days based on count (start from today if after 16:00, else yesterday)
// Returns dates sorted from oldest to newest (for display left to right)
const getTradingDays = (count: number): string[] => {
  const dates: string[] = [];
  const today = new Date();
  const currentHour = today.getHours();
  today.setHours(0, 0, 0, 0); // Normalize to local midnight
  let daysBack = currentHour < 20 ? 1 : 0; // Skip today if before 16:00

  while (dates.length < count) {
    const date = new Date(today);
    date.setDate(date.getDate() - daysBack);
    const dayOfWeek = date.getDay();

    // Skip weekends (Saturday = 6, Sunday = 0)
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      // Format as YYYY-MM-DD in local timezone to avoid timezone issues
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      if (dateStr >= MIN_DATE) {
        dates.push(dateStr);
      } else {
        // If we hit the absolute minimum date, stop going back
        break;
      }
    }
    daysBack++;
  }

  // Sort from oldest to newest (for display left to right)
  return dates.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
};

// Helper function to get last 3 trading days (starting from yesterday, sorted oldest first)
const getLastThreeDays = (): string[] => {
  return getTradingDays(3);
};

// Multi-range helpers (aturan sama kayak BrokerSummary)
const generateTradingDays = (start: string, end: string): string[] => {
  if (!start || !end) return [];
  const startDateObj = new Date(start);
  const endDateObj = new Date(end);
  const days: string[] = [];
  const current = new Date(startDateObj);
  while (current <= endDateObj) {
    const dow = current.getDay();
    if (dow !== 0 && dow !== 6) {
      const y = current.getFullYear();
      const m = String(current.getMonth() + 1).padStart(2, '0');
      const d = String(current.getDate()).padStart(2, '0');
      days.push(`${y}-${m}-${d}`);
    }
    current.setDate(current.getDate() + 1);
  }
  return days;
};

const nextTradingDay = (dateStr: string): string => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  do { d.setDate(d.getDate() + 1); } while (d.getDay() === 0 || d.getDay() === 6);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const formatRangeLabel = (start: string, end: string): string => {
  if (!start || !end) return '';
  const s = new Date(start);
  const e = new Date(end);
  const sStr = s.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const eStr = e.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  return `${sStr} - ${eStr}`;
};

// ---- Multi-range aggregation (mirror rumus akumulasi Total yang sudah ada) ----
// Menghitung peta total (buy/sell/netBuy/netSell per kode) untuk subset tanggal.
// Rumus disalin persis dari agregasi grand-total di dalam komponen —
// kode lama tidak diubah, jadi output single-range dijamin identik.
interface TxRangeBuyTotal {
  buyerVol: number; buyerValue: number; buyerAvg: number; buyerFreq: number;
  buyerOrdNum: number; buyerNoSplit: number; buyerLot: number; buyerLotPerFreq: number; buyerLotPerOrdNum: number; buyerLotPerNoSplit: number;
  buyerAvgCount: number; buyerLotPerFreqSum: number; buyerLotPerFreqWeight: number;
  buyerLotPerOrdNumSum: number; buyerLotPerOrdNumWeight: number;
  buyerLotPerNoSplitSum: number; buyerLotPerNoSplitWeight: number;
}
interface TxRangeSellTotal {
  sellerVol: number; sellerValue: number; sellerAvg: number; sellerFreq: number;
  sellerOrdNum: number; sellerNoSplit: number; sellerLot: number; sellerLotPerFreq: number; sellerLotPerOrdNum: number; sellerLotPerNoSplit: number;
  sellerAvgCount: number; sellerLotPerFreqSum: number; sellerLotPerFreqWeight: number;
  sellerLotPerOrdNumSum: number; sellerLotPerOrdNumWeight: number;
  sellerLotPerNoSplitSum: number; sellerLotPerNoSplitWeight: number;
}
interface TxRangeNetBuyTotal {
  netBuyVol: number; netBuyValue: number; netBuyAvg: number; netBuyFreq: number;
  netBuyOrdNum: number; netBuyNoSplit: number; netBuyLot: number; netBuyLotPerFreq: number; netBuyLotPerOrdNum: number; netBuyLotPerNoSplit: number;
  netBuyAvgCount: number; netBuyLotPerFreqSum: number; netBuyLotPerFreqWeight: number;
  netBuyLotPerOrdNumSum: number; netBuyLotPerOrdNumWeight: number;
  netBuyLotPerNoSplitSum: number; netBuyLotPerNoSplitWeight: number;
}
interface TxRangeNetSellTotal {
  netSellVol: number; netSellValue: number; netSellAvg: number; netSellFreq: number;
  netSellOrdNum: number; netSellNoSplit: number; netSellLot: number; netSellLotPerFreq: number; netSellLotPerOrdNum: number; netSellLotPerNoSplit: number;
  netSellAvgCount: number; netSellLotPerFreqSum: number; netSellLotPerFreqWeight: number;
  netSellLotPerOrdNumSum: number; netSellLotPerOrdNumWeight: number;
  netSellLotPerNoSplitSum: number; netSellLotPerNoSplitWeight: number;
}
interface TxNetListItem {
  stock: string; netValue: number; netLot: number; netVol: number; netAvg: number;
  netFreq: number; netOrdNum: number; buyLotPerFreq: number; buyLotPerOrdNum: number;
  sellLotPerFreq: number; sellLotPerOrdNum: number; buyValue: number; sellValue: number;
  netBuyNoSplit?: number; netBuyLotPerNoSplit?: number; netSellNoSplit?: number; netSellLotPerNoSplit?: number;
}

const aggregateTxRangeTotals = (
  dates: string[],
  txData: Map<string, BrokerTransactionData[]>
): { buyMap: Map<string, TxRangeBuyTotal>; sellMap: Map<string, TxRangeSellTotal>; netBuyMap: Map<string, TxRangeNetBuyTotal>; netSellMap: Map<string, TxRangeNetSellTotal> } => {
  const buyMap = new Map<string, TxRangeBuyTotal>();
  const sellMap = new Map<string, TxRangeSellTotal>();
  const netBuyMap = new Map<string, TxRangeNetBuyTotal>();
  const netSellMap = new Map<string, TxRangeNetSellTotal>();

  dates.forEach(date => {
    const dateData = txData.get(date) || [];
    dateData.forEach(dayData => {
      const buyStock = dayData.BCode || '';
      if (buyStock) {
        if (!buyMap.has(buyStock)) {
          buyMap.set(buyStock, {
            buyerVol: 0, buyerValue: 0, buyerAvg: 0, buyerFreq: 0, buyerOrdNum: 0, buyerNoSplit: 0,
            buyerLot: 0, buyerLotPerFreq: 0, buyerLotPerOrdNum: 0, buyerLotPerNoSplit: 0, buyerAvgCount: 0,
            buyerLotPerFreqSum: 0, buyerLotPerFreqWeight: 0, buyerLotPerOrdNumSum: 0, buyerLotPerOrdNumWeight: 0,
            buyerLotPerNoSplitSum: 0, buyerLotPerNoSplitWeight: 0,
          });
        }
        const total = buyMap.get(buyStock)!;
        const dayFreq = Number(dayData.BFreq) || Number(dayData.TransactionCount) || 0;
        const dayOrdNum = Number(dayData.NewBuyerOrdNum !== undefined ? dayData.NewBuyerOrdNum : dayData.BOrdNum) || 0;
        const dayNoSplit = Number(dayData.BNoSplit) || 0;
        const dayLot = Number(dayData.BLot) || 0;
        const dayLotPerFreq = dayData.BLotPerFreq;
        const dayLotPerOrdNum = dayData.BLotPerOrdNum;
        const dayLotPerNoSplit = dayData.BLotPerNoSplit;
        total.buyerVol += Number(dayData.BuyerVol) || 0;
        total.buyerValue += Number(dayData.BuyerValue) || 0;
        total.buyerFreq += dayFreq;
        total.buyerOrdNum += dayOrdNum;
        total.buyerNoSplit += dayNoSplit;
        total.buyerLot += dayLot;
        if (dayFreq > 0 && dayLotPerFreq !== undefined && dayLotPerFreq !== null) {
          total.buyerLotPerFreqSum += dayLotPerFreq * dayFreq;
          total.buyerLotPerFreqWeight += dayFreq;
        }
        if (dayOrdNum !== 0 && dayLotPerOrdNum !== undefined && dayLotPerOrdNum !== null) {
          total.buyerLotPerOrdNumSum += dayLotPerOrdNum * Math.abs(dayOrdNum);
          total.buyerLotPerOrdNumWeight += Math.abs(dayOrdNum);
        }
        if (dayNoSplit > 0 && dayLotPerNoSplit !== undefined && dayLotPerNoSplit !== null) {
          total.buyerLotPerNoSplitSum += dayLotPerNoSplit * dayNoSplit;
          total.buyerLotPerNoSplitWeight += dayNoSplit;
        }
      }

      const sellStock = dayData.SCode || '';
      if (sellStock) {
        if (!sellMap.has(sellStock)) {
          sellMap.set(sellStock, {
            sellerVol: 0, sellerValue: 0, sellerAvg: 0, sellerFreq: 0, sellerOrdNum: 0, sellerNoSplit: 0,
            sellerLot: 0, sellerLotPerFreq: 0, sellerLotPerOrdNum: 0, sellerLotPerNoSplit: 0, sellerAvgCount: 0,
            sellerLotPerFreqSum: 0, sellerLotPerFreqWeight: 0, sellerLotPerOrdNumSum: 0, sellerLotPerOrdNumWeight: 0,
            sellerLotPerNoSplitSum: 0, sellerLotPerNoSplitWeight: 0,
          });
        }
        const total = sellMap.get(sellStock)!;
        const dayFreq = Number(dayData.SFreq) || Number(dayData.TransactionCount) || 0;
        const dayOrdNum = Number(dayData.NewSellerOrdNum !== undefined ? dayData.NewSellerOrdNum : dayData.SOrdNum) || 0;
        const dayNoSplit = Number(dayData.SNoSplit) || 0;
        const dayLot = Number(dayData.SLot) || 0;
        const dayLotPerFreq = dayData.SLotPerFreq;
        const dayLotPerOrdNum = dayData.SLotPerOrdNum;
        const dayLotPerNoSplit = dayData.SLotPerNoSplit;
        total.sellerVol += Number(dayData.SellerVol) || 0;
        total.sellerValue += Number(dayData.SellerValue) || 0;
        total.sellerFreq += dayFreq;
        total.sellerOrdNum += dayOrdNum;
        total.sellerNoSplit += dayNoSplit;
        total.sellerLot += dayLot;
        if (dayFreq > 0 && dayLotPerFreq !== undefined && dayLotPerFreq !== null) {
          total.sellerLotPerFreqSum += dayLotPerFreq * dayFreq;
          total.sellerLotPerFreqWeight += dayFreq;
        }
        if (dayOrdNum !== 0 && dayLotPerOrdNum !== undefined && dayLotPerOrdNum !== null) {
          total.sellerLotPerOrdNumSum += dayLotPerOrdNum * Math.abs(dayOrdNum);
          total.sellerLotPerOrdNumWeight += Math.abs(dayOrdNum);
        }
        if (dayNoSplit > 0 && dayLotPerNoSplit !== undefined && dayLotPerNoSplit !== null) {
          total.sellerLotPerNoSplitSum += dayLotPerNoSplit * dayNoSplit;
          total.sellerLotPerNoSplitWeight += dayNoSplit;
        }
      }

      const netBuyStock = dayData.NBCode || '';
      if (netBuyStock) {
        if (!netBuyMap.has(netBuyStock)) {
          netBuyMap.set(netBuyStock, {
            netBuyVol: 0, netBuyValue: 0, netBuyAvg: 0, netBuyFreq: 0, netBuyOrdNum: 0, netBuyNoSplit: 0,
            netBuyLot: 0, netBuyLotPerFreq: 0, netBuyLotPerOrdNum: 0, netBuyLotPerNoSplit: 0, netBuyAvgCount: 0,
            netBuyLotPerFreqSum: 0, netBuyLotPerFreqWeight: 0, netBuyLotPerOrdNumSum: 0, netBuyLotPerOrdNumWeight: 0,
            netBuyLotPerNoSplitSum: 0, netBuyLotPerNoSplitWeight: 0,
          });
        }
        const total = netBuyMap.get(netBuyStock)!;
        const nbVol = dayData.NetBuyVol || 0;
        const nbVal = dayData.NBVal || dayData.NetBuyValue || 0;
        const nbFreq = dayData.NBFreq || 0;
        const nbOrdNum = dayData.NBOrdNum || 0;
        const nbNoSplit = Number(dayData.NBNoSplit) || 0;
        const nbLot = Number(dayData.NBLot) || 0;
        const nbLotPerFreq = dayData.NBLotPerFreq;
        const nbLotPerOrdNum = dayData.NBLotPerOrdNum;
        const nbLotPerNoSplit = dayData.NBLotPerNoSplit;
        total.netBuyVol += nbVol;
        total.netBuyValue += nbVal;
        total.netBuyFreq += nbFreq;
        total.netBuyOrdNum += nbOrdNum;
        total.netBuyNoSplit += nbNoSplit;
        total.netBuyLot += nbLot;
        if (nbFreq !== 0 && nbLotPerFreq !== undefined && nbLotPerFreq !== null) {
          total.netBuyLotPerFreqSum += nbLotPerFreq * Math.abs(nbFreq);
          total.netBuyLotPerFreqWeight += Math.abs(nbFreq);
        }
        if (nbOrdNum !== 0 && nbLotPerOrdNum !== undefined && nbLotPerOrdNum !== null) {
          total.netBuyLotPerOrdNumSum += nbLotPerOrdNum * Math.abs(nbOrdNum);
          total.netBuyLotPerOrdNumWeight += Math.abs(nbOrdNum);
        }
        if (nbNoSplit !== 0 && nbLotPerNoSplit !== undefined && nbLotPerNoSplit !== null) {
          total.netBuyLotPerNoSplitSum += nbLotPerNoSplit * Math.abs(nbNoSplit);
          total.netBuyLotPerNoSplitWeight += Math.abs(nbNoSplit);
        }
      }

      const netSellStock = dayData.NSCode || '';
      if (netSellStock) {
        if (!netSellMap.has(netSellStock)) {
          netSellMap.set(netSellStock, {
            netSellVol: 0, netSellValue: 0, netSellAvg: 0, netSellFreq: 0, netSellOrdNum: 0, netSellNoSplit: 0,
            netSellLot: 0, netSellLotPerFreq: 0, netSellLotPerOrdNum: 0, netSellLotPerNoSplit: 0, netSellAvgCount: 0,
            netSellLotPerFreqSum: 0, netSellLotPerFreqWeight: 0, netSellLotPerOrdNumSum: 0, netSellLotPerOrdNumWeight: 0,
            netSellLotPerNoSplitSum: 0, netSellLotPerNoSplitWeight: 0,
          });
        }
        const total = netSellMap.get(netSellStock)!;
        const nsVol = dayData.NetSellVol || 0;
        const nsVal = dayData.NSVal || dayData.NetSellValue || 0;
        const nsFreq = dayData.NSFreq || 0;
        const nsOrdNum = dayData.NSOrdNum || 0;
        const nsNoSplit = Number(dayData.NSNoSplit) || 0;
        const nsLot = Number(dayData.NSLot) || 0;
        const nsLotPerFreq = dayData.NSLotPerFreq;
        const nsLotPerOrdNum = dayData.NSLotPerOrdNum;
        const nsLotPerNoSplit = dayData.NSLotPerNoSplit;
        total.netSellVol += nsVol;
        total.netSellValue += nsVal;
        total.netSellFreq += nsFreq;
        total.netSellOrdNum += nsOrdNum;
        total.netSellNoSplit += nsNoSplit;
        total.netSellLot += nsLot;
        if (nsFreq !== 0 && nsLotPerFreq !== undefined && nsLotPerFreq !== null) {
          total.netSellLotPerFreqSum += nsLotPerFreq * Math.abs(nsFreq);
          total.netSellLotPerFreqWeight += Math.abs(nsFreq);
        }
        if (nsOrdNum !== 0 && nsLotPerOrdNum !== undefined && nsLotPerOrdNum !== null) {
          total.netSellLotPerOrdNumSum += nsLotPerOrdNum * Math.abs(nsOrdNum);
          total.netSellLotPerOrdNumWeight += Math.abs(nsOrdNum);
        }
        if (nsNoSplit !== 0 && nsLotPerNoSplit !== undefined && nsLotPerNoSplit !== null) {
          total.netSellLotPerNoSplitSum += nsLotPerNoSplit * Math.abs(nsNoSplit);
          total.netSellLotPerNoSplitWeight += Math.abs(nsNoSplit);
        }
      }
    });
  });

  buyMap.forEach((total) => {
    total.buyerAvg = total.buyerLot > 0 ? total.buyerValue / (total.buyerLot * 100) : 0;
    total.buyerLotPerFreq = total.buyerLotPerFreqWeight > 0 ? total.buyerLotPerFreqSum / total.buyerLotPerFreqWeight : 0;
    total.buyerLotPerOrdNum = total.buyerLotPerOrdNumWeight > 0 ? total.buyerLotPerOrdNumSum / total.buyerLotPerOrdNumWeight : 0;
    total.buyerLotPerNoSplit = total.buyerLotPerNoSplitWeight > 0 ? total.buyerLotPerNoSplitSum / total.buyerLotPerNoSplitWeight : 0;
  });
  sellMap.forEach((total) => {
    total.sellerAvg = total.sellerLot > 0 ? total.sellerValue / (total.sellerLot * 100) : 0;
    total.sellerLotPerFreq = total.sellerLotPerFreqWeight > 0 ? total.sellerLotPerFreqSum / total.sellerLotPerFreqWeight : 0;
    total.sellerLotPerOrdNum = total.sellerLotPerOrdNumWeight > 0 ? total.sellerLotPerOrdNumSum / total.sellerLotPerOrdNumWeight : 0;
    total.sellerLotPerNoSplit = total.sellerLotPerNoSplitWeight > 0 ? total.sellerLotPerNoSplitSum / total.sellerLotPerNoSplitWeight : 0;
  });
  netBuyMap.forEach((total) => {
    const netBuyLotVolume = total.netBuyLot * 100;
    total.netBuyAvg = Math.abs(netBuyLotVolume) > 0 ? total.netBuyValue / netBuyLotVolume : 0;
    total.netBuyLotPerFreq = total.netBuyLotPerFreqWeight > 0 ? total.netBuyLotPerFreqSum / total.netBuyLotPerFreqWeight : 0;
    total.netBuyLotPerOrdNum = total.netBuyLotPerOrdNumWeight > 0 ? total.netBuyLotPerOrdNumSum / total.netBuyLotPerOrdNumWeight : 0;
    total.netBuyLotPerNoSplit = total.netBuyLotPerNoSplitWeight > 0 ? total.netBuyLotPerNoSplitSum / total.netBuyLotPerNoSplitWeight : 0;
  });
  netSellMap.forEach((total) => {
    const netSellLotVolume = total.netSellLot * 100;
    total.netSellAvg = Math.abs(netSellLotVolume) > 0 ? total.netSellValue / netSellLotVolume : 0;
    total.netSellLotPerFreq = total.netSellLotPerFreqWeight > 0 ? total.netSellLotPerFreqSum / total.netSellLotPerFreqWeight : 0;
    total.netSellLotPerOrdNum = total.netSellLotPerOrdNumWeight > 0 ? total.netSellLotPerOrdNumSum / total.netSellLotPerOrdNumWeight : 0;
    total.netSellLotPerNoSplit = total.netSellLotPerNoSplitWeight > 0 ? total.netSellLotPerNoSplitSum / total.netSellLotPerNoSplitWeight : 0;
  });

  return { buyMap, sellMap, netBuyMap, netSellMap };
};

// Daftar net (bentuk allNetDataForTotalMemo) dari peta range — cabang ALL/Broker.
const buildAllBrokerNetLists = (
  netBuyMap: Map<string, TxRangeNetBuyTotal>,
  netSellMap: Map<string, TxRangeNetSellTotal>
): { netBuy: TxNetListItem[]; netSell: TxNetListItem[] } => {
  const netBuy = Array.from(netBuyMap.entries())
    .filter(([, d]) => Math.abs(d.netBuyLot || 0) > 0 || Math.abs(d.netBuyValue || 0) > 0)
    .map(([stock, d]) => ({
      stock,
      netValue: d.netBuyValue,
      netLot: d.netBuyLot,
      netVol: d.netBuyVol,
      netAvg: d.netBuyAvg,
      netFreq: d.netBuyFreq,
      netOrdNum: d.netBuyOrdNum,
      buyLotPerFreq: d.netBuyLotPerFreq,
      buyLotPerOrdNum: d.netBuyLotPerOrdNum,
      sellLotPerFreq: 0,
      sellLotPerOrdNum: 0,
      buyValue: d.netBuyValue,
      sellValue: 0,
      netBuyNoSplit: d.netBuyNoSplit,
      netBuyLotPerNoSplit: d.netBuyLotPerNoSplit,
    }))
    .sort((a, b) => b.netLot - a.netLot);

  const netSell = Array.from(netSellMap.entries())
    .filter(([, d]) => Math.abs(d.netSellLot || 0) > 0 || Math.abs(d.netSellValue || 0) > 0)
    .map(([stock, d]) => ({
      stock,
      netValue: -d.netSellValue,
      netLot: -d.netSellLot,
      netVol: -d.netSellVol,
      netAvg: d.netSellAvg,
      netFreq: d.netSellFreq,
      netOrdNum: d.netSellOrdNum,
      buyLotPerFreq: 0,
      buyLotPerOrdNum: 0,
      sellLotPerFreq: d.netSellLotPerFreq,
      sellLotPerOrdNum: d.netSellLotPerOrdNum,
      buyValue: 0,
      sellValue: d.netSellValue,
      netSellNoSplit: d.netSellNoSplit,
      netSellLotPerNoSplit: d.netSellLotPerNoSplit,
    }))
    .sort((a, b) => Math.abs(b.netLot) - Math.abs(a.netLot));

  return { netBuy, netSell };
};

// Daftar net dari nett-ing buy-sell per kode (cabang normal).
const buildNettedStockLists = (
  buyMap: Map<string, TxRangeBuyTotal>,
  sellMap: Map<string, TxRangeSellTotal>
): { netBuy: TxNetListItem[]; netSell: TxNetListItem[] } => {
  const allStocksSet = new Set<string>();
  buyMap.forEach((_, stock) => allStocksSet.add(stock));
  sellMap.forEach((_, stock) => allStocksSet.add(stock));

  const data = Array.from(allStocksSet).map(stock => {
    const buyData = buyMap.get(stock);
    const sellData = sellMap.get(stock);
    const buyValue = buyData?.buyerValue || 0;
    const buyLot = buyData?.buyerLot || 0;
    const buyVol = buyData?.buyerVol || 0;
    const buyFreq = buyData?.buyerFreq || 0;
    const buyOrdNum = buyData?.buyerOrdNum || 0;
    const buyLotPerFreq = buyData?.buyerLotPerFreq || 0;
    const buyLotPerOrdNum = buyData?.buyerLotPerOrdNum || 0;
    const sellValue = sellData?.sellerValue || 0;
    const sellLot = sellData?.sellerLot || 0;
    const sellVol = sellData?.sellerVol || 0;
    const sellFreq = sellData?.sellerFreq || 0;
    const sellOrdNum = sellData?.sellerOrdNum || 0;
    const sellLotPerFreq = sellData?.sellerLotPerFreq || 0;
    const sellLotPerOrdNum = sellData?.sellerLotPerOrdNum || 0;
    const netValue = buyValue - sellValue;
    const netLot = buyLot - sellLot;
    const netAvg = Math.abs(netLot * 100) !== 0 ? Math.abs(netValue) / Math.abs(netLot * 100) : 0;
    const netFreq = buyFreq - sellFreq;
    const netOrdNum = buyOrdNum - sellOrdNum;
    const buyNoSplit = buyData?.buyerNoSplit || 0;
    const sellNoSplit = sellData?.sellerNoSplit || 0;
    const netBuyNoSplit = buyNoSplit - sellNoSplit;
    const netSellNoSplit = sellNoSplit - buyNoSplit;
    const netBuyLotPerNoSplit = buyData?.buyerLotPerNoSplit || 0;
    const netSellLotPerNoSplit = sellData?.sellerLotPerNoSplit || 0;

    return {
      stock, netValue, netLot, netVol: buyVol - sellVol, netAvg, netFreq, netOrdNum,
      buyLotPerFreq, buyLotPerOrdNum, sellLotPerFreq, sellLotPerOrdNum, buyValue, sellValue,
      netBuyNoSplit, netBuyLotPerNoSplit, netSellNoSplit, netSellLotPerNoSplit
    };
  });

  const netBuy = data.filter(item => item.netValue > 0 && (Math.abs(item.netLot) > 0 || Math.abs(item.netValue) > 0))
    .sort((a, b) => b.netLot - a.netLot);
  const netSell = data.filter(item => item.netValue < 0 && (Math.abs(item.netLot) > 0 || Math.abs(item.netValue) > 0))
    .sort((a, b) => Math.abs(b.netLot) - Math.abs(a.netLot));

  return { netBuy, netSell };
};

// Note: Sector filter is now used instead of F/D filter
// Sector mapping is loaded from backend API

// Page ID for menu preferences
const PAGE_ID = 'broker-activity-new-summary';

// Interface for user preferences
interface UserPreferences {
  selectedBrokers: string[];
  selectedTickers: string[];
  selectedSectors: string[];
  pivotFilter: 'Broker' | 'Stock';
  invFilter: 'F' | 'D' | '';
  boardFilter: 'RG' | 'TN' | 'NG' | '';
  showFrequency: boolean;
  showOrder: boolean;
  showNoSplit: boolean;
  startDate?: string;
  endDate?: string;
}

// Utility functions for saving/loading preferences (now using backend)
const loadPreferences = (pageId: string): Partial<UserPreferences> | null => {
  try {
    // Try to load from cache first (synchronous)
    const cached = menuPreferencesService.getCachedPreferences(pageId);
    if (cached) {
      return cached as Partial<UserPreferences>;
    }
    const prefs = menuPreferencesService.loadPreferences(pageId);
    return prefs ? prefs as Partial<UserPreferences> : null;
  } catch (error) {
    console.warn('Failed to load cached preferences:', error);
  }
  return null;
};

const savePreferences = (pageId: string, prefs: Partial<UserPreferences>) => {
  // Save to cookies (synchronous)
  menuPreferencesService.savePreferences(pageId, prefs);
};

const COLUMN_WIDTHS = {
  CODE: '45px',
  VAL: '50px',
  LOT: '60px',
  AVG: '55px',
  FREQ: '55px',
  LOT_F: '55px',
  ORD: '45px',
  LOT_O: '45px',
  SEP: '30px',
  ROW_HEIGHT: '22px' // Configurable row height
};

interface NewBrokerSummaryProps {
  selectedStock?: string;
  isDashboard?: boolean;
  isActive?: boolean;
}

export function NewBrokerSummary({ selectedStock, isDashboard = false, isActive = true }: NewBrokerSummaryProps = {}) {
  const { showToast } = useToast();
  const { profile } = useProfile();
  // AI (dev process): hanya admin/developer (+ email allowlist) yang bisa buka
  const canUseAiFeatures = hasAdminPrivileges(profile?.role || '') || NEW_BROKER_PRIVILEGED_EMAILS.includes(profile?.email || '');
  const isTrial = profile?.subscriptionStatus === 'trial';

  // Force default restrictions for trial users
  useEffect(() => {
    if (isTrial) {
      setSelectedTickers([]);
      setAppliedSelectedTickers([]);
      setSelectedSectors([]);
      setAppliedSelectedSectors([]);
      setDraftShowOrder(false);
      setShowOrder(false);
    }
  }, [isTrial]);

  const [selectedDates, setSelectedDates] = useState<string[]>([]);

  // Load preferences from cookies on mount
  const effectivePageId = isDashboard ? `dashboard-${PAGE_ID}` : PAGE_ID;
  const [savedPrefs] = useState(() => loadPreferences(effectivePageId));

  const [startDate, setStartDate] = useState(() => {
    // Try to load from preferences first
    if (!isDashboard && savedPrefs?.startDate) {
      return savedPrefs.startDate < MIN_DATE ? MIN_DATE : savedPrefs.startDate;
    }
    // In dashboard mode, we wait for the API to provide exact dates
    if (isDashboard) {
      return '';
    }
    // Fallback to default
    const threeDays = getLastThreeDays();
    if (threeDays.length > 0) {
      const sortedDates = [...threeDays].sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
      const res = sortedDates[0] || '';
      return res < MIN_DATE ? MIN_DATE : res;
    }
    return '';
  });
  const [endDate, setEndDate] = useState(() => {
    // Try to load from preferences first
    if (!isDashboard && savedPrefs?.endDate) {
      return savedPrefs.endDate < MIN_DATE ? MIN_DATE : savedPrefs.endDate;
    }
    // In dashboard mode, we wait for the API to provide exact dates
    if (isDashboard) {
      return '';
    }
    // Fallback to default
    const threeDays = getLastThreeDays();
    if (threeDays.length > 0) {
      const sortedDates = [...threeDays].sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
      const res = sortedDates[sortedDates.length - 1] || '';
      return res < MIN_DATE ? MIN_DATE : res;
    }
    return '';
  });
  const [brokerInput, setBrokerInput] = useState('');
  const [debouncedBrokerInput, setDebouncedBrokerInput] = useState('');
  const [selectedBrokers, setSelectedBrokers] = useState<string[]>(() => {
    // Try to load from preferences first
    if (savedPrefs?.selectedBrokers && savedPrefs.selectedBrokers.length > 0) {
      return savedPrefs.selectedBrokers;
    }
    // Fallback to default
    return ['ALL'];
  });
  const [showBrokerSuggestions, setShowBrokerSuggestions] = useState(false);
  // Windowing state for virtual scrolling
  const [brokerScrollOffset, setBrokerScrollOffset] = useState(0);
  const ITEMS_PER_PAGE = 30; // Render 30 items at a time (reduced for better performance)
  const startDateRef = useRef<HTMLInputElement>(null);
  const endDateRef = useRef<HTMLInputElement>(null);
  const dropdownBrokerRef = useRef<HTMLDivElement>(null);
  const menuContainerRef = useRef<HTMLDivElement>(null);

  // Refs for table synchronization
  const valueTableRef = useRef<HTMLTableElement>(null);
  const netTableRef = useRef<HTMLTableElement>(null);
  const valueTableContainerRef = useRef<HTMLDivElement>(null);
  const netTableContainerRef = useRef<HTMLDivElement>(null);

  // Refs for lazy loading intersection observer
  const valueTableSentinelRef = useRef<HTMLTableCellElement>(null);
  const netTableSentinelRef = useRef<HTMLTableCellElement>(null);

  // Ref to track if column width sync has been done (prevent infinite loop)
  const hasSyncedColumnWidthsRef = useRef<boolean>(false);
  // Store column widths to maintain consistency after lazy loading
  const columnWidthsRef = useRef<number[]>([]);
  // Refs for Total table (unused but preserved for future use)
  const totalTableContainerRef = useRef<HTMLDivElement>(null); // used by scroll sync listener
  const dateColumnWidthsRef = useRef<Map<string, number>>(new Map());
  const totalColumnWidthRef = useRef<number>(0);

  // API data states

  // Store raw data (before ticker filtering) for availableTickers extraction
  const [rawTransactionData, setRawTransactionData] = useState<Map<string, BrokerTransactionData[]>>(new Map());
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [availableBrokers, setAvailableBrokers] = useState<string[]>([]);
  const [isDataReady, setIsDataReady] = useState<boolean>(false); // Control when to show tables
  const [shouldFetchData, setShouldFetchData] = useState<boolean>(false); // Control when to fetch data (only when Show button clicked)

  // Track last fetch parameters to detect if only sector filter changed
  const lastFetchParamsRef = useRef<{
    brokers: string[];
    dates: string[];
    pivot: 'Broker' | 'Stock'; // Track pivotFilter
    inv: string; // Track invFilter
    board: string; // Track boardFilter
    tickers: string[]; // Track selectedTickers to detect changes
    sectors: string[]; // Track selectedSectors to detect changes
  } | null>(null);

  // Sector Filter states (changed from F/D filter)
  const [activeSectorFilter, setActiveSectorFilter] = useState<string>('All'); // 'All' or sector name (active filter - used for filtering displayed data)
  const [availableSectors, setAvailableSectors] = useState<string[]>([]); // List of available sectors (excluding 'All')
  const [stockToSectorMap, setStockToSectorMap] = useState<{ [stock: string]: string }>({}); // Stock code -> sector name mapping
  const [pivotFilter, setPivotFilter] = useState<'Broker' | 'Stock'>(() => {
    // NewBrokerSummary: selalu mode Broker (rows = broker). Filter Output disembunyikan.
    return 'Stock';
  });
  const [invFilter, setInvFilter] = useState<'F' | 'D' | ''>(() => {
    // Try to load from preferences first
    if (savedPrefs?.invFilter !== undefined) {
      return savedPrefs.invFilter;
    }
    // Fallback to default (empty - All)
    return '';
  });
  const [boardFilter, setBoardFilter] = useState<'RG' | 'TN' | 'NG' | ''>(() => {
    // Try to load from preferences first
    if (savedPrefs?.boardFilter !== undefined) {
      return savedPrefs.boardFilter;
    }
    // Fallback to default
    return 'RG';
  });




  // Toggle untuk show/hide kolom Frequency dan Order (STAGED/DRAFT - only committed when Show clicked)
  const [draftShowFrequency, setDraftShowFrequency] = useState<boolean>(() => {
    // Try to load from preferences first
    if (savedPrefs?.showFrequency !== undefined) {
      return savedPrefs.showFrequency;
    }
    // Fallback to default
    return true;
  });
  const [draftShowOrder, setDraftShowOrder] = useState<boolean>(() => {
    if (isDashboard) {
      return false;
    }
    // Try to load from preferences first
    if (savedPrefs?.showOrder !== undefined) {
      return savedPrefs.showOrder;
    }
    // Fallback to default
    return true;
  });
  const [draftShowNoSplit, setDraftShowNoSplit] = useState<boolean>(() => {
    // Try to load from preferences first
    if (savedPrefs?.showNoSplit !== undefined) {
      return savedPrefs.showNoSplit;
    }
    // Fallback to default
    return false;
  });

  // Effective toggle states - these actually control the table rendering
  // Initialized from draft states which are loaded from savedPrefs
  const [showFrequency, setShowFrequency] = useState<boolean>(draftShowFrequency);
  const [showOrder, setShowOrder] = useState<boolean>(draftShowOrder);
  const [showNoSplit, setShowNoSplit] = useState<boolean>(draftShowNoSplit);

  // Visualisasi label untuk Output dropdown (ditukar untuk display)
  // Logika tetap menggunakan pivotFilter yang asli
  // Tukar label: Broker -> Stock, Stock -> Broker
  const pivotFilterDisplayLabel = pivotFilter === 'Broker' ? 'Stock' : 'Broker';

  // Helper untuk mendapatkan value dari display label (untuk onChange)
  const handlePivotFilterChange = (displayLabel: string) => {
    // Tukar kembali: Stock -> Broker, Broker -> Stock
    const actualValue: 'Broker' | 'Stock' = displayLabel === 'Stock' ? 'Broker' : 'Stock';
    setPivotFilter(actualValue);
  };

  // Multi-select ticker/sector states (combined)
  const [tickerInput, setTickerInput] = useState('');
  const [debouncedTickerInput, setDebouncedTickerInput] = useState('');
  // true = user sedang mengedit teks kotak Ticker (teks jadi sumber pilihan, termasuk teks kosong)
  const [tickerTextDirty, setTickerTextDirty] = useState(false);
  const [selectedTickers, setSelectedTickers] = useState<string[]>(() => {
    if (isTrial) {
      return [];
    }
    // If locked to a specific stock
    if (selectedStock) {
      return [selectedStock];
    }
    // Try to load from preferences first
    if (savedPrefs?.selectedTickers) {
      return savedPrefs.selectedTickers;
    }
    // Fallback to default (empty - show all tickers)
    return [];
  });
  const [selectedSectors, setSelectedSectors] = useState<string[]>(() => {
    if (isTrial) {
      return [];
    }
    // Try to load from preferences first
    if (savedPrefs?.selectedSectors) {
      return savedPrefs.selectedSectors;
    }
    // Fallback to default (empty)
    return [];
  });

  // Effective/Applied selection states - these actually control the data processing
  // They are only updated when the "Show" button is clicked
  const [appliedSelectedBrokers, setAppliedSelectedBrokers] = useState<string[]>(selectedBrokers);
  const [appliedSelectedTickers, setAppliedSelectedTickers] = useState<string[]>(selectedStock ? [selectedStock] : selectedTickers);

  // Block filter (mirip BrokerSummary): Default = auto top-5 net buy/sell,
  // Manual = user pilih items (saham saat pivot Broker, broker saat pivot Stock)
  const [blockFilter, setBlockFilter] = useState<string>('Default');
  const [selectedBlockItems, setSelectedBlockItems] = useState<string[]>([]);
  const [showBlockDropdown, setShowBlockDropdown] = useState(false);
  const [blockSearchInput, setBlockSearchInput] = useState('');
  const blockDropdownRef = useRef<HTMLDivElement>(null);

  // Multi-range (sama kayak BrokerSummary): tabel update saat Show diklik
  const [showRange2, setShowRange2] = useState(false);
  const [range2StartDate, setRange2StartDate] = useState('');
  const [range2EndDate, setRange2EndDate] = useState('');
  const [showRangeDropdown, setShowRangeDropdown] = useState(false);
  const rangeDropdownRef = useRef<HTMLDivElement>(null);
  const [extraRanges, setExtraRanges] = useState<Array<{ id: string; startDate: string; endDate: string }>>([]);
  const [displayedRangesMeta, setDisplayedRangesMeta] = useState<Array<{ id: string; startDate: string; endDate: string }>>([]);

  // AI Insights (sama kayak BrokerSummary lama)
  const [showAIPopup, setShowAIPopup] = useState(false);
  const [closePriceForAI, setClosePriceForAI] = useState<number | null>(null);
  // Overlay AI per tanggal (YYYY-MM-DD) — null = tertutup
  const [aiDateOverlay, setAiDateOverlay] = useState<string | null>(null);
  
  // Auto-trigger data fetch when isDashboard is true
  const dashboardAutoFetchRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isDashboard || !selectedStock || !isActive) return;
    if (dashboardAutoFetchRef.current === selectedStock) return;
    
    // When stock changes from props, ensure selectedTickers matches it
    setSelectedTickers([selectedStock]);
    
    const timer = setTimeout(() => {
      if (startDate && endDate && availableBrokers.length > 0) {
        dashboardAutoFetchRef.current = selectedStock;
        // Build mock dates array similar to Show button handler
        let datesToUse: string[] = [];
        const startParts = startDate.split('-').map(Number);
        const endParts = endDate.split('-').map(Number);
        if (startParts.length === 3 && endParts.length === 3) {
          const start = new Date(startParts[0]!, startParts[1]! - 1, startParts[2]!);
          const end = new Date(endParts[0]!, endParts[1]! - 1, endParts[2]!);
          if (start <= end) {
            const dateArray: string[] = [];
            const currentDate = new Date(start);
            while (currentDate <= end) {
              const dayOfWeek = currentDate.getDay();
              if (dayOfWeek !== 0 && dayOfWeek !== 6) {
                const year = currentDate.getFullYear();
                const month = String(currentDate.getMonth() + 1).padStart(2, '0');
                const day = String(currentDate.getDate()).padStart(2, '0');
                dateArray.push(`${year}-${month}-${day}`);
              }
              currentDate.setDate(currentDate.getDate() + 1);
            }
            datesToUse = dateArray.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
          }
        }
        
        setSelectedDates(datesToUse);
        setAppliedSelectedTickers([selectedStock]);
        // Set pivotFilter to Stock because we want to see which brokers transacted this stock
        setPivotFilter('Stock');
        setAppliedPivotFilter('Stock');
        setActiveSectorFilter('All');
        setRawTransactionData(new Map());
        setIsDataReady(false);
        hasSyncedColumnWidthsRef.current = false;
        columnWidthsRef.current = [];
        shouldFetchDataRef.current = true;
        setShouldFetchData(true);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [isDashboard, selectedStock, startDate, endDate, availableBrokers.length, isActive]);
  const [appliedSelectedSectors, setAppliedSelectedSectors] = useState<string[]>(selectedSectors);
  const [appliedPivotFilter, setAppliedPivotFilter] = useState<'Broker' | 'Stock'>(pivotFilter);
  const [appliedInvFilter, setAppliedInvFilter] = useState<'F' | 'D' | ''>(invFilter);
  const [appliedBoardFilter, setAppliedBoardFilter] = useState<'RG' | 'TN' | 'NG' | ''>(boardFilter);
  const [showTickerSuggestions, setShowTickerSuggestions] = useState(false);
  const [highlightedTickerIndex, setHighlightedTickerIndex] = useState<number>(-1);

  // Optimize highlightedTickerIndex updates with debounce to reduce re-renders
  const highlightedTickerIndexRef = useRef<number>(-1);
  const highlightTickerTimeoutRef = useRef<NodeJS.Timeout | null>(null);



  const setHighlightedTickerIndexOptimized = useCallback((idx: number) => {
    highlightedTickerIndexRef.current = idx;
    if (highlightTickerTimeoutRef.current) {
      clearTimeout(highlightTickerTimeoutRef.current);
    }
    highlightTickerTimeoutRef.current = setTimeout(() => {
      setHighlightedTickerIndex(highlightedTickerIndexRef.current);
    }, 16); // ~60fps update rate
  }, []);
  const dropdownTickerRef = useRef<HTMLDivElement>(null);
  // Windowing state for virtual scrolling
  const [tickerScrollOffset, setTickerScrollOffset] = useState(0);
  const [sectorScrollOffset, setSectorScrollOffset] = useState(0);

  useEffect(() => {
    hasSyncedColumnWidthsRef.current = false;
    // Also clear stored widths as layout might change completely
    columnWidthsRef.current = [];
  }, [selectedDates, appliedPivotFilter, appliedSelectedBrokers, appliedSelectedTickers, appliedSelectedSectors, showFrequency, showOrder, showNoSplit]);

  // Stock list from API (loaded once on mount for fast dropdown)
  const [availableStocksFromAPI, setAvailableStocksFromAPI] = useState<string[]>([]);
  const [isLoadingStocks, setIsLoadingStocks] = useState<boolean>(false);


  // OPTIMIZED: Use stock list from API as primary source (fast), fallback to extraction from rawTransactionData
  // FIXED: Use rawTransactionData (before ticker filtering) to show all available tickers for selected broker(s)
  // CRITICAL: If sector is selected, only show tickers from that sector
  const availableTickers = useMemo(() => {
    const sortTickers = (a: string, b: string) => {
      const aIsWarrant = a.length > 4;
      const bIsWarrant = b.length > 4;
      if (aIsWarrant && !bIsWarrant) return 1;
      if (!aIsWarrant && bIsWarrant) return -1;
      return a.localeCompare(b);
    };

    // Primary source: Use stock list from API (fast, loaded once on mount)
    if (availableStocksFromAPI.length > 0) {
      // If sector is selected, filter tickers by sector
      if (selectedSectors.length > 0) {
        const tickersFromSectors = availableStocksFromAPI.filter(ticker => {
          const tickerSector = stockToSectorMap[ticker.toUpperCase()];
          return tickerSector && selectedSectors.includes(tickerSector);
        });
        return tickersFromSectors.sort(sortTickers);
      }
      return [...availableStocksFromAPI].sort(sortTickers);
    }

    // Fallback: Extract from rawTransactionData (slower, but works if API not loaded yet)
    const allTickers = new Set<string>();
    if (isDataReady && rawTransactionData.size > 0) {
      // Extract from all dates in rawTransactionData (before filtering) to show all available tickers
      rawTransactionData.forEach((dateData) => {
        dateData.forEach(item => {
          if (item.BCode) allTickers.add(item.BCode);
          if (item.SCode) allTickers.add(item.SCode);
          if (item.NBCode) allTickers.add(item.NBCode);
          if (item.NSCode) allTickers.add(item.NSCode);
        });
      });
    }

    // If sector is selected, filter tickers by sector
    if (selectedSectors.length > 0) {
      const tickersFromSectors = Array.from(allTickers).filter(ticker => {
        const tickerSector = stockToSectorMap[ticker.toUpperCase()];
        return tickerSector && selectedSectors.includes(tickerSector);
      });
      return tickersFromSectors.sort(sortTickers);
    }

    return Array.from(allTickers).sort(sortTickers);
  }, [availableStocksFromAPI, rawTransactionData, isDataReady, selectedSectors, stockToSectorMap]); // Include availableStocksFromAPI as primary source

  // Debounce broker input for better performance (increased to 300ms to reduce re-renders)
  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedBrokerInput(brokerInput);
      // Reset scroll offset when search changes
      setBrokerScrollOffset(0);
    }, 300);
    return () => clearTimeout(timeout);
  }, [brokerInput]);

  // Debounce ticker input for better performance (increased to 300ms to reduce re-renders)
  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedTickerInput(tickerInput);
      // Reset scroll offsets when search changes
      setTickerScrollOffset(0);
      setSectorScrollOffset(0);
    }, 300);
    return () => clearTimeout(timeout);
  }, [tickerInput]);

  // Construct preferences object
  const currentPreferences = useMemo<Partial<UserPreferences>>(() => {
    const prefs: Partial<UserPreferences> = {
      selectedBrokers,
      selectedTickers,
      selectedSectors,
      pivotFilter,
      invFilter,
      boardFilter,
      showFrequency: draftShowFrequency, // Persist draft/staged settings immediately
      showOrder: draftShowOrder,         // Persist draft/staged settings immediately
      showNoSplit: draftShowNoSplit,     // Persist draft/staged settings immediately
    };
    if (startDate) prefs.startDate = startDate;
    if (endDate) prefs.endDate = endDate;
    return prefs;
  }, [selectedBrokers, selectedTickers, selectedSectors, pivotFilter, invFilter, boardFilter, draftShowFrequency, draftShowOrder, draftShowNoSplit, startDate, endDate]);

  // Ref to hold the latest preferences for unmount saving
  const preferencesRef = useRef(currentPreferences);

  // Update ref whenever preferences change
  useEffect(() => {
    preferencesRef.current = currentPreferences;
  }, [currentPreferences]);

  // Save preferences to cookies with debounce
  useEffect(() => {
    const timeout = setTimeout(() => {
      savePreferences(effectivePageId, currentPreferences);
    }, 500); // Debounce 500ms to reduce cookie writes

    return () => clearTimeout(timeout);
  }, [currentPreferences]);


  // Save on unmount to capture pending changes (e.g. user navigates away quickly)
  useEffect(() => {
    return () => {
      savePreferences(effectivePageId, preferencesRef.current);
    };
  }, []);


  // Memoized filtered brokers list for better performance
  const filteredBrokersList = useMemo(() => {
    const searchTerm = debouncedBrokerInput.toLowerCase();
    if (searchTerm === '') {
      return availableBrokers;
    }
    return availableBrokers.filter(broker => broker.toLowerCase().includes(searchTerm));
  }, [availableBrokers, debouncedBrokerInput]);

  // Windowed filtered brokers list (only show visible items)
  const visibleFilteredBrokers = useMemo(() => {
    const start = brokerScrollOffset;
    const end = start + ITEMS_PER_PAGE;
    return filteredBrokersList.slice(start, end);
  }, [filteredBrokersList, brokerScrollOffset]);


  // Kata kunci suggestion = token terakhir setelah koma ("AADI, BK" → "bk")
  const tickerSearchTerm = useMemo(
    () => (debouncedTickerInput.split(/[,;]/).pop() || '').trim().toLowerCase(),
    [debouncedTickerInput]
  );

  // Memoized filtered tickers list for better performance
  const filteredTickersList = useMemo(() => {
    const searchTerm = tickerSearchTerm;
    if (searchTerm === '') return availableTickers;
    return availableTickers.filter(t => t.toLowerCase().includes(searchTerm));
  }, [availableTickers, tickerSearchTerm]);

  // Memoized filtered sectors list for better performance
  const filteredSectorsList = useMemo(() => {
    const searchTerm = tickerSearchTerm;
    if (searchTerm === '') return availableSectors;
    return availableSectors.filter(s => s.toLowerCase().includes(searchTerm));
  }, [availableSectors, tickerSearchTerm]);

  // Windowed filtered tickers list (only show visible items)
  const visibleFilteredTickers = useMemo(() => {
    const start = tickerScrollOffset;
    const end = start + ITEMS_PER_PAGE;
    return filteredTickersList.slice(start, end);
  }, [filteredTickersList, tickerScrollOffset]);

  // Windowed filtered sectors list (only show visible items)
  const visibleFilteredSectors = useMemo(() => {
    const start = sectorScrollOffset;
    const end = start + ITEMS_PER_PAGE;
    return filteredSectorsList.slice(start, end);
  }, [filteredSectorsList, sectorScrollOffset]);

  // Request cancellation ref
  const abortControllerRef = useRef<AbortController | null>(null);
  const shouldFetchDataRef = useRef<boolean>(false); // Ref to track shouldFetchData for async functions (always up-to-date)
  // Generasi fetch — tiap klik Show naik. Boolean shouldFetchData tidak berubah
  // bila diklik saat fetch berjalan (true→true) sehingga efek tidak jalan ulang;
  // counter ini menjamin fetch selalu di-restart dengan parameter terbaru.
  const [fetchGen, setFetchGen] = useState<number>(0);
  const fetchGenRef = useRef<number>(0);

  // Cache for API responses to avoid redundant calls
  // Key format: `${broker}-${date}-${market}`
  const dataCacheRef = useRef<Map<string, { data: BrokerTransactionData[]; timestamp: number }>>(new Map());

  // Virtual scrolling states for performance
  // FIXED: Always start with exactly 20 rows (like BrokerSummary MAX_DISPLAY_ROWS)
  const MAX_DISPLAY_ROWS = 20;
  const [visibleRowCount, setVisibleRowCount] = useState<number>(MAX_DISPLAY_ROWS); // Start with 20 visible rows
  const visibleRowCountRef = useRef<number>(MAX_DISPLAY_ROWS); // Ref so IntersectionObserver guard stays accurate without adding visibleRowCount to effect deps
  visibleRowCountRef.current = visibleRowCount;

  // FIXED: Don't reset visible rows on input change - only reset when data actually changes
  // This prevents unnecessary re-renders when user changes input
  // Visible rows will reset automatically when new data is loaded (via transactionData change)

  // Load available brokers, tickers, and initial dates on component mount
  // IMPORTANT: This effect runs ONLY ONCE on mount - load metadata only, NO auto-fetch data
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        // OPTIMIZED: Try to load sector mapping and broker list from localStorage first for instant display
        const SECTOR_MAPPING_CACHE_KEY = 'broker_transaction_sector_mapping';
        const BROKER_LIST_CACHE_KEY = 'broker_transaction_available_brokers';
        const SECTOR_MAPPING_CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours
        const BROKER_LIST_CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

        let cachedSectorMapping: { stockToSector: { [stock: string]: string }; sectors: string[]; timestamp: number } | null = null;
        try {
          // Load Sector Mapping
          const cachedSectors = localStorage.getItem(SECTOR_MAPPING_CACHE_KEY);
          if (cachedSectors) {
            const parsed = JSON.parse(cachedSectors);
            if (parsed.timestamp && (Date.now() - parsed.timestamp) < SECTOR_MAPPING_CACHE_TTL) {
              cachedSectorMapping = parsed;
              setStockToSectorMap(parsed.stockToSector);
              const sectors = parsed.sectors || [];
              if (!sectors.includes('IDX')) sectors.push('IDX');
              setAvailableSectors(sectors.sort((a: string, b: string) => {
                if (a === 'IDX') return -1;
                if (b === 'IDX') return 1;
                return a.localeCompare(b);
              }));
            }
          }

          // Load Broker List
          const cachedBrokers = localStorage.getItem(BROKER_LIST_CACHE_KEY);
          if (cachedBrokers) {
            const parsed = JSON.parse(cachedBrokers);
            if (parsed.timestamp && (Date.now() - parsed.timestamp) < BROKER_LIST_CACHE_TTL && parsed.brokers) {
              setAvailableBrokers(parsed.brokers);
              console.log(`[NewBrokerSummary] Loaded ${parsed.brokers.length} brokers from cache`);
            }
          }
        } catch (e) {
          console.warn('[NewBrokerSummary] Failed to load metadata from cache:', e);
        }

        // OPTIMIZATION: Set default dates synchronously BEFORE any await to prevent 2s empty UI delay
        const initialDates = getTradingDays(3);
        const sortedDates = [...initialDates].sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
        
        if (isDashboard || (!savedPrefs?.startDate || !savedPrefs?.endDate)) {
          setSelectedDates(sortedDates);
          if (sortedDates.length > 0) {
            setStartDate(sortedDates[0] ?? '');
            setEndDate(sortedDates[sortedDates.length - 1] ?? '');
          }
        }

        // OPTIMIZED: Load broker list, dates, and sector mapping in parallel for faster initial load
        setIsLoadingStocks(true);
        const [brokers, sectorResponse] = await Promise.all([
          fetchBrokerList(),
          cachedSectorMapping ? Promise.resolve({ success: true, data: cachedSectorMapping }) : fetchSectorMapping(),
        ]);

        if (brokers && brokers.length > 0) {
          setAvailableBrokers(brokers);
          // Update cache
          try {
            localStorage.setItem(BROKER_LIST_CACHE_KEY, JSON.stringify({
              brokers: brokers,
              timestamp: Date.now()
            }));
          } catch (e) { /* ignore */ }
        } else if (availableBrokers.length === 0) {
          throw new Error('Failed to load broker list');
        }

        // DYNAMIC STOCK LIST: Fetch directly from Azure Blob (raw/stockmetadata.parquet).
        // No board filter — RG / NG / TN all map to the same parquet file.
        // Falls back to static STOCK_LIST on error.
        const STOCK_LIST_CACHE_KEY = 'broker_transaction_stock_list_v3';
        const STOCK_LIST_CACHE_TTL = 4 * 60 * 60 * 1000; // 4 h — parquet is updated once per day
        let dynamicStocksLoaded = false;

        // Try localStorage cache first (instant)
        try {
          const cached = localStorage.getItem(STOCK_LIST_CACHE_KEY);
          if (cached) {
            const parsed = JSON.parse(cached);
            if (parsed.timestamp && (Date.now() - parsed.timestamp) < STOCK_LIST_CACHE_TTL && Array.isArray(parsed.stocks) && parsed.stocks.length > 0) {
              setAvailableStocksFromAPI(parsed.stocks);
              console.log(`[NewBrokerSummary] Loaded ${parsed.stocks.length} stocks from cache (blob parquet)`);
              dynamicStocksLoaded = true;
              setIsLoadingStocks(false);
            }
          }
        } catch (e) { /* ignore cache errors */ }

        // Fetch fresh from blob (non-blocking if cache hit; blocking otherwise)
        const fetchFromBlob = async () => {
          const rows = await fetchStockMetadata();
          const stocks = Array.from(new Set(rows.map(r => r.symbol).filter(Boolean))).sort();
          if (stocks.length === 0) throw new Error('Empty stock list from parquet');
          setAvailableStocksFromAPI(stocks);
          console.log(`[NewBrokerSummary] Loaded ${stocks.length} stocks from blob parquet`);
          try {
            localStorage.setItem(STOCK_LIST_CACHE_KEY, JSON.stringify({ stocks, timestamp: Date.now() }));
          } catch (e) { /* ignore */ }
        };

        if (!dynamicStocksLoaded) {
          try {
            await fetchFromBlob();
          } catch (e) {
            setAvailableStocksFromAPI(STOCK_LIST);
            console.warn('[NewBrokerSummary] Fallback: loaded stocks from static list', e);
          }
        } else {
          // Background refresh — parquet is in browser Cache API so this is usually instant
          fetchFromBlob().catch(() => { /* ignore background errors */ });
        }
        setIsLoadingStocks(false);

        // Update sector mapping if we got fresh data (not from cache)
        if (sectorResponse.success && sectorResponse.data && !cachedSectorMapping) {
          setStockToSectorMap(sectorResponse.data.stockToSector);
          // Ensure IDX is included in sectors (IDX is a special sector for aggregate index data)
          const sectorsWithIdx = sectorResponse.data.sectors || [];
          if (!sectorsWithIdx.includes('IDX')) {
            sectorsWithIdx.push('IDX');
          }
          // Sort with IDX always first
          setAvailableSectors(sectorsWithIdx.sort((a: string, b: string) => {
            if (a === 'IDX') return -1;
            if (b === 'IDX') return 1;
            return a.localeCompare(b);
          })); // Exclude 'All' from available sectors
          // Cache for next time
          try {
            localStorage.setItem(SECTOR_MAPPING_CACHE_KEY, JSON.stringify({
              stockToSector: sectorResponse.data.stockToSector,
              sectors: sectorsWithIdx,
              timestamp: Date.now()
            }));
          } catch (e) {
            // Ignore cache errors
          }
        } else if (cachedSectorMapping) {
          // Update cache to include IDX if not present (for old cache)
          const cachedSectors = cachedSectorMapping.sectors || [];
          if (!cachedSectors.includes('IDX')) {
            cachedSectors.push('IDX');
            // Sort with IDX always first
            setAvailableSectors(cachedSectors.sort((a: string, b: string) => {
              if (a === 'IDX') return -1;
              if (b === 'IDX') return 1;
              return a.localeCompare(b);
            }));
            // Update cache
            try {
              localStorage.setItem(SECTOR_MAPPING_CACHE_KEY, JSON.stringify({
                stockToSector: cachedSectorMapping.stockToSector,
                sectors: cachedSectors,
                timestamp: cachedSectorMapping.timestamp
              }));
            } catch (e) {
              // Ignore cache errors
            }
          }
        } else if (!cachedSectorMapping && (!sectorResponse.success || !sectorResponse.data)) {
          console.warn('[NewBrokerSummary] Failed to load sector mapping, sector filter will not work');
          // Fallback: ensure IDX is available even if API fails
          setAvailableSectors(['IDX']);
        }

        // Set default to last 3 trading days ONLY IF no saved preferences exist, OR if in dashboard mode
        // (Dates are now set synchronously at the beginning of this function to prevent UI delay)

          // Trigger initial load for dashboard or first-time users
          if (isDashboard) {
            // Wait briefly to ensure other states (like sectors) are initialized
            setTimeout(() => {
              shouldFetchDataRef.current = true;
              setShouldFetchData(true);
            }, 100);
          }

      } catch (error) {
        console.error('Error loading initial data:', error);
        setIsLoading(false);
        setIsLoadingStocks(false);

        // Fallback to hardcoded broker list and local date calculation
        const brokers = ['MG', 'CIMB', 'UOB', 'COIN', 'NH', 'TRIM', 'DEWA', 'BNCA', 'PNLF', 'VRNA', 'SD', 'LMGA', 'DEAL', 'ESA', 'SSA', 'AK'];
        setAvailableBrokers(brokers);

        // Default to last 3 trading days ONLY IF no saved preferences exist
        const fallbackDates = getTradingDays(3);
        // Sort by date (oldest first) for display
        const sortedDates = [...fallbackDates].sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

        if (!savedPrefs?.startDate || !savedPrefs?.endDate) {
          setSelectedDates(sortedDates);

          if (sortedDates.length > 0) {
            setStartDate(sortedDates[0] ?? '');
            setEndDate(sortedDates[sortedDates.length - 1] ?? '');
          }
        }
      }

      // Trigger auto-fetch if we have dates (either from prefs or fallback)
      // BUT only if we don't have prefs, because the auto-load useEffect handles prefs case
      const currentPrefs = menuPreferencesService.getCachedPreferences(PAGE_ID);
      if (!currentPrefs?.['startDate'] || !currentPrefs?.['endDate']) {
        shouldFetchDataRef.current = true;
        setShouldFetchData(true);
      }
    };

    loadInitialData();
  }, []); // IMPORTANT: Empty dependency array - only run once on mount

  // Auto-load data from saved preferences on mount (after initial data is loaded)
  useEffect(() => {
    // FIX: Force reload preferences using the local typed helper
    // This ensures strict type safety and handles the fallback to defaults correctly
    const prefs = loadPreferences(effectivePageId);

    // Restore non-date preferences that might have been missed by useState initializers
    if (prefs) {
      if (prefs.selectedBrokers && prefs.selectedBrokers.length > 0) setSelectedBrokers(prefs.selectedBrokers);
      if (prefs.selectedTickers && prefs.selectedTickers.length > 0) setSelectedTickers(prefs.selectedTickers);
      if (prefs.selectedSectors && prefs.selectedSectors.length > 0) {
        setSelectedSectors(prefs.selectedSectors);
        // Also update active sector filter immediately
        const newActiveSectorFilter: string = prefs.selectedSectors[0] ?? 'All';
        setActiveSectorFilter(newActiveSectorFilter);
      }
      if (prefs.pivotFilter) setPivotFilter(prefs.pivotFilter);
      if (prefs.invFilter !== undefined) setInvFilter(prefs.invFilter);
      if (prefs.boardFilter !== undefined) setBoardFilter(prefs.boardFilter);
      if (prefs.showFrequency !== undefined) {
        setDraftShowFrequency(prefs.showFrequency);
        setShowFrequency(prefs.showFrequency);
      }
      if (prefs.showOrder !== undefined && !isDashboard) {
        setDraftShowOrder(prefs.showOrder);
        setShowOrder(prefs.showOrder);
      }
      if (prefs.showNoSplit !== undefined) {
        setDraftShowNoSplit(prefs.showNoSplit);
        setShowNoSplit(prefs.showNoSplit);
      }

      // Sync applied states from loaded preferences
      if (prefs.selectedBrokers && prefs.selectedBrokers.length > 0) setAppliedSelectedBrokers(prefs.selectedBrokers);
      if (prefs.selectedTickers && prefs.selectedTickers.length > 0) setAppliedSelectedTickers(prefs.selectedTickers);
      if (prefs.selectedSectors && prefs.selectedSectors.length > 0) setAppliedSelectedSectors(prefs.selectedSectors);
      if (prefs.pivotFilter) setAppliedPivotFilter(prefs.pivotFilter);
      if (prefs.invFilter !== undefined) setAppliedInvFilter(prefs.invFilter);
      if (prefs.boardFilter !== undefined) setAppliedBoardFilter(prefs.boardFilter);
    }

    // Only auto-load dates if we have them in the reloaded prefs and we are NOT in dashboard mode
    if (isDashboard || !prefs?.startDate || !prefs?.endDate) {
      return; // No saved preferences or dashboard mode, don't auto-load dates from prefs
    }

    const savedStartDate = prefs.startDate;
    const savedEndDate = prefs.endDate;

    // Wait a bit to ensure initial data (brokers, sectors) are loaded
    const timer = setTimeout(() => {
      // Generate date array from saved startDate and endDate (only trading days)
      const startParts = savedStartDate.split('-').map(Number);
      const endParts = savedEndDate.split('-').map(Number);

      if (startParts.length === 3 && endParts.length === 3) {
        const startYear = startParts[0];
        const startMonth = startParts[1];
        const startDay = startParts[2];
        const endYear = endParts[0];
        const endMonth = endParts[1];
        const endDay = endParts[2];

        if (startYear !== undefined && startMonth !== undefined && startDay !== undefined &&
          endYear !== undefined && endMonth !== undefined && endDay !== undefined) {
          const start = new Date(startYear, startMonth - 1, startDay);
          const end = new Date(endYear, endMonth - 1, endDay);

          // Check if range is valid
          if (start <= end) {
            // Generate date array (only trading days)
            const dateArray: string[] = [];
            const currentDate = new Date(start);

            while (currentDate <= end) {
              const dayOfWeek = currentDate.getDay();
              // Skip weekends (Saturday = 6, Sunday = 0)
              if (dayOfWeek !== 0 && dayOfWeek !== 6) {
                // Format as YYYY-MM-DD in local timezone
                const year = currentDate.getFullYear();
                const month = String(currentDate.getMonth() + 1).padStart(2, '0');
                const day = String(currentDate.getDate()).padStart(2, '0');
                const dateString = `${year}-${month}-${day}`;
                dateArray.push(dateString);
              }
              // Move to next day
              currentDate.setDate(currentDate.getDate() + 1);
            }

            // Sort by date (oldest first) for display
            const datesToUse = dateArray.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

            // Update selectedDates
            setSelectedDates(datesToUse);
            setStartDate(savedStartDate);
            setEndDate(savedEndDate);

            // Determine active sector filter from saved preferences (redundant but safe)
            if (prefs.selectedSectors && prefs.selectedSectors.length > 0) {
              const savedSectors = prefs.selectedSectors;
              const newActiveSectorFilter: string = savedSectors[0] ?? 'All';
              setActiveSectorFilter(newActiveSectorFilter);
            }

            // Trigger auto-load by setting shouldFetchData to true
            // Use ref first (synchronous), then state (async)
            shouldFetchDataRef.current = true;
            setShouldFetchData(true);
          }
        }
      }
    }, 500); // Small delay to ensure initial data is loaded

    return () => clearTimeout(timer);
  }, []); // Only run once on mount

  // Load transaction data only when shouldFetchData is true (triggered by Show button or auto-load)
  useEffect(() => {
    // Only fetch if shouldFetchData is true
    if (!shouldFetchData) {
      return;
    }

    // Cancel previous request if exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const loadTransactionData = async () => {
      // CRITICAL: Check ref instead of state - ref is always up-to-date even in async context
      if (!shouldFetchDataRef.current) {
        setIsLoading(false);
        setIsDataReady(false);
        return;
      }

      // Validation: For Broker pivot, need brokers OR sectors. For Stock pivot, need tickers OR sectors.
      if (appliedPivotFilter === 'Broker') {
        const hasSpecificBrokers = appliedSelectedBrokers.length > 0 && !appliedSelectedBrokers.includes('ALL');
        const hasAllBroker = appliedSelectedBrokers.includes('ALL');
        const hasSectorOnly = appliedSelectedSectors.length > 0 && (appliedSelectedBrokers.length === 0 || appliedSelectedBrokers.includes('ALL'));
        const isValid = hasSpecificBrokers || hasAllBroker || hasSectorOnly;
        if (!isValid || selectedDates.length === 0) {
          setRawTransactionData(new Map()); // Clear raw data too
          setIsLoading(false);
          setIsDataReady(false);
          setShouldFetchData(false);
          shouldFetchDataRef.current = false;
          return;
        }

        // GUARD: If ALL selected but brokers not loaded yet, wait
        if (hasAllBroker && availableBrokers.length === 0) {
          console.log('[NewBrokerSummary] Skipping load: Brokers not loaded yet');
          return;
        }
      } else if (appliedPivotFilter === 'Stock') {
        if ((appliedSelectedTickers.length === 0 && appliedSelectedSectors.length === 0) || selectedDates.length === 0) {
          setRawTransactionData(new Map()); // Clear raw data too
          setIsLoading(false);
          setIsDataReady(false);
          setShouldFetchData(false);
          shouldFetchDataRef.current = false;
          return;
        }
      }

      // CRITICAL: Check ref again after validation
      if (!shouldFetchDataRef.current || abortController.signal.aborted) {
        setIsLoading(false);
        setIsDataReady(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const cache = dataCacheRef.current;
        const now = Date.now();

        // Clear expired cache entries
        for (const [key, value] of cache.entries()) {
          if (now - value.timestamp > CACHE_EXPIRY_MS) {
            cache.delete(key);
          }
        }

        // Determine all files that need to be fetched
        const allFetchTasks = appliedPivotFilter === 'Stock'
          ? selectedDates.flatMap(date => {
            if (appliedSelectedTickers.length > 0) {
              return appliedSelectedTickers.map((code: string) => ({ code, date, type: 'stock' as const }));
            }
            if (appliedSelectedSectors.length > 0) {
              // OPTIMIZATION: Use SectorTransactionHelper to check if we should use optimized file
              if (isSectorTransaction(appliedPivotFilter, appliedSelectedTickers, appliedSelectedSectors)) {
                // Use single file per sector (e.g. Basic Material_ALL)
                return appliedSelectedSectors.map((sector: string) => {
                  const code = getSectorTransactionCode(sector);
                  return { code, date, type: 'stock' as const };
                });
              }

              // Fallback (should typically not happen given logic above, 
              // unless we decide mixed selection needs fallback, logic here keeps old behavior just in case)
              let stocksToFetch: string[] = [];
              if (appliedSelectedSectors.includes('IDX')) {
                stocksToFetch = availableStocksFromAPI;
              } else {
                stocksToFetch = availableStocksFromAPI.filter(ticker => {
                  const tickerSector = stockToSectorMap[ticker.toUpperCase()];
                  return tickerSector && appliedSelectedSectors.includes(tickerSector);
                });
              }
              return stocksToFetch.map((code: string) => ({ code, date, type: 'stock' as const }));
            }
            return [];
          })
          : selectedDates.flatMap(date => {
            const hasSpecificBrokers = appliedSelectedBrokers.length > 0 && !appliedSelectedBrokers.includes('ALL');
            const hasAllBroker = appliedSelectedBrokers.includes('ALL');

            if (hasAllBroker) {
              // If specific sectors are selected, try fetching sector-specific ALL files
              if (appliedSelectedSectors.length > 0 && !appliedSelectedSectors.includes('IDX')) {
                return appliedSelectedSectors.map(sector => ({
                  code: `${sector}_ALL`,
                  date,
                  type: 'broker' as const,
                  isConsolidated: true
                }));
              }
              // Normal ALL or IDX sector
              return [{ code: 'ALL', date, type: 'broker' as const, isConsolidated: true }];
            } else if (hasSpecificBrokers) {
              return appliedSelectedBrokers.map((code: string) => ({ code, date, type: 'broker' as const }));
            } else if (appliedSelectedSectors.length > 0) {
              // Same as hasAllBroker sector logic
              if (!appliedSelectedSectors.includes('IDX')) {
                return appliedSelectedSectors.map(sector => ({
                  code: `${sector}_ALL`,
                  date,
                  type: 'broker' as const,
                  isConsolidated: true
                }));
              }
              return [{ code: 'ALL', date, type: 'broker' as const, isConsolidated: true }];
            }
            return [];
          });

        // Check cache first and separate cached vs uncached requests
        const cachedResults: Array<{ date: string; code: string; data: BrokerTransactionData[] }> = [];
        const uncachedTasks: Array<{ code: string; date: string }> = [];

        allFetchTasks.forEach(({ code, date }) => {
          const cacheKey = `${code}-${date}-${appliedPivotFilter}-${appliedInvFilter}-${appliedBoardFilter}`;
          const cached = cache.get(cacheKey);
          if (cached && (now - cached.timestamp) <= CACHE_EXPIRY_MS) {
            cachedResults.push({ date, code, data: cached.data });
          } else {
            uncachedTasks.push({ code, date });
          }
        });

        // Initialize with cached data immediately if available
        if (cachedResults.length > 0) {
          setRawTransactionData(prev => {
            const newMap = new Map(prev);
            cachedResults.forEach(({ date, data }) => {
              const existing = newMap.get(date) || [];
              newMap.set(date, [...existing, ...data]);
            });
            return newMap;
          });
          setIsDataReady(true);
        }

        const CONCURRENCY_LIMIT = 50;
        if (uncachedTasks.length > 0) {
          let hasShownFirstData = cachedResults.length > 0;

          // Get a SAS token to fetch broker_transaction CSVs directly from Azure Blob.
          // The backend only gates/authorizes access here; data is read client-side.
          const tokenRes = await api.getBrokerTransactionSasToken();
          if (!tokenRes.success || !tokenRes.data) {
            throw new Error(tokenRes.error || 'Failed to get blob access token');
          }
          const { containerUrl, sasToken } = tokenRes.data;

          // Process tasks in chunks
          for (let i = 0; i < uncachedTasks.length; i += CONCURRENCY_LIMIT) {
            if (!shouldFetchDataRef.current || abortController.signal.aborted) {
              setIsLoading(false);
              setIsDataReady(false);
              return;
            }

            const chunk = uncachedTasks.slice(i, i + CONCURRENCY_LIMIT);
            const chunkPromises = chunk.map(async ({ code, date }) => {
              // Find task metadata to check if it's consolidated
              const task = allFetchTasks.find(t => t.code === code && t.date === date);
              const isConsolidated = (task as any)?.isConsolidated;

              try {
                // Fetch and parse the CSV directly from Azure Blob Storage
                const blobPaths = getBrokerTransactionBlobPath(code, date, appliedPivotFilter, appliedInvFilter, appliedBoardFilter);
                const csvText = await fetchBrokerTransactionCsv(blobPaths, containerUrl, sasToken, abortController.signal);

                if (csvText) {
                  const data = parseBrokerTransactionCSV(csvText, appliedPivotFilter);
                  // Store in cache
                  const cacheKey = `${code}-${date}-${appliedPivotFilter}-${appliedInvFilter}-${appliedBoardFilter}`;
                  cache.set(cacheKey, { data, timestamp: Date.now() });
                  return { success: true, code, date, data };
                }

                // FALLBACK LOGIC for 'ALL' or 'Sector_ALL'
                if (isConsolidated && availableBrokers.length > 0) {
                  console.warn(`[NewBrokerSummary] Consolidated file not found for ${code}-${date}, falling back to individual fetches...`);

                  // Determine which brokers to fetch based on sector if applicable

                  // Fetch all individual brokers sequentially or in parallel?
                  // To keep it simple and safe within this promise, we'll return a special status
                  // so the main loop can handle the fallback by adding new tasks.
                  return { success: false, code, date, error: 'FALLBACK_REQUIRED', isConsolidated };
                }

                return { success: false, code, date, error: `No broker transaction data found for ${code} on ${date}` };
              } catch (err: any) {
                return { success: false, code, date, error: err.message };
              }
            });

            const chunkResults = await Promise.all(chunkPromises);

            if (!shouldFetchDataRef.current || abortController.signal.aborted) {
              setIsLoading(false);
              setIsDataReady(false);
              return;
            }

            const batchResults: Array<{ date: string; code: string; data: BrokerTransactionData[] }> = [];
            const fallbackTasks: Array<{ code: string; date: string }> = [];

            chunkResults.forEach((result: any) => {
              if (result.success && result.data && result.data.length > 0) {
                batchResults.push({ date: result.date, code: result.code, data: result.data });
              } else if (result.error === 'FALLBACK_REQUIRED') {
                // If consolidated file failed, we need to fetch all individual brokers for THIS date
                availableBrokers.forEach(brokerCode => {
                  fallbackTasks.push({ code: brokerCode, date: result.date });
                });
              }
            });

            // If we have fallback tasks, add them to uncachedTasks to be processed in later chunks
            if (fallbackTasks.length > 0) {
              console.log(`[NewBrokerSummary] Adding ${fallbackTasks.length} fallback tasks for individual broker fetches`);
              uncachedTasks.push(...fallbackTasks);
            }

            if (batchResults.length > 0) {
              setRawTransactionData(prev => {
                const newMap = new Map(prev);
                batchResults.forEach(({ date, data }) => {
                  const existing = newMap.get(date) || [];
                  newMap.set(date, [...existing, ...data]);
                });
                return newMap;
              });

              if (!hasShownFirstData) {
                setIsDataReady(true);
                hasShownFirstData = true;
              }
            }
          }
        }

        if (!shouldFetchDataRef.current || abortController.signal.aborted) {
          setIsLoading(false);
          setIsDataReady(false);
          return;
        }

        setIsLoading(false);
        setIsDataReady(true);
        setShouldFetchData(false);
        shouldFetchDataRef.current = false;

      } catch (error: any) {
        if (error.message !== 'Fetch aborted') {
          console.error('Error fetching transaction data:', error);
          setError('Gagal memuat data transaksi. Silakan coba lagi.');
        }
        setIsLoading(false);
        setShouldFetchData(false);
        shouldFetchDataRef.current = false;
      }
    };

    loadTransactionData();

    return () => {
      abortController.abort();
    };
  }, [shouldFetchData, fetchGen]);
  // Run when shouldFetchData toggles (Show button clicked) or fetchGen naik (klik ulang saat fetch berjalan)

  // FOREIGN DATA for FNVal (sama kayak BrokerSummary lama): fetch khusus investor
  // asing (inv=F) per ticker-tanggal. Hanya saat filter All; saat F/D datanya
  // sudah sesuai filter jadi tidak perlu fetch tambahan.
  const [foreignTxByDate, setForeignTxByDate] = useState<Map<string, BrokerTransactionData[]>>(new Map());
  useEffect(() => {
    if (!isDataReady || isLoading) {
      return;
    }
    // Hanya pivot Stock (halaman ini dikunci di situ) + filter All + ada tanggal
    if (appliedPivotFilter !== 'Stock' || appliedInvFilter !== '' || selectedDates.length === 0) {
      setForeignTxByDate(prev => (prev.size > 0 ? new Map() : prev));
      return;
    }

    const abortController = new AbortController();
    const loadForeignData = async () => {
      try {
        const cache = dataCacheRef.current;
        const now = Date.now();

        // Susun task F (mirror task utama pivot Stock)
        let foreignTasks: Array<{ code: string; date: string }> = [];
        if (appliedSelectedTickers.length > 0) {
          foreignTasks = selectedDates.flatMap(date =>
            appliedSelectedTickers.map((code: string) => ({ code, date }))
          );
        } else if (appliedSelectedSectors.length > 0) {
          if (isSectorTransaction(appliedPivotFilter, appliedSelectedTickers, appliedSelectedSectors)) {
            foreignTasks = selectedDates.flatMap(date =>
              appliedSelectedSectors.map((sector: string) => ({ code: getSectorTransactionCode(sector), date }))
            );
          } else {
            let stocksToFetch: string[] = [];
            if (appliedSelectedSectors.includes('IDX')) {
              stocksToFetch = availableStocksFromAPI;
            } else {
              stocksToFetch = availableStocksFromAPI.filter(ticker => {
                const tickerSector = stockToSectorMap[ticker.toUpperCase()];
                return tickerSector && appliedSelectedSectors.includes(tickerSector);
              });
            }
            foreignTasks = selectedDates.flatMap(date =>
              stocksToFetch.map((code: string) => ({ code, date }))
            );
          }
        }
        if (foreignTasks.length === 0) return;

        // Pisahkan cache-hit vs network (cache key sudah memuat inv=F)
        const foreignMap = new Map<string, BrokerTransactionData[]>();
        const uncached: Array<{ code: string; date: string }> = [];
        foreignTasks.forEach(({ code, date }) => {
          const cacheKey = `${code}-${date}-${appliedPivotFilter}-F-${appliedBoardFilter}`;
          const cached = cache.get(cacheKey);
          if (cached && (now - cached.timestamp) <= CACHE_EXPIRY_MS) {
            const existing = foreignMap.get(date) || [];
            foreignMap.set(date, [...existing, ...cached.data]);
          } else {
            uncached.push({ code, date });
          }
        });

        if (uncached.length > 0) {
          const tokenRes = await api.getBrokerTransactionSasToken();
          if (!tokenRes.success || !tokenRes.data) {
            throw new Error(tokenRes.error || 'Failed to get blob access token');
          }
          const { containerUrl, sasToken } = tokenRes.data;

          const CONCURRENCY_LIMIT = 50;
          for (let i = 0; i < uncached.length; i += CONCURRENCY_LIMIT) {
            if (abortController.signal.aborted) return;
            const chunk = uncached.slice(i, i + CONCURRENCY_LIMIT);
            const chunkResults = await Promise.all(chunk.map(async ({ code, date }) => {
              try {
                const blobPaths = getBrokerTransactionBlobPath(code, date, appliedPivotFilter, 'F', appliedBoardFilter);
                const csvText = await fetchBrokerTransactionCsv(blobPaths, containerUrl, sasToken, abortController.signal);
                if (!csvText) return null;
                const data = parseBrokerTransactionCSV(csvText, appliedPivotFilter);
                if (data.length === 0) return null;
                const cacheKey = `${code}-${date}-${appliedPivotFilter}-F-${appliedBoardFilter}`;
                cache.set(cacheKey, { data, timestamp: Date.now() });
                return { date, data };
              } catch (err: any) {
                if (err?.name === 'AbortError') throw err;
                return null;
              }
            }));
            if (abortController.signal.aborted) return;
            chunkResults.forEach(res => {
              if (res && res.data.length > 0) {
                const existing = foreignMap.get(res.date) || [];
                foreignMap.set(res.date, [...existing, ...res.data]);
              }
            });
          }
        }

        if (abortController.signal.aborted) return;
        setForeignTxByDate(foreignMap);
      } catch (error: any) {
        if (error?.name !== 'AbortError' && error?.message !== 'Fetch aborted') {
          console.warn('[NewBrokerSummary] Foreign fetch for FNVal failed:', error?.message || error);
        }
      }
    };

    loadForeignData();

    return () => {
      abortController.abort();
    };
  }, [isDataReady, isLoading, appliedInvFilter, appliedBoardFilter, appliedPivotFilter, selectedDates, appliedSelectedTickers, appliedSelectedSectors, availableStocksFromAPI, stockToSectorMap]);

  // PROCESS DATA: Memoized Aggregation Logic
  // This decouple processing from fetching, allowing UI to remain responsive during heavy calc
  const transactionData = useMemo(() => {
    // Return empty if no data
    if (rawTransactionData.size === 0) return new Map<string, BrokerTransactionData[]>();

    const processedMap = new Map<string, BrokerTransactionData[]>();

    for (const date of selectedDates) {
      // Get raw data for this date
      const rawDateRows = rawTransactionData.get(date);
      if (!rawDateRows || rawDateRows.length === 0) continue;

      // Processing Logic matches original implementation but is now memoized
      let processedRows: BrokerTransactionData[] = [];

      // Logic Branch 1: Stock Pivot (Group by Emiten)
      if (appliedPivotFilter === 'Stock') {
        const stockDataMap = new Map<string, BrokerTransactionData[]>();


        // Sector Logic: Group by Emiten if sector selected
        if (appliedSelectedSectors.length > 0) {
          rawDateRows.forEach(row => {
            const emiten = row.Emiten || '';
            if (!emiten) return;
            // Append to group
            const existing = stockDataMap.get(emiten) || [];
            stockDataMap.set(emiten, [...existing, row]);
          });
        } else {
          // Individual Tickers: Replace/Dedup
          rawDateRows.forEach(row => {
            const emiten = (row.Emiten || 'UNKNOWN').toUpperCase();

            // Handle duplicates if necessary (original logic used processedStocks)
            // Simpler approach: Group everything by Emiten
            const existing = stockDataMap.get(emiten) || [];
            stockDataMap.set(emiten, [...existing, row]);
          });
        }

        // Flatten grouped data
        const allStockRows: BrokerTransactionData[] = [];
        stockDataMap.forEach(rows => {
          for (let i = 0; i < rows.length; i++) {
            allStockRows.push(rows[i]);
          }
        });

        // Filter by Broker (if selected)
        let filteredRows = allStockRows;
        if (appliedSelectedBrokers.length > 0 && !appliedSelectedBrokers.includes('ALL')) {
          const authorizedBrokers = new Set(appliedSelectedBrokers.map(b => b.toUpperCase()));
          filteredRows = filteredRows.filter(row => {
            const emiten = (row.Emiten || '').toUpperCase();
            // Check if any participating party matches selected broker
            return authorizedBrokers.has(emiten) ||
              authorizedBrokers.has((row.BCode || '').toUpperCase()) ||
              authorizedBrokers.has((row.SCode || '').toUpperCase()) ||
              authorizedBrokers.has((row.NBCode || '').toUpperCase()) ||
              authorizedBrokers.has((row.NSCode || '').toUpperCase());
          });
        }

        // Aggregate Globally by Broker Code (if multiple tickers selected & no sector)
        if (appliedSelectedTickers.length > 1 && appliedSelectedSectors.length === 0) {
          const globalBrokerMap = new Map<string, BrokerTransactionData[]>();

          filteredRows.forEach(row => {
            // Identify broker code
            const brokerCode = String(row.BCode || row.SCode || row.NBCode || row.NSCode || row.Emiten || '').toUpperCase().trim();
            if (!brokerCode) return;
            const existing = globalBrokerMap.get(brokerCode) || [];
            globalBrokerMap.set(brokerCode, [...existing, row]);
          });

          // Reduce/Sum Aggregation
          processedRows = [];
          globalBrokerMap.forEach(brokerRows => {
            if (brokerRows.length === 0) return;
            if (brokerRows.length === 1) {
              // Single row - recalculate avgs just in case
              const r = brokerRows[0]!;
              if ((!r.BuyerAvg || r.BuyerAvg === 0) && r.BuyerVol) r.BuyerAvg = r.BuyerValue / r.BuyerVol;
              if ((!r.SellerAvg || r.SellerAvg === 0) && r.SellerVol) r.SellerAvg = r.SellerValue / r.SellerVol;
              processedRows.push(r);
              return;
            }

            // Aggregate multiple rows
            const first = brokerRows[0]!;
            const acc = {
              ...first,
              BuyerVol: 0, BuyerValue: 0, BuyerAvg: 0, BFreq: 0, BOrdNum: 0, BLot: 0,
              SellerVol: 0, SellerValue: 0, SellerAvg: 0, SFreq: 0, SOrdNum: 0, SLot: 0,
              NetBuyVol: 0, NetBuyValue: 0, NetSellVol: 0, NetSellValue: 0,
              TotalVolume: 0, TotalValue: 0, TransactionCount: 0, AvgPrice: 0
            };

            brokerRows.forEach(r => {
              acc.BuyerVol += r.BuyerVol || 0;
              acc.BuyerValue += r.BuyerValue || 0;
              acc.BFreq = (acc.BFreq || 0) + (r.BFreq || 0);
              acc.BOrdNum = (acc.BOrdNum || 0) + (r.BOrdNum || 0);
              acc.BLot = (acc.BLot || 0) + (r.BLot || 0);

              acc.SellerVol += r.SellerVol || 0;
              acc.SellerValue += r.SellerValue || 0;
              acc.SFreq = (acc.SFreq || 0) + (r.SFreq || 0);
              acc.SOrdNum = (acc.SOrdNum || 0) + (r.SOrdNum || 0);
              acc.SLot = (acc.SLot || 0) + (r.SLot || 0);

              // Note: Net values are re-calculated at end
              acc.TotalVolume += r.TotalVolume || 0;
              acc.TotalValue += r.TotalValue || 0;
              acc.TransactionCount += r.TransactionCount || 0;
            });

            // Recalc Averages & Net
            if (acc.BuyerVol > 0) acc.BuyerAvg = acc.BuyerValue / acc.BuyerVol;
            if (acc.SellerVol > 0) acc.SellerAvg = acc.SellerValue / acc.SellerVol;
            if (acc.TotalVolume > 0) acc.AvgPrice = acc.TotalValue / acc.TotalVolume;

            // Net Calculation
            const netVol = acc.BuyerVol - acc.SellerVol;
            const netVal = acc.BuyerValue - acc.SellerValue;

            // Determine Net Buy vs Net Sell based on VALUE (not Volume)
            if (netVal >= 0) {
              acc.NetBuyVol = netVol;
              acc.NetBuyValue = netVal;
              // Map to short properties for aggregation compatibility
              acc.NBVal = netVal;
              acc.NBLot = netVol / 100;

              acc.NetSellVol = 0;
              acc.NetSellValue = 0;
              acc.NBCode = acc.BCode || '';
              acc.NSCode = '';

              // Clear short properties for sell side
              acc.NSVal = 0;
              acc.NSLot = 0;
            } else {
              acc.NetBuyVol = 0;
              acc.NetBuyValue = 0;

              // Clear short properties for buy side
              acc.NBVal = 0;
              acc.NBLot = 0;

              acc.NetSellVol = Math.abs(netVol);
              acc.NetSellValue = Math.abs(netVal);
              // Map to short properties for aggregation compatibility
              acc.NSVal = Math.abs(netVal);
              acc.NSLot = Math.abs(netVol) / 100;

              acc.NSCode = acc.SCode || '';
              acc.NBCode = '';
            }

            // Re-map derived fields for display (LotPerFreq etc)
            acc.BLotPerFreq = acc.BFreq ? acc.BuyerVol / acc.BFreq : 0;
            acc.SLotPerFreq = acc.SFreq ? acc.SellerVol / acc.SFreq : 0;

            processedRows.push(acc);
          });

        } else {
          // Single ticker OR Sector mode: Use filtered rows directly (no global broker aggregation)
          processedRows = filteredRows;
        }

      } else {
        // Logic Branch 2: Broker Pivot (Group by Emiten)
        // Aggregate same Emiten across multiple brokers (or same broker multiple entries)

        // Filter by Ticker (if selected)
        let filteredRows = rawDateRows;

        // Apply Ticker Filter (Normal and Sector)
        if (appliedSelectedTickers.length > 0) {
          const authorizedTickers = new Set(appliedSelectedTickers.map(t => t.toUpperCase()));
          filteredRows = filteredRows.filter(row => {
            const emiten = (row.Emiten || '').toUpperCase();
            return authorizedTickers.has(emiten);
          });
        }

        // Apply Sector Filter
        if (appliedSelectedSectors.length > 0) {
          filteredRows = filteredRows.filter(row => {
            const emiten = (row.Emiten || '').toUpperCase();
            const sector = stockToSectorMap[emiten];
            if (appliedSelectedSectors.includes('IDX')) return true;
            return sector && appliedSelectedSectors.includes(sector);
          });
        }

        // Aggregate by Emiten
        const emitenMap = new Map<string, BrokerTransactionData[]>();
        filteredRows.forEach(row => {
          const emiten = (row.Emiten || '').toUpperCase();
          if (!emiten) return;
          const existing = emitenMap.get(emiten) || [];
          emitenMap.set(emiten, [...existing, row]);
        });

        processedRows = [];
        emitenMap.forEach((rows, _emiten) => {
          if (rows.length === 0) return;
          if (rows.length === 1) {
            processedRows.push(rows[0]!);
            return;
          }

          const first = rows[0]!;
          const acc = {
            ...first,
            BuyerVol: 0, BuyerValue: 0, BuyerAvg: 0, BFreq: 0, BOrdNum: 0, BLot: 0,
            SellerVol: 0, SellerValue: 0, SellerAvg: 0, SFreq: 0, SOrdNum: 0, SLot: 0,
            NetBuyVol: 0, NetBuyValue: 0, NetSellVol: 0, NetSellValue: 0,
            TotalVolume: 0, TotalValue: 0, TransactionCount: 0, AvgPrice: 0
          };

          rows.forEach(r => {
            acc.BuyerVol += r.BuyerVol || 0;
            acc.BuyerValue += r.BuyerValue || 0;
            acc.BFreq = (acc.BFreq || 0) + (r.BFreq || 0);
            acc.BOrdNum = (acc.BOrdNum || 0) + (r.BOrdNum || 0);
            acc.BLot = (acc.BLot || 0) + (r.BLot || 0);

            acc.SellerVol += r.SellerVol || 0;
            acc.SellerValue += r.SellerValue || 0;
            acc.SFreq = (acc.SFreq || 0) + (r.SFreq || 0);
            acc.SOrdNum = (acc.SOrdNum || 0) + (r.SOrdNum || 0);
            acc.SLot = (acc.SLot || 0) + (r.SLot || 0);

            acc.TotalVolume += r.TotalVolume || 0;
            acc.TotalValue += r.TotalValue || 0;
            acc.TransactionCount += r.TransactionCount || 0;
          });

          if (acc.BuyerVol > 0) acc.BuyerAvg = acc.BuyerValue / acc.BuyerVol;
          if (acc.SellerVol > 0) acc.SellerAvg = acc.SellerValue / acc.SellerVol;
          if (acc.TotalVolume > 0) acc.AvgPrice = acc.TotalValue / acc.TotalVolume;

          const netVol = acc.BuyerVol - acc.SellerVol;
          const netVal = acc.BuyerValue - acc.SellerValue;

          // Determine Net Buy vs Net Sell based on VALUE (not Volume)
          if (netVal >= 0) {
            acc.NetBuyVol = netVol;
            acc.NetBuyValue = netVal;
            // Map to short properties for aggregation compatibility
            acc.NBVal = netVal;
            acc.NBLot = netVol / 100;

            acc.NetSellVol = 0;
            acc.NetSellValue = 0;
            acc.NBCode = acc.BCode || '';
            acc.NSCode = '';

            // Clear short properties for sell side
            acc.NSVal = 0;
            acc.NSLot = 0;
          } else {
            acc.NetBuyVol = 0;
            acc.NetBuyValue = 0;

            // Clear short properties for buy side
            acc.NBVal = 0;
            acc.NBLot = 0;

            acc.NetSellVol = Math.abs(netVol);
            acc.NetSellValue = Math.abs(netVal);
            // Map to short properties for aggregation compatibility
            acc.NSVal = Math.abs(netVal);
            acc.NSLot = Math.abs(netVol) / 100;

            acc.NSCode = acc.SCode || '';
            acc.NBCode = '';
          }

          acc.BLotPerFreq = acc.BFreq ? acc.BuyerVol / acc.BFreq : 0;
          acc.SLotPerFreq = acc.SFreq ? acc.SellerVol / acc.SFreq : 0;

          processedRows.push(acc);
        });
      }

      processedMap.set(date, processedRows);
    }

    return processedMap;

  }, [rawTransactionData, appliedPivotFilter, appliedSelectedBrokers, appliedSelectedTickers, appliedSelectedSectors, selectedDates, stockToSectorMap]);

  // CRITICAL: REMOVED useEffect that updates selectedDates when startDate/endDate changes
  // selectedDates will ONLY be updated when user clicks Show button
  // This ensures NO side effects when user changes date input - table and data remain unchanged

  const formatDisplayDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Helper function to trigger date picker
  const triggerDatePicker = (inputRef: React.RefObject<HTMLInputElement>) => {
    if (inputRef.current) {
      inputRef.current.showPicker();
    }
  };

  // Helper function to format date for input (YYYY-MM-DD)
  const formatDateForInput = (date: string | undefined) => {
    return date || ''; // Already in YYYY-MM-DD format
  };

  // Font size fixed to normal
  const getFontSizeClass = () => 'text-[12px]';

  // Optimize handlers with useCallback to prevent unnecessary re-renders
  const handleBrokerSelect = useCallback((broker: string) => {
    setSelectedBrokers(prev => {
      if (broker === 'ALL') {
        // Select "ALL" only (don't add all individual brokers to avoid cluttering the list)
        if (!prev.includes('ALL')) {
          return ['ALL'];
        }
        return prev;
      } else if (!prev.includes(broker)) {
        // If "ALL" is already selected, replace it with the specific broker
        // Otherwise, add the broker to the list
        if (prev.includes('ALL')) {
          return [broker];
        } else {
          return [...prev, broker];
        }
      }
      return prev;
    });
    setBrokerInput('');
    // Don't close dropdown - allow multiple selections
  }, []);

  // Handle broker removal
  const handleRemoveBroker = useCallback((broker: string) => {
    setSelectedBrokers(prev => prev.filter(b => b !== broker));
    // CRITICAL: Keep existing data visible - no auto-fetch, no hide tables
    // User must click Show button to fetch new data
  }, []);

  const handleClearAllBrokers = useCallback(() => {
    setSelectedBrokers([]);
  }, []);

  // Parse ketikan mentah jadi pilihan valid TANPA ubah state.
  // "AADI" → [AADI], "AADI, BKSL" → keduanya. Teks = daftar lengkap pilihan
  // (bukan tambahan), jadi menghapus emiten dari teks = emiten itu ikut lepas.
  // Sumber: gabungan API + hasil fetch + static (tidak tergantung timing load).
  const parseTypedSelections = () => {
    const stockSet = new Set<string>();
    availableStocksFromAPI.forEach(s => stockSet.add(String(s).trim().toUpperCase()));
    availableTickers.forEach(s => stockSet.add(String(s).trim().toUpperCase()));
    (STOCK_LIST || []).forEach((s: string) => stockSet.add(String(s).trim().toUpperCase()));
    const sectorMap = new Map(availableSectors.map(s => [s.trim().toUpperCase(), s]));
    const tickers: string[] = [];
    const sectors: string[] = [];
    let matched = 0; // jumlah token valid
    const commitToken = (tok: string) => {
      if (!tok) return;
      if (stockSet.has(tok)) {
        matched++;
        if (!tickers.includes(tok)) tickers.push(tok);
      } else {
        const sec = sectorMap.get(tok);
        if (sec) {
          matched++;
          if (!sectors.includes(sec)) sectors.push(sec);
        }
      }
    };
    tickerInput.split(/[,;]+/).forEach(part => {
      const token = part.trim().toUpperCase();
      if (!token) return;
      // Cocok utuh dulu (penting untuk nama sektor multi-kata), kalau tidak cocok pecah per spasi
      if (stockSet.has(token) || sectorMap.has(token)) {
        commitToken(token);
      } else {
        token.split(/\s+/).forEach(commitToken);
      }
    });
    return { tickers, sectors, matched };
  };

  // Pilihan "saat ini": kalau teks sedang diedit, teks yang berlaku.
  // Dipakai juga untuk daftar "Selected" & tanda ✓ di dropdown supaya langsung
  // ikut berubah saat teks dihapus/diketik (tanpa menunggu commit).
  // Sengaja tidak di-commit ke selectedTickers per ketikan: itu memicu render ulang tabel.
  const liveTickerSelection = useMemo(
    () => tickerTextDirty
      ? parseTypedSelections()
      : { tickers: selectedTickers, sectors: selectedSectors, matched: 0 },
    [tickerTextDirty, tickerInput, selectedTickers, selectedSectors, availableStocksFromAPI, availableTickers, availableSectors]
  );
  const getBaseSelection = () => liveTickerSelection;

  const applySelection = (tickers: string[], sectors: string[]) => {
    setSelectedTickers(tickers);
    setSelectedSectors(sectors);
    setTickerInput('');
    setTickerTextDirty(false);
  };

  // Commit ketikan ke pilihan saat klik di luar dropdown (termasuk klik Show).
  // mousedown jalan sebelum onClick Show, jadi ketikan harus di-commit di sini,
  // kalau dibuang Show hanya melihat tickerInput kosong.
  // Ketikan tanpa token valid dibiarkan supaya Show bisa kasih warning.
  const commitTypedTickersRef = useRef<() => void>(() => {});
  commitTypedTickersRef.current = () => {
    if (!tickerTextDirty) return;
    const sel = parseTypedSelections();
    if (tickerInput.trim() !== '' && sel.matched === 0) return;
    applySelection(sel.tickers, sel.sectors);
  };

  // Handle ticker selection
  // Don't close dropdown - allow multiple selections
  const handleTickerSelect = (ticker: string) => {
    const base = getBaseSelection();
    applySelection(base.tickers.includes(ticker) ? base.tickers : [...base.tickers, ticker], base.sectors);
  };

  // Handle ticker removal
  // CRITICAL: Keep existing data visible - no auto-fetch, no hide tables
  // User must click Show button to fetch new data
  const handleRemoveTicker = (ticker: string) => {
    const base = getBaseSelection();
    applySelection(base.tickers.filter(t => t !== ticker), base.sectors);
  };

  const handleClearAll = () => {
    applySelection([], []);
  };

  // Handle sector selection (from combined dropdown)
  const handleSectorSelect = (sector: string) => {
    const base = getBaseSelection();
    applySelection(base.tickers, base.sectors.includes(sector) ? base.sectors : [...base.sectors, sector]);
  };

  // Uncheck sektor dari list (tanpa reset ticker)
  const handleDeselectSector = (sector: string) => {
    const base = getBaseSelection();
    applySelection(base.tickers, base.sectors.filter(s => s !== sector));
  };

  // Handle sector removal
  // When the last sector is removed, clear selectedTickers to return to normal state
  // (dropdown shows all tickers again)
  const handleRemoveSector = (sector: string) => {
    const base = getBaseSelection();
    const newSectors = base.sectors.filter(s => s !== sector);
    applySelection(base.sectors.length === 1 ? [] : base.tickers, newSectors);
  };

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownBrokerRef.current && !dropdownBrokerRef.current.contains(event.target as Node)) {
        setShowBrokerSuggestions(false);
        setBrokerInput('');
      }
      if (dropdownTickerRef.current && !dropdownTickerRef.current.contains(event.target as Node)) {
        setShowTickerSuggestions(false);
        commitTypedTickersRef.current();
      }
      if (blockDropdownRef.current && !blockDropdownRef.current.contains(event.target as Node)) {
        setShowBlockDropdown(false);
      }
      if (rangeDropdownRef.current && !rangeDropdownRef.current.contains(event.target as Node)) {
        setShowRangeDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Monitor menu height to detect if it wraps to 2 rows
  useEffect(() => {
    const checkMenuHeight = () => {
      if (menuContainerRef.current) {
        // If menu height is more than ~50px, it's likely 2 rows (single row is usually ~40-45px)
        // const menuHeight = menuContainerRef.current.offsetHeight; // (Currently Unused)
      }
    };

    // Check initially
    checkMenuHeight();

    // Check on window resize
    window.addEventListener('resize', checkMenuHeight);

    // Use ResizeObserver for more accurate detection
    let resizeObserver: ResizeObserver | null = null;
    if (menuContainerRef.current) {
      resizeObserver = new ResizeObserver(() => {
        checkMenuHeight();
      });
      resizeObserver.observe(menuContainerRef.current);
    }

    // Also check when selectedTickers changes (ticker selection affects menu height)
    const timeoutId = setTimeout(checkMenuHeight, 100);

    return () => {
      window.removeEventListener('resize', checkMenuHeight);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      clearTimeout(timeoutId);
    };
  }, [selectedTickers, selectedBrokers, selectedDates, startDate, endDate, invFilter, boardFilter]);

  // OPTIMIZED: Sync table widths between Value and Net tables - Simplified and more efficient
  useEffect(() => {
    // Wait for tables to be ready (data loaded and DOM rendered)
    if (isLoading || !isDataReady) return;

    // DINONAKTIFKAN sementara (uji coba): sistem kunci-lebar lama di bawah menimpa
    // setelan React (kunci 24px/colgroup) dengan hasil ukur DOM yang terkadang kelebaran
    // (font belum siap / konten parsial) sehingga awal-load beda dengan habis-Show.
    // Sinkronisasi gross-vs-net tetap ditangani effect per-kolom yang terpisah.
    // Untuk rollback: hapus 1 baris return ini.
    return;

    // FIXED: Flag to prevent infinite loop from ResizeObserver
    let isSyncing = false;

    // Calculate columns per date group dynamically (Not used in this simplified sync)
    // const baseCols = 9;
    // const optionalCols = (showFrequency ? 4 : 0) + (showOrder ? 4 : 0);
    // const colsPerDate = baseCols + optionalCols;

    const syncTableWidths = () => {


      // GUARD: Prevent recursive calls
      if (isSyncing) return;

      const valueContainer = valueTableContainerRef.current;
      const netContainer = netTableContainerRef.current;
      const valueTable = valueTableRef.current;
      const netTable = netTableRef.current;

      if (!valueContainer || !netContainer || !valueTable || !netTable) return;

      // Set flag to prevent recursive calls
      isSyncing = true;

      // DISABLED: Syncing container widths to 100% was causing "melebar" (widening) bug
      // Each table should stick to its natural max-content width based on column constants
      /*
      if (selectedDates.length <= 7) {
        // ...
      }
      */


      // Step 2: Let VALUE table calculate column widths naturally with auto layout first
      // CRITICAL: Force width max-content to prevent columns from stretching to fill container
      valueTable.style.tableLayout = 'auto';
      netTable.style.tableLayout = 'auto';
      valueTable.style.width = 'max-content';
      valueTable.style.minWidth = '0';
      netTable.style.width = 'max-content';
      netTable.style.minWidth = '0';


      // Force a reflow to ensure layout is stable before measuring
      void valueTable.offsetWidth;
      void netTable.offsetWidth;

      // Step 3: Measure column widths from VALUE table after it stabilizes
      const valueHeaderRows = valueTable.querySelectorAll('thead tr');
      const netHeaderRows = netTable.querySelectorAll('thead tr');

      if (valueHeaderRows.length >= 2 && netHeaderRows.length >= 2) {
        const valueColumnHeaderRow = valueHeaderRows[1];
        const netColumnHeaderRow = netHeaderRows[1];

        if (valueColumnHeaderRow && netColumnHeaderRow) {
          const valueHeaderCells = Array.from(valueColumnHeaderRow.querySelectorAll('th'));
          const netHeaderCells = Array.from(netColumnHeaderRow.querySelectorAll('th'));
          const valueBodyRows = valueTable.querySelectorAll('tbody tr');

          // Ensure both tables have same number of columns
          if (valueHeaderCells.length === netHeaderCells.length) {
            // Calculate column widths from VALUE table
            const columnWidths: number[] = [];

            valueHeaderCells.forEach((valueCell, index) => {
              const valueEl = valueCell as HTMLElement;

              // CRITICAL: For accurate measurement, prioritize body cells over header cells
              // Body cells have actual data and more accurate widths, especially for first column
              let maxWidth = 0;
              let minWidth = Infinity;
              let widthsWithBorder: number[] = [];
              let widthsWithoutBorder: number[] = [];

              // First, measure from body cells (more accurate, especially for first column with border-l-2)
              // OPTIMIZED: Measure only first 50 rows (sampling) for performance when large data is loaded
              const sampleRows = Array.from(valueBodyRows).slice(0, 50);
              sampleRows.forEach((row) => {
                const cells = row.querySelectorAll('td');
                if (cells[index]) {
                  const cellEl = cells[index] as HTMLElement;
                  // Use getBoundingClientRect().width for more accurate measurement
                  // This gives the actual rendered width including all borders and padding
                  const cellRect = cellEl.getBoundingClientRect();
                  const cellWidth = cellRect.width;
                  maxWidth = Math.max(maxWidth, cellWidth);
                  minWidth = Math.min(minWidth, cellWidth);

                  // For first column, check if this cell has border-l-2
                  if (index === 0) {
                    const computedStyle = window.getComputedStyle(cellEl);
                    const borderLeft = parseFloat(computedStyle.borderLeftWidth) || 0;
                    if (borderLeft >= 2) {
                      widthsWithBorder.push(cellWidth);
                    } else {
                      widthsWithoutBorder.push(cellWidth);
                    }
                  }
                }
              });

              // If no body cells found, fallback to header cell
              if (maxWidth === 0) {
                const headerRect = valueEl.getBoundingClientRect();
                maxWidth = headerRect.width;
              } else {
                // Ensure header cell matches the body cell width
                // This is especially important for first column with border-l-2
                const headerRect = valueEl.getBoundingClientRect();
                // Use the larger of body width or header width to ensure consistency
                maxWidth = Math.max(maxWidth, headerRect.width);
              }

              // CRITICAL: For first column (index 0), use the width from cells WITH border-l-2
              // This ensures consistent width and eliminates the "space kosong" issue
              if (index === 0 && widthsWithBorder.length > 0) {
                // Use the average width of cells with border-l-2 (more accurate)
                const avgWidthWithBorder = widthsWithBorder.reduce((sum, w) => sum + w, 0) / widthsWithBorder.length;
                maxWidth = avgWidthWithBorder;
              } else if (index === 0 && widthsWithoutBorder.length > 0 && widthsWithBorder.length === 0) {
                // If no cells with border found, use cells without border and add border width
                const avgWidthWithoutBorder = widthsWithoutBorder.reduce((sum, w) => sum + w, 0) / widthsWithoutBorder.length;
                const computedStyle = window.getComputedStyle(valueEl);
                const borderLeft = parseFloat(computedStyle.borderLeftWidth) || 0;
                maxWidth = avgWidthWithoutBorder + borderLeft;
              }

              columnWidths[index] = maxWidth;
            });

            // CRITICAL: Cap code columns (BY/SL) to COLUMN_WIDTHS.CODE to prevent
            // measured widths from overriding the intended fixed width.
            // Column structure per date group: BY, BVal, BLot, BAvg, [BFreq, Lot/F], [BOr, Lot/Or], [BNS, Lot/NS], #, SL, SVal, SLot, SAvg, [SFreq, Lot/F], [SOr, Lot/Or], [SNS, Lot/NS]
            const codeMaxPx = parseFloat(COLUMN_WIDTHS.CODE) || 40;
            const buyerBaseCols = 4; // BY, BVal, BLot, BAvg
            const buyerOptCols = (showFrequency ? 2 : 0) + (showOrder ? 2 : 0) + (showNoSplit ? 2 : 0);
            const sepCol = 1; // # separator
            const byOffset = 0; // BY is always first in each date group
            const slOffset = buyerBaseCols + buyerOptCols + sepCol; // SL comes after buyer cols + #
            const sellerBaseCols = 4; // SL, SVal, SLot, SAvg
            const sellerOptCols = (showFrequency ? 2 : 0) + (showOrder ? 2 : 0) + (showNoSplit ? 2 : 0);
            const colsPerDateGroup = buyerBaseCols + buyerOptCols + sepCol + sellerBaseCols + sellerOptCols;

            for (let i = 0; i < columnWidths.length; i++) {
              const posInGroup = i % colsPerDateGroup;
              if (posInGroup === byOffset || posInGroup === slOffset) {
                // This is a code column (BY or SL) — cap width
                columnWidths[i] = Math.min(columnWidths[i] ?? 0, codeMaxPx);
              }
            }

            // Store column widths in ref for later use
            columnWidthsRef.current = columnWidths;

            // Step 4: Apply fixed layout and sync widths to both tables
            // CRITICAL: Set fixed layout FIRST before applying widths
            valueTable.style.tableLayout = 'fixed';
            netTable.style.tableLayout = 'fixed';

            // Force a reflow after setting fixed layout
            void valueTable.offsetWidth;
            void netTable.offsetWidth;

            // Apply widths to VALUE table headers (lock widths to prevent reflow changes)
            valueHeaderCells.forEach((valueCell, index) => {
              const valueEl = valueCell as HTMLElement;
              const width = columnWidths[index];

              if (width && width > 0) {
                // Lock width with all three properties to prevent browser from changing it
                valueEl.style.width = `${width}px`;
                valueEl.style.minWidth = `${width}px`;
                valueEl.style.maxWidth = `${width}px`;
                // Also set box-sizing to ensure border is included correctly
                valueEl.style.boxSizing = 'border-box';
              }
            });

            // Apply same widths to NET table headers (to match VALUE table)
            netHeaderCells.forEach((netCell, index) => {
              const netEl = netCell as HTMLElement;
              const width = columnWidths[index];

              if (width && width > 0) {
                // Lock width with all three properties to prevent browser from changing it
                netEl.style.width = `${width}px`;
                netEl.style.minWidth = `${width}px`;
                netEl.style.maxWidth = `${width}px`;
                // Also set box-sizing to ensure border is included correctly
                netEl.style.boxSizing = 'border-box';
              }
            });

            // Apply widths to body cells in both tables (lock to prevent reflow changes)
            const netBodyRows = netTable.querySelectorAll('tbody tr');

            valueBodyRows.forEach((row) => {
              const cells = row.querySelectorAll('td');
              cells.forEach((cell, index) => {
                const cellEl = cell as HTMLElement;
                const width = columnWidths[index];

                if (width && width > 0) {
                  // Lock width to prevent browser from changing it during reflow
                  cellEl.style.width = `${width}px`;
                  cellEl.style.minWidth = `${width}px`;
                  cellEl.style.maxWidth = `${width}px`;
                  cellEl.style.boxSizing = 'border-box';
                }
              });
            });

            netBodyRows.forEach((row) => {
              const cells = row.querySelectorAll('td');
              cells.forEach((cell, index) => {
                const cellEl = cell as HTMLElement;
                const width = columnWidths[index];

                if (width && width > 0) {
                  // Lock width to prevent browser from changing it during reflow
                  cellEl.style.width = `${width}px`;
                  cellEl.style.minWidth = `${width}px`;
                  cellEl.style.maxWidth = `${width}px`;
                  cellEl.style.boxSizing = 'border-box';
                }
              });
            });

            // Force a final reflow to ensure all widths are applied and locked
            void valueTable.offsetWidth;
            void netTable.offsetWidth;

            // CRITICAL: Re-apply fixed layout after setting widths to ensure it sticks
            // This prevents React re-render from overriding the layout
            valueTable.style.tableLayout = 'fixed';
            netTable.style.tableLayout = 'fixed';
            // CRITICAL: Prevent table from forcibly expanding to 100% (fixing "melebar sendiri" bug)
            // Use max-content so table width perfectly matches sum of fixed column widths
            valueTable.style.width = 'max-content';
            netTable.style.width = 'max-content';
          }
        }
      }

      // Reset flag after sync completes
      isSyncing = false;
    };

    // CRITICAL: Reset sync flag when data changes or when loading starts
    // This ensures sync happens every time new data is loaded (not just first time)
    if (transactionData.size === 0 || isLoading) {
      hasSyncedColumnWidthsRef.current = false;
      columnWidthsRef.current = [];
    }

    // FIXED: Sync container widths once when data is ready
    // This ensures tables align without breaking column rendering
    // CRITICAL: Only sync once per data load, not when visibleRowCount changes (lazy loading)
    // IMPORTANT: Sync every time data is ready (not just first time) to handle data changes
    if (isDataReady && visibleRowCount > 0 && maxRows > 0 && !hasSyncedColumnWidthsRef.current) {
      // Use multiple RAF + timeout to ensure DOM is fully ready and all styles are applied
      // This is critical for accurate column width measurement, especially for first column with border-l-2
      let rafId1: number | null = null;
      let rafId2: number | null = null;
      let rafId3: number | null = null;
      let timeoutId: NodeJS.Timeout | null = null;

      let doubleCheckTimeoutId: NodeJS.Timeout | null = null;

      let syncStarted = false;
      let fontsTimeoutId: NodeJS.Timeout | null = null;
      const startSyncChain = () => {
        if (syncStarted) return;
        syncStarted = true;
        if (fontsTimeoutId !== null) clearTimeout(fontsTimeoutId);
      rafId1 = requestAnimationFrame(() => {
        rafId2 = requestAnimationFrame(() => {
          rafId3 = requestAnimationFrame(() => {
            timeoutId = setTimeout(() => {
              // Double check ref hasn't changed (prevent race condition)
              if (!hasSyncedColumnWidthsRef.current) {
                syncTableWidths(); // Sync container widths and column widths
                hasSyncedColumnWidthsRef.current = true;

                // CRITICAL: Double-check and re-apply after a short delay to ensure widths stick
                // This prevents browser from changing widths during subsequent reflows
                doubleCheckTimeoutId = setTimeout(() => {
                  const valueTable = valueTableRef.current;
                  const netTable = netTableRef.current;
                  if (valueTable && netTable && columnWidthsRef.current.length > 0) {
                    // Ensure fixed layout is still set
                    valueTable.style.tableLayout = 'fixed';
                    netTable.style.tableLayout = 'fixed';
                    // CRITICAL: Prevent table from forcibly expanding
                    valueTable.style.width = 'max-content';
                    netTable.style.width = 'max-content';

                    // Re-apply widths to ensure they stick
                    const valueHeaderRows = valueTable.querySelectorAll('thead tr');
                    const netHeaderRows = netTable.querySelectorAll('thead tr');
                    if (valueHeaderRows.length >= 2 && netHeaderRows.length >= 2) {
                      const valueColumnHeaderRow = valueHeaderRows[1];
                      const netColumnHeaderRow = netHeaderRows[1];
                      if (valueColumnHeaderRow && netColumnHeaderRow) {
                        const valueHeaderCells = Array.from(valueColumnHeaderRow.querySelectorAll('th'));
                        const netHeaderCells = Array.from(netColumnHeaderRow.querySelectorAll('th'));
                        const columnWidths = columnWidthsRef.current;

                        valueHeaderCells.forEach((cell, index) => {
                          const el = cell as HTMLElement;
                          const width = columnWidths[index];

                          if (width && width > 0) {
                            el.style.width = `${width}px`;
                            el.style.minWidth = `${width}px`;
                            el.style.maxWidth = `${width}px`;
                            el.style.boxSizing = 'border-box';
                          }
                        });

                        netHeaderCells.forEach((cell, index) => {
                          const el = cell as HTMLElement;
                          const width = columnWidths[index];

                          if (width && width > 0) {
                            el.style.width = `${width}px`;
                            el.style.minWidth = `${width}px`;
                            el.style.maxWidth = `${width}px`;
                            el.style.boxSizing = 'border-box';
                          }
                        });
                      }
                    }
                  }
                }, 100); // Small delay to catch any reflows
              }
            }, 200); // Increased timeout to ensure all styles (including borders) are fully applied
          });
        });
      });
      }; // end startSyncChain

      // Ukur HANYA setelah webfont siap: mengukur saat fallback font aktif bikin
      // kolom terkunci kelebaran (gejala: lebar saat pertama buka, bener setelah Show).
      // Fallback 1.2 detik supaya tidak nunggu font selamanya.
      if (typeof document !== 'undefined' && document.fonts && document.fonts.ready) {
        document.fonts.ready.then(startSyncChain).catch(startSyncChain);
        fontsTimeoutId = setTimeout(startSyncChain, 1200);
      } else {
        startSyncChain();
      }

      return () => {
        if (fontsTimeoutId !== null) clearTimeout(fontsTimeoutId);
        if (rafId1 !== null) cancelAnimationFrame(rafId1);
        if (rafId2 !== null) cancelAnimationFrame(rafId2);
        if (rafId3 !== null) cancelAnimationFrame(rafId3);
        if (timeoutId !== null) clearTimeout(timeoutId);
        if (doubleCheckTimeoutId !== null) clearTimeout(doubleCheckTimeoutId);
      };
    }

    // No cleanup needed if condition not met
    return undefined;
  }, [transactionData, isLoading, isDataReady]); // FIXED: Removed visibleRowCount - only sync when data changes, not when lazy loading adds more rows

  // CRITICAL: Maintain column widths after lazy loading adds new rows
  // This ensures widths don't change when visibleRowCount increases
  useEffect(() => {
    // Only maintain widths if sync has been done and we have data
    if (!hasSyncedColumnWidthsRef.current || !isDataReady || isLoading) return;

    const valueTable = valueTableRef.current;
    const netTable = netTableRef.current;

    if (!valueTable || !netTable) return;

    // Ensure fixed layout is maintained
    if (valueTable.style.tableLayout !== 'fixed') {
      valueTable.style.tableLayout = 'fixed';
    }
    if (netTable.style.tableLayout !== 'fixed') {
      netTable.style.tableLayout = 'fixed';
    }
    // CRITICAL: Ensure width is constrained
    if (valueTable.style.width !== 'max-content') {
      valueTable.style.width = 'max-content';
    }
    if (netTable.style.width !== 'max-content') {
      netTable.style.width = 'max-content';
    }

    // Re-apply widths to new rows that were added by lazy loading
    // Get stored column widths from existing cells
    const valueHeaderRows = valueTable.querySelectorAll('thead tr');
    const netHeaderRows = netTable.querySelectorAll('thead tr');

    if (valueHeaderRows.length >= 2 && netHeaderRows.length >= 2) {
      const valueColumnHeaderRow = valueHeaderRows[1];
      const netColumnHeaderRow = netHeaderRows[1];

      if (valueColumnHeaderRow && netColumnHeaderRow) {
        const valueHeaderCells = Array.from(valueColumnHeaderRow.querySelectorAll('th'));

        // Get widths from stored ref first, fallback to reading from DOM
        let columnWidths: number[] = [];
        if (columnWidthsRef.current.length > 0) {
          // Use stored widths from initial sync
          columnWidths = [...columnWidthsRef.current];
        } else {
          // Fallback: read from header cells
          valueHeaderCells.forEach((cell) => {
            const el = cell as HTMLElement;
            const width = parseFloat(el.style.width) || el.offsetWidth;
            columnWidths.push(width);
          });
        }

        // Apply widths to all body cells (including newly added ones from lazy loading)
        // Skip sel yang sudah punya inline width (batch sebelumnya) — hemat layout
        const valueBodyRows = valueTable.querySelectorAll('tbody tr');
        const netBodyRows = netTable.querySelectorAll('tbody tr');

        valueBodyRows.forEach((row) => {
          const cells = row.querySelectorAll('td');
          cells.forEach((cell, index) => {
            const cellEl = cell as HTMLElement;
            if (cellEl.style.width) return;
            const width = columnWidths[index];
            if (width && width > 0) {
              cellEl.style.width = `${width}px`;
              cellEl.style.minWidth = `${width}px`;
              cellEl.style.maxWidth = `${width}px`;
              cellEl.style.boxSizing = 'border-box';
            }
          });
        });

        netBodyRows.forEach((row) => {
          const cells = row.querySelectorAll('td');
          cells.forEach((cell, index) => {
            const cellEl = cell as HTMLElement;
            if (cellEl.style.width) return;
            const width = columnWidths[index];
            if (width && width > 0) {
              cellEl.style.width = `${width}px`;
              cellEl.style.minWidth = `${width}px`;
              cellEl.style.maxWidth = `${width}px`;
              cellEl.style.boxSizing = 'border-box';
            }
          });
        });
      }
    }
  }, [visibleRowCount, isDataReady, isLoading]); // Re-apply widths when lazy loading adds rows


  // Measure and store date column widths from VALUE table to apply to Total table
  useEffect(() => {
    if (isLoading || !isDataReady) return;

    const showOnlyTotal = selectedDates.length > 30;
    if (showOnlyTotal) return;

    const valueTable = valueTableRef.current;
    if (!valueTable) return;

    // Calculate colsPerDate dynamically based on toggles
    const baseCols = 9;
    const optionalCols = (showFrequency ? 4 : 0) + (showOrder ? 4 : 0) + (showNoSplit ? 4 : 0);
    const colsPerDate = baseCols + optionalCols;

    // Wait for table to render
    const measureWidths = () => {
      const valueHeaderRows = valueTable.querySelectorAll('thead tr');
      if (valueHeaderRows.length < 2) return;

      // Get the second row (column headers) for accurate width measurement
      const columnHeaderRow = valueHeaderRows[1];
      if (!columnHeaderRow) return;

      const columnHeaderCells = Array.from(columnHeaderRow.querySelectorAll('th'));

      // Clear previous widths
      dateColumnWidthsRef.current.clear();
      totalColumnWidthRef.current = 0;

      // Measure width of each date column (excluding Total column)
      // Each date column spans colsPerDate columns (dynamic based on toggles)
      let cellIndex = 0;

      selectedDates.forEach((date) => {
        // Each date column spans colsPerDate columns, so we need to sum the width of colsPerDate cells
        let totalWidth = 0;
        for (let i = 0; i < colsPerDate && cellIndex < columnHeaderCells.length; i++) {
          const cell = columnHeaderCells[cellIndex] as HTMLElement;
          if (cell) {
            // Use offsetWidth for more accurate measurement
            const cellWidth = cell.offsetWidth || cell.getBoundingClientRect().width || 0;
            totalWidth += cellWidth;
          }
          cellIndex++;
        }

        if (totalWidth > 0) {
          dateColumnWidthsRef.current.set(date, totalWidth);
        }
      });

      // Measure width of Total column (also spans colsPerDate columns)
      let totalColumnWidth = 0;
      for (let i = 0; i < colsPerDate && cellIndex < columnHeaderCells.length; i++) {
        const cell = columnHeaderCells[cellIndex] as HTMLElement;
        if (cell) {
          const cellWidth = cell.offsetWidth || cell.getBoundingClientRect().width || 0;
          totalColumnWidth += cellWidth;
        }
        cellIndex++;
      }

      if (totalColumnWidth > 0) {
        totalColumnWidthRef.current = totalColumnWidth;
      }
    };

    // Measure after a delay to ensure table is fully rendered and column widths are synced
    // Wait longer to ensure syncTableWidths has finished (which runs after 100ms)
    const timeoutId = setTimeout(measureWidths, 300);

    return () => clearTimeout(timeoutId);
  }, [transactionData, isLoading, isDataReady, selectedDates, showFrequency, showOrder, showNoSplit]);

  // ROBUST CONSOLIDATED HORIZONTAL SCROLL SYNC
  useEffect(() => {
    if (isLoading || !isDataReady) return;

    const vContainer = valueTableContainerRef.current;
    const nContainer = netTableContainerRef.current;
    const tContainer = totalTableContainerRef.current;

    if (!vContainer || !nContainer) return;

    // Sync coalesced per frame: event scroll yang cepat digabung jadi satu,
    // echo programmatic jadi no-op (target sudah sama → skip tulis) sehingga
    // tidak ada ping-pong/balap antar tabel.
    let pending: { source: HTMLDivElement; targets: (HTMLDivElement | null)[] } | null = null;
    let rafId = 0;

    const handleScroll = (source: HTMLDivElement, targets: (HTMLDivElement | null)[]) => {
      pending = { source, targets };
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        const p = pending;
        pending = null;
        if (!p) return;
        const x = p.source.scrollLeft;
        p.targets.forEach(target => {
          if (target && target.scrollLeft !== x) {
            target.scrollLeft = x;
          }
        });
      });
    };

    const onVScroll = () => handleScroll(vContainer, [nContainer, tContainer]);
    const onNScroll = () => handleScroll(nContainer, [vContainer, tContainer]);
    // Only attach listener to Total container if it exists
    const onTScroll = () => handleScroll(tContainer!, [vContainer, nContainer]);

    vContainer.addEventListener('scroll', onVScroll, { passive: true });
    nContainer.addEventListener('scroll', onNScroll, { passive: true });
    if (tContainer) {
      tContainer.addEventListener('scroll', onTScroll, { passive: true });
    }

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = 0;
      pending = null;
      vContainer.removeEventListener('scroll', onVScroll);
      nContainer.removeEventListener('scroll', onNScroll);
      if (tContainer) {
        tContainer.removeEventListener('scroll', onTScroll);
      }
    };
  }, [isDataReady, isLoading, selectedDates.length, pivotFilter, showFrequency, showOrder, showNoSplit]);

  // Overlay AI per tanggal mengubah lebar tabel tanpa memicu event scroll —
  // paksa satu pass sync tiap overlay dibuka/ditutup agar VALUE & NET tetap sejajar.
  useEffect(() => {
    const vContainer = valueTableContainerRef.current;
    const nContainer = netTableContainerRef.current;
    if (!vContainer || !nContainer) return;
    if (nContainer.scrollLeft !== vContainer.scrollLeft) {
      nContainer.scrollLeft = vContainer.scrollLeft;
    }
  }, [aiDateOverlay]);

  // Sinkron lebar PER KOLOM Value & NET (bukan per tabel) — garansi kedua
  // tabel identik persis sehingga scroll horizontal selalu sejajar.
  // Dijalankan setelah colgroup (w-4/w-6 ala BrokerSummary) dipasang.
  useEffect(() => {
    if (isLoading || !isDataReady) return;

    const syncColumnWidths = () => {
      const valueTable = valueTableRef.current;
      const netTable = netTableRef.current;

      if (!valueTable || !netTable) return;

      const vHeads = Array.from(valueTable.querySelectorAll('thead tr:last-child th')) as HTMLElement[];
      const nHeads = Array.from(netTable.querySelectorAll('thead tr:last-child th')) as HTMLElement[];
      if (vHeads.length === 0 || vHeads.length !== nHeads.length) return;

      // Reset dulu supaya terukur alami, paksa reflow, lalu samakan per kolom
      [...vHeads, ...nHeads].forEach(c => { c.style.width = ''; });
      void valueTable.offsetWidth;

      for (let i = 0; i < vHeads.length; i++) {
        const w = Math.max(vHeads[i]!.offsetWidth, nHeads[i]!.offsetWidth);
        if (w > 0) {
          vHeads[i]!.style.width = `${w}px`;
          nHeads[i]!.style.width = `${w}px`;
        }
      }
    };

    // Run sync after a short delay to allow rendering
    const timeoutId = setTimeout(syncColumnWidths, 100);
    return () => clearTimeout(timeoutId);
  }, [isDataReady, isLoading, selectedDates.length, displayedRangesMeta.length, showFrequency, showOrder, showNoSplit, pivotFilter]);

  // Memoize expensive calculations
  const { uniqueStocks, filteredStocks, sortedStocksByDate, sortedNetStocksByDate, totalDataByStock, totalNetDataByStock, sortedTotalStocks, sortedTotalNetStocks, buyStocksByDate, sellStocksByDate, netBuyStocksByDate, netSellStocksByDate, totalBuyDataByStock, totalSellDataByStock, totalNetBuyDataByStock, totalNetSellDataByStock } = useMemo<{
    uniqueStocks: string[];
    filteredStocks: string[];
    sortedStocksByDate: Map<string, string[]>;
    sortedNetStocksByDate: Map<string, string[]>;
    totalDataByStock: Map<string, {
      buyerVol: number;
      buyerValue: number;
      buyerAvg: number;
      buyerFreq: number;
      buyerOrdNum: number;
      sellerVol: number;
      sellerValue: number;
      sellerAvg: number;
      sellerFreq: number;
      sellerOrdNum: number;
      buyerAvgCount: number;
      sellerAvgCount: number;
    }>;
    totalNetDataByStock: Map<string, {
      netBuyVol: number;
      netBuyValue: number;
      netBuyAvg: number;
      netBuyFreq: number;
      netBuyOrdNum: number;
      netSellVol: number;
      netSellValue: number;
      netSellAvg: number;
      netSellFreq: number;
      netSellOrdNum: number;
      netBuyAvgCount: number;
      netSellAvgCount: number;
    }>;
    sortedTotalStocks: string[];
    sortedTotalNetStocks: string[];
    buyStocksByDate: Map<string, Array<{ stock: string; data: BrokerTransactionData }>>;
    sellStocksByDate: Map<string, Array<{ stock: string; data: BrokerTransactionData }>>;
    netBuyStocksByDate: Map<string, Array<{ stock: string; data: BrokerTransactionData }>>;
    netSellStocksByDate: Map<string, Array<{ stock: string; data: BrokerTransactionData }>>;
    totalBuyDataByStock: Map<string, { buyerVol: number; buyerValue: number; buyerAvg: number; buyerFreq: number; buyerOrdNum: number; buyerNoSplit: number; buyerLot: number; buyerLotPerFreq: number; buyerLotPerOrdNum: number; buyerLotPerNoSplit: number; buyerAvgCount: number; }>;
    totalSellDataByStock: Map<string, { sellerVol: number; sellerValue: number; sellerAvg: number; sellerFreq: number; sellerOrdNum: number; sellerNoSplit: number; sellerLot: number; sellerLotPerFreq: number; sellerLotPerOrdNum: number; sellerLotPerNoSplit: number; sellerAvgCount: number; }>;
    totalNetBuyDataByStock: Map<string, { netBuyVol: number; netBuyValue: number; netBuyAvg: number; netBuyFreq: number; netBuyOrdNum: number; netBuyNoSplit: number; netBuyLot: number; netBuyLotPerFreq: number; netBuyLotPerOrdNum: number; netBuyLotPerNoSplit: number; netBuyAvgCount: number; netBuyLotPerFreqSum: number; netBuyLotPerFreqWeight: number; netBuyLotPerOrdNumSum: number; netBuyLotPerOrdNumWeight: number; netBuyLotPerNoSplitSum: number; netBuyLotPerNoSplitWeight: number; }>;
    totalNetSellDataByStock: Map<string, { netSellVol: number; netSellValue: number; netSellAvg: number; netSellFreq: number; netSellOrdNum: number; netSellNoSplit: number; netSellLot: number; netSellLotPerFreq: number; netSellLotPerOrdNum: number; netSellLotPerNoSplit: number; netSellAvgCount: number; netSellLotPerFreqSum: number; netSellLotPerFreqWeight: number; netSellLotPerOrdNumSum: number; netSellLotPerOrdNumWeight: number; netSellLotPerNoSplitSum: number; netSellLotPerNoSplitWeight: number; }>;
  }>(() => {
    // FIXED: Only check transactionData - don't depend on selectedBrokers/selectedDates to prevent re-calculation on input change
    if (transactionData.size === 0) {
      return {
        uniqueStocks: [],
        filteredStocks: [],
        sortedStocksByDate: new Map(),
        sortedNetStocksByDate: new Map(),
        totalDataByStock: new Map(),
        totalNetDataByStock: new Map(),
        sortedTotalStocks: [],
        sortedTotalNetStocks: [],
        buyStocksByDate: new Map(),
        sellStocksByDate: new Map(),
        netBuyStocksByDate: new Map(),
        netSellStocksByDate: new Map(),
        totalBuyDataByStock: new Map(),
        totalSellDataByStock: new Map(),
        totalNetBuyDataByStock: new Map(),
        totalNetSellDataByStock: new Map()
      };
    }

    // Treat 4 sections independently: Buy (BCode), Sell (SCode), Net Buy (NBCode), Net Sell (NSCode)
    // Each section filters and sorts separately
    // FIXED: Don't filter by selectedTickers here - filtering will be done at render time to prevent re-calculation on ticker change
    // FIXED: Use all dates from transactionData, not selectedDates (to prevent re-calculation on input change)
    const datesToProcess = Array.from(transactionData.keys());

    // CRITICAL: Don't filter by sector here - filter will be applied at render time
    // This prevents re-computation when sector filter changes
    // SECTION 1: Buy (BCode) - Filter and sort independently
    // Each row represents a separate entry in Value Buy section using BCode (column 1)
    const buyStocksByDate = new Map<string, Array<{ stock: string; data: BrokerTransactionData }>>();
    datesToProcess.forEach(date => {
      const dateData = transactionData.get(date) || [];
      const buyData = dateData
        .filter(d => {
          // Filter out rows where BCode is empty
          const bCode = d.BCode || '';
          if (!bCode) return false;

          // FIXED: Don't filter by selectedTickers or sector here - will be filtered at render time

          return true;
        })
        .filter(d => {
          // Filter out rows where all Buy section values are 0
          // Use BLot directly from CSV (column 1), not calculated from BuyerVol
          const buyerLot = d.BLot !== undefined ? d.BLot : ((d.BuyerVol || 0) / 100);
          const buyerVal = d.BuyerValue || 0;
          const buyerFreq = d.BFreq || 0;
          const buyerOrdNum = d.BOrdNum || 0;
          return Math.abs(buyerLot) > 0 || Math.abs(buyerVal) > 0 || Math.abs(buyerFreq) > 0 || Math.abs(buyerOrdNum) > 0;
        })
        .sort((a, b) => ((b.BLot !== undefined ? b.BLot : ((b.BuyerVol || 0) / 100))) - ((a.BLot !== undefined ? a.BLot : ((a.BuyerVol || 0) / 100))))
        .map(d => ({ stock: d.BCode || '', data: d })); // Use BCode as stock identifier for this section
      buyStocksByDate.set(date, buyData);
    });

    // SECTION 2: Sell (SCode) - Filter and sort independently
    // Each row represents a separate entry in Value Sell section using SCode (column 9)
    const sellStocksByDate = new Map<string, Array<{ stock: string; data: BrokerTransactionData }>>();
    datesToProcess.forEach(date => {
      const dateData = transactionData.get(date) || [];
      const sellData = dateData
        .filter(d => {
          // Filter out rows where SCode is empty
          const sCode = d.SCode || '';
          if (!sCode) return false;

          // FIXED: Don't filter by selectedTickers or sector here - will be filtered at render time

          return true;
        })
        .filter(d => {
          // Filter out rows where all Sell section values are 0
          // Use SLot directly from CSV (column 9), not calculated from SellerVol
          const sellerLot = d.SLot !== undefined ? d.SLot : ((d.SellerVol || 0) / 100);
          const sellerVal = d.SellerValue || 0;
          const sellerFreq = d.SFreq || 0;
          const sellerOrdNum = d.SOrdNum || 0;
          return Math.abs(sellerLot) > 0 || Math.abs(sellerVal) > 0 || Math.abs(sellerFreq) > 0 || Math.abs(sellerOrdNum) > 0;
        })
        .sort((a, b) => ((b.SLot !== undefined ? b.SLot : ((b.SellerVol || 0) / 100))) - ((a.SLot !== undefined ? a.SLot : ((a.SellerVol || 0) / 100))))
        .map(d => ({ stock: d.SCode || '', data: d })); // Use SCode as stock identifier for this section
      sellStocksByDate.set(date, sellData);
    });

    // SECTION 3: Net Buy (NBCode) - Filter and sort independently
    // CRITICAL: For Output: stock with multiple brokers, calculate net per date from Buy - Sell per stock (Emiten)
    // Each row represents a separate entry in Net Buy section using NBCode (column 17)
    const netBuyStocksByDate = new Map<string, Array<{ stock: string; data: BrokerTransactionData }>>();

    // SECTION 4: Net Sell (NSCode) - Filter and sort independently
    // CRITICAL: For Output: stock with multiple brokers, calculate net per date from Buy - Sell per stock (Emiten)
    // Each row represents a separate entry in Net Sell section using NSCode (column 25)
    const netSellStocksByDate = new Map<string, Array<{ stock: string; data: BrokerTransactionData }>>();

    datesToProcess.forEach(date => {
      const dateData = transactionData.get(date) || [];

      // For consolidated ALL/{SECTOR}_ALL rows, NetBuyVol/NetBuyValue and
      // NetSellVol/NetSellValue are BOTH nonzero (and Buy==Sell, so the Buy-Sell
      // recompute below always yields 0). Keep a lookup so those rows can use
      // their own NB*/NS* values directly instead of being recomputed.
      const rowByEmiten = new Map<string, BrokerTransactionData>();
      dateData.forEach(d => {
        if (d.Emiten) rowByEmiten.set(d.Emiten, d);
      });

      // CRITICAL: For Output: stock, calculate net per date from Buy - Sell per stock (Emiten)
      // Aggregate Buy and Sell per Emiten (stock) for this date
      const buyByStock = new Map<string, { buyerLot: number; buyerValue: number; buyerFreq: number; buyerOrdNum: number; buyerLotPerFreq: number; buyerLotPerOrdNum: number; buyerLotPerFreqSum: number; buyerLotPerFreqWeight: number; buyerLotPerOrdNumSum: number; buyerLotPerOrdNumWeight: number; buyerNoSplit: number; buyerLotPerNoSplitSum: number; buyerLotPerNoSplitWeight: number }>();
      const sellByStock = new Map<string, { sellerLot: number; sellerValue: number; sellerFreq: number; sellerOrdNum: number; sellerLotPerFreq: number; sellerLotPerOrdNum: number; sellerLotPerFreqSum: number; sellerLotPerFreqWeight: number; sellerLotPerOrdNumSum: number; sellerLotPerOrdNumWeight: number; sellerNoSplit: number; sellerLotPerNoSplitSum: number; sellerLotPerNoSplitWeight: number }>();

      dateData.forEach(d => {
        const emiten = d.Emiten || '';
        if (!emiten) return;

        // Aggregate Buy data per Emiten (stock)
        const buyerLot = d.BLot !== undefined ? d.BLot : ((d.BuyerVol || 0) / 100);
        const buyerValue = d.BuyerValue || 0;
        const buyerFreq = d.BFreq || 0;
        const buyerOrdNum = d.BOrdNum || 0;
        const buyerLotPerFreq = d.BLotPerFreq;
        const buyerLotPerOrdNum = d.BLotPerOrdNum;
        const newBuyerOrdNum = d.NewBuyerOrdNum !== undefined ? d.NewBuyerOrdNum : buyerOrdNum;

        if (!buyByStock.has(emiten)) {
          buyByStock.set(emiten, { buyerLot: 0, buyerValue: 0, buyerFreq: 0, buyerOrdNum: 0, buyerLotPerFreq: 0, buyerLotPerOrdNum: 0, buyerLotPerFreqSum: 0, buyerLotPerFreqWeight: 0, buyerLotPerOrdNumSum: 0, buyerLotPerOrdNumWeight: 0, buyerNoSplit: 0, buyerLotPerNoSplitSum: 0, buyerLotPerNoSplitWeight: 0 });
        }
        const buyData = buyByStock.get(emiten)!;
        buyData.buyerLot += buyerLot;
        buyData.buyerValue += buyerValue;
        buyData.buyerFreq += buyerFreq;
        buyData.buyerOrdNum += buyerOrdNum;
        buyData.buyerNoSplit += d.BNoSplit || 0;

        // Weighted average for Lot/F (same as B/S)
        if (buyerFreq > 0 && buyerLotPerFreq !== undefined && buyerLotPerFreq !== null) {
          buyData.buyerLotPerFreqSum += buyerLotPerFreq * buyerFreq;
          buyData.buyerLotPerFreqWeight += buyerFreq;
        }
        // Weighted average for Lot/Or (same as B/S)
        if (newBuyerOrdNum !== 0 && buyerLotPerOrdNum !== undefined && buyerLotPerOrdNum !== null) {
          buyData.buyerLotPerOrdNumSum += buyerLotPerOrdNum * Math.abs(newBuyerOrdNum);
          buyData.buyerLotPerOrdNumWeight += Math.abs(newBuyerOrdNum);
        }
        // Weighted average for Lot/Non (same as B/S)
        const buyerNoSplit = d.BNoSplit || 0;
        const buyerLotPerNoSplit = d.BLotPerNoSplit;
        if (buyerNoSplit > 0 && buyerLotPerNoSplit !== undefined && buyerLotPerNoSplit !== null) {
          buyData.buyerLotPerNoSplitSum += buyerLotPerNoSplit * buyerNoSplit;
          buyData.buyerLotPerNoSplitWeight += buyerNoSplit;
        }

        // Aggregate Sell data per Emiten (stock)
        const sellerLot = d.SLot !== undefined ? d.SLot : ((d.SellerVol || 0) / 100);
        const sellerValue = d.SellerValue || 0;
        const sellerFreq = d.SFreq || 0;
        const sellerOrdNum = d.SOrdNum || 0;
        const sellerLotPerFreq = d.SLotPerFreq;
        const sellerLotPerOrdNum = d.SLotPerOrdNum;
        const newSellerOrdNum = d.NewSellerOrdNum !== undefined ? d.NewSellerOrdNum : sellerOrdNum;

        if (!sellByStock.has(emiten)) {
          sellByStock.set(emiten, { sellerLot: 0, sellerValue: 0, sellerFreq: 0, sellerOrdNum: 0, sellerLotPerFreq: 0, sellerLotPerOrdNum: 0, sellerLotPerFreqSum: 0, sellerLotPerFreqWeight: 0, sellerLotPerOrdNumSum: 0, sellerLotPerOrdNumWeight: 0, sellerNoSplit: 0, sellerLotPerNoSplitSum: 0, sellerLotPerNoSplitWeight: 0 });
        }
        const sellData = sellByStock.get(emiten)!;
        sellData.sellerLot += sellerLot;
        sellData.sellerValue += sellerValue;
        sellData.sellerFreq += sellerFreq;
        sellData.sellerOrdNum += sellerOrdNum;
        sellData.sellerNoSplit += d.SNoSplit || 0;

        // Weighted average for Lot/F (same as B/S)
        if (sellerFreq > 0 && sellerLotPerFreq !== undefined && sellerLotPerFreq !== null) {
          sellData.sellerLotPerFreqSum += sellerLotPerFreq * sellerFreq;
          sellData.sellerLotPerFreqWeight += sellerFreq;
        }
        // Weighted average for Lot/Or (same as B/S)
        if (newSellerOrdNum !== 0 && sellerLotPerOrdNum !== undefined && sellerLotPerOrdNum !== null) {
          sellData.sellerLotPerOrdNumSum += sellerLotPerOrdNum * Math.abs(newSellerOrdNum);
          sellData.sellerLotPerOrdNumWeight += Math.abs(newSellerOrdNum);
        }
        // Weighted average for Lot/Non (same as B/S)
        const sellerNoSplit = d.SNoSplit || 0;
        const sellerLotPerNoSplit = d.SLotPerNoSplit;
        if (sellerNoSplit > 0 && sellerLotPerNoSplit !== undefined && sellerLotPerNoSplit !== null) {
          sellData.sellerLotPerNoSplitSum += sellerLotPerNoSplit * sellerNoSplit;
          sellData.sellerLotPerNoSplitWeight += sellerNoSplit;
        }
      });

      // Calculate Net Buy and Net Sell per stock from Buy - Sell
      const netBuyDataArray: Array<{ stock: string; data: BrokerTransactionData }> = [];
      const netSellDataArray: Array<{ stock: string; data: BrokerTransactionData }> = [];

      const allStocks = new Set([...buyByStock.keys(), ...sellByStock.keys()]);

      allStocks.forEach(stock => {
        // Consolidated ALL/{SECTOR}_ALL row: NetBuyVol/Value and NetSellVol/Value
        // are both already meaningful (and Buy==Sell so Buy-Sell would give 0).
        // Use the row's own NB*/NS* values directly for both sections.
        const consolidatedRow = rowByEmiten.get(stock);
        if (consolidatedRow && (consolidatedRow.NetBuyVol || 0) > 0 && (consolidatedRow.NetSellVol || 0) > 0) {
          netBuyDataArray.push({
            stock,
            data: {
              ...consolidatedRow,
              NBCode: stock,
              BuyerVol: 0, BuyerValue: 0, SellerVol: 0, SellerValue: 0,
              NetSellVol: 0, NetSellValue: 0,
            },
          });
          netSellDataArray.push({
            stock,
            data: {
              ...consolidatedRow,
              NSCode: stock,
              BuyerVol: 0, BuyerValue: 0, SellerVol: 0, SellerValue: 0,
              NetBuyVol: 0, NetBuyValue: 0,
            },
          });
          return;
        }

        const buyData = buyByStock.get(stock) || { buyerLot: 0, buyerValue: 0, buyerFreq: 0, buyerOrdNum: 0, buyerLotPerFreq: 0, buyerLotPerOrdNum: 0, buyerLotPerFreqSum: 0, buyerLotPerFreqWeight: 0, buyerLotPerOrdNumSum: 0, buyerLotPerOrdNumWeight: 0, buyerNoSplit: 0, buyerLotPerNoSplitSum: 0, buyerLotPerNoSplitWeight: 0 };
        const sellData = sellByStock.get(stock) || { sellerLot: 0, sellerValue: 0, sellerFreq: 0, sellerOrdNum: 0, sellerLotPerFreq: 0, sellerLotPerOrdNum: 0, sellerLotPerFreqSum: 0, sellerLotPerFreqWeight: 0, sellerLotPerOrdNumSum: 0, sellerLotPerOrdNumWeight: 0, sellerNoSplit: 0, sellerLotPerNoSplitSum: 0, sellerLotPerNoSplitWeight: 0 };

        // Calculate Net Buy = Buy - Sell (only if positive)
        const netBuyLot = buyData.buyerLot - sellData.sellerLot;
        const netBuyValue = buyData.buyerValue - sellData.sellerValue;
        const netBuyFreq = buyData.buyerFreq - sellData.sellerFreq;
        const netBuyOrdNum = buyData.buyerOrdNum - sellData.sellerOrdNum;

        // Calculate Lot/F and Lot/Or using weighted average from Buy/Sell (same as B/S)
        // For Net Buy: use Buy weighted average (since Net Buy = Buy - Sell, use Buy's Lot/F and Lot/Or)
        const netBuyLotPerFreq = buyData.buyerLotPerFreqWeight > 0 ? buyData.buyerLotPerFreqSum / buyData.buyerLotPerFreqWeight : 0;
        const netBuyLotPerOrdNum = buyData.buyerLotPerOrdNumWeight > 0 ? buyData.buyerLotPerOrdNumSum / buyData.buyerLotPerOrdNumWeight : 0;
        // NoSplit net = signed buy-sell difference (mirrors CSV NoSplit_NetBuy)
        const netBuyNoSplit = (buyData.buyerNoSplit || 0) - (sellData.sellerNoSplit || 0);
        // Use Buy weighted average (same as Lot/F and Lot/Or)
        const netBuyLotPerNoSplit = buyData.buyerLotPerNoSplitWeight > 0 ? buyData.buyerLotPerNoSplitSum / buyData.buyerLotPerNoSplitWeight : 0;

        if (netBuyValue > 0 || (netBuyValue === 0 && netBuyLot > 0)) {
          const netBuyLotVolume = netBuyLot * 100;
          // FIXED: Allow negative volume for Average calculation (don't force > 0)
          const netBuyAvg = netBuyLotVolume !== 0 ? netBuyValue / netBuyLotVolume : 0;

          // Create Net Buy data object
          const netBuyData: BrokerTransactionData = {
            Emiten: stock,
            NBCode: stock,
            NBLot: netBuyLot,
            NBVal: netBuyValue,
            NBAvg: netBuyAvg,
            NBFreq: netBuyFreq,
            NBOrdNum: netBuyOrdNum,
            NBLotPerFreq: netBuyLotPerFreq,
            NBLotPerOrdNum: netBuyLotPerOrdNum,
            NBNoSplit: netBuyNoSplit,
            NBLotPerNoSplit: netBuyLotPerNoSplit,
            NetBuyVol: netBuyLotVolume,
            NetBuyValue: netBuyValue,
            BuyerVol: 0,
            BuyerValue: 0,
            SellerVol: 0,
            SellerValue: 0,
            NetSellVol: 0,
            NetSellValue: 0,
            BuyerAvg: 0,
            SellerAvg: 0,
            TotalVolume: 0,
            AvgPrice: 0,
            TransactionCount: 0,
            TotalValue: 0
          };

          netBuyDataArray.push({ stock, data: netBuyData });
        }

        // Calculate Net Sell = Sell - Buy (only if positive)
        const netSellLot = sellData.sellerLot - buyData.buyerLot;
        const netSellValue = sellData.sellerValue - buyData.buyerValue;
        const netSellFreq = sellData.sellerFreq - buyData.buyerFreq;
        const netSellOrdNum = sellData.sellerOrdNum - buyData.buyerOrdNum;

        // Calculate Lot/F and Lot/Or using weighted average from Buy/Sell (same as B/S)
        // For Net Sell: use Sell weighted average (since Net Sell = Sell - Buy, use Sell's Lot/F and Lot/Or)
        const netSellLotPerFreq = sellData.sellerLotPerFreqWeight > 0 ? sellData.sellerLotPerFreqSum / sellData.sellerLotPerFreqWeight : 0;
        const netSellLotPerOrdNum = sellData.sellerLotPerOrdNumWeight > 0 ? sellData.sellerLotPerOrdNumSum / sellData.sellerLotPerOrdNumWeight : 0;
        // NoSplit net = signed sell-buy difference (mirrors CSV NoSplit_NetSell)
        const netSellNoSplit = (sellData.sellerNoSplit || 0) - (buyData.buyerNoSplit || 0);
        // Use Sell weighted average (same as Lot/F and Lot/Or)
        const netSellLotPerNoSplit = sellData.sellerLotPerNoSplitWeight > 0 ? sellData.sellerLotPerNoSplitSum / sellData.sellerLotPerNoSplitWeight : 0;

        if (netSellValue > 0 || (netSellValue === 0 && netSellLot > 0)) {
          const netSellLotVolume = netSellLot * 100;
          // FIXED: Allow negative volume for Average calculation (don't force > 0)
          const netSellAvg = netSellLotVolume !== 0 ? netSellValue / netSellLotVolume : 0;

          // Create Net Sell data object
          const netSellData: BrokerTransactionData = {
            Emiten: stock,
            NSCode: stock,
            NSLot: netSellLot,
            NSVal: netSellValue,
            NSAvg: netSellAvg,
            NSFreq: netSellFreq,
            NSOrdNum: netSellOrdNum,
            NSLotPerFreq: netSellLotPerFreq,
            NSLotPerOrdNum: netSellLotPerOrdNum,
            NSNoSplit: netSellNoSplit,
            NSLotPerNoSplit: netSellLotPerNoSplit,
            NetSellVol: netSellLotVolume,
            NetSellValue: netSellValue,
            BuyerVol: 0,
            BuyerValue: 0,
            SellerVol: 0,
            SellerValue: 0,
            NetBuyVol: 0,
            NetBuyValue: 0,
            BuyerAvg: 0,
            SellerAvg: 0,
            TotalVolume: 0,
            AvgPrice: 0,
            TransactionCount: 0,
            TotalValue: 0
          };

          netSellDataArray.push({ stock, data: netSellData });
        }
      });

      // Sort by value (highest to lowest)
      netBuyDataArray.sort((a, b) => (Number(b.data.NBLot) || 0) - (Number(a.data.NBLot) || 0));
      netSellDataArray.sort((a, b) => (Number(b.data.NSLot) || 0) - (Number(a.data.NSLot) || 0));

      netBuyStocksByDate.set(date, netBuyDataArray);
      netSellStocksByDate.set(date, netSellDataArray);
    });

    // Get all unique stocks for each section separately (for Total column)
    const allBuyStocks = new Set<string>();
    const allSellStocks = new Set<string>();
    const allNetBuyStocks = new Set<string>();
    const allNetSellStocks = new Set<string>();

    datesToProcess.forEach(date => {
      const dateData = transactionData.get(date) || [];
      dateData.forEach(item => {
        if (item.BCode) allBuyStocks.add(item.BCode);
        if (item.SCode) allSellStocks.add(item.SCode);
        if (item.NBCode) allNetBuyStocks.add(item.NBCode);
        if (item.NSCode) allNetSellStocks.add(item.NSCode);
      });
    });

    // For backward compatibility
    const uniqueStocks = Array.from(new Set([...allBuyStocks, ...allSellStocks, ...allNetBuyStocks, ...allNetSellStocks]));
    const filteredStocks = uniqueStocks;

    // For backward compatibility, create sortedStocksByDate and sortedNetStocksByDate
    const sortedStocksByDate = new Map<string, string[]>();
    datesToProcess.forEach(date => {
      const buyData = buyStocksByDate.get(date) || [];
      sortedStocksByDate.set(date, buyData.map(d => d.stock));
    });

    const sortedNetStocksByDate = new Map<string, string[]>();
    datesToProcess.forEach(date => {
      const sellData = sellStocksByDate.get(date) || [];
      sortedNetStocksByDate.set(date, sellData.map(d => d.stock));
    });

    // Calculate total data aggregated across all dates for each stock - for VALUE table
    // Aggregate per section separately: Buy section by BCode, Sell section by SCode
    const totalBuyDataByStock = new Map<string, {
      buyerVol: number;
      buyerValue: number;
      buyerAvg: number;
      buyerFreq: number;
      buyerOrdNum: number;
      buyerNoSplit: number;
      buyerLot: number;
      buyerLotPerFreq: number;
      buyerLotPerOrdNum: number;
      buyerLotPerNoSplit: number;
      buyerAvgCount: number;
      buyerLotPerFreqSum: number; // For weighted average calculation
      buyerLotPerFreqWeight: number; // For weighted average calculation
      buyerLotPerOrdNumSum: number; // For weighted average calculation
      buyerLotPerOrdNumWeight: number; // For weighted average calculation
      buyerLotPerNoSplitSum: number; // For weighted average calculation
      buyerLotPerNoSplitWeight: number; // For weighted average calculation
    }>();

    const totalSellDataByStock = new Map<string, {
      sellerVol: number;
      sellerValue: number;
      sellerAvg: number;
      sellerFreq: number;
      sellerOrdNum: number;
      sellerNoSplit: number;
      sellerLot: number;
      sellerLotPerFreq: number;
      sellerLotPerOrdNum: number;
      sellerLotPerNoSplit: number;
      sellerAvgCount: number;
      sellerLotPerFreqSum: number; // For weighted average calculation
      sellerLotPerFreqWeight: number; // For weighted average calculation
      sellerLotPerOrdNumSum: number; // For weighted average calculation
      sellerLotPerOrdNumWeight: number; // For weighted average calculation
      sellerLotPerNoSplitSum: number; // For weighted average calculation
      sellerLotPerNoSplitWeight: number; // For weighted average calculation
    }>();

    datesToProcess.forEach(date => {
      const dateData = transactionData.get(date) || [];
      dateData.forEach(dayData => {
        // Aggregate Buy section by BCode
        const buyStock = dayData.BCode || '';
        if (buyStock) {
          if (!totalBuyDataByStock.has(buyStock)) {
            totalBuyDataByStock.set(buyStock, {
              buyerVol: 0,
              buyerValue: 0,
              buyerAvg: 0,
              buyerFreq: 0,
              buyerOrdNum: 0,
              buyerNoSplit: 0,
              buyerLot: 0,
              buyerLotPerFreq: 0,
              buyerLotPerOrdNum: 0,
              buyerLotPerNoSplit: 0,
              buyerAvgCount: 0,
              buyerLotPerFreqSum: 0,
              buyerLotPerFreqWeight: 0,
              buyerLotPerOrdNumSum: 0,
              buyerLotPerOrdNumWeight: 0,
              buyerLotPerNoSplitSum: 0,
              buyerLotPerNoSplitWeight: 0,
            });
          }
          const total = totalBuyDataByStock.get(buyStock)!;
          const dayFreq = Number(dayData.BFreq) || Number(dayData.TransactionCount) || 0;
          // Use NewBuyerOrdNum for aggregation, fallback to BOrdNum if not available
          const dayOrdNum = Number(dayData.NewBuyerOrdNum !== undefined ? dayData.NewBuyerOrdNum : dayData.BOrdNum) || 0;
          const dayNoSplit = Number(dayData.BNoSplit) || 0;
          const dayLot = Number(dayData.BLot) || 0;
          // Use Lot/F and Lot/ON from CSV only - no manual calculation
          const dayLotPerFreq = dayData.BLotPerFreq;
          const dayLotPerOrdNum = dayData.BLotPerOrdNum;
          const dayLotPerNoSplit = dayData.BLotPerNoSplit;

          total.buyerVol += Number(dayData.BuyerVol) || 0;
          total.buyerValue += Number(dayData.BuyerValue) || 0;
          total.buyerFreq += dayFreq;
          total.buyerOrdNum += dayOrdNum; // This now uses NewBuyerOrdNum
          total.buyerNoSplit += dayNoSplit;
          total.buyerLot += dayLot;

          // Weighted average for Lot/F and Lot/ON
          if (dayFreq > 0 && dayLotPerFreq !== undefined && dayLotPerFreq !== null) {
            total.buyerLotPerFreqSum += dayLotPerFreq * dayFreq;
            total.buyerLotPerFreqWeight += dayFreq;
          }
          if (dayOrdNum !== 0 && dayLotPerOrdNum !== undefined && dayLotPerOrdNum !== null) {
            total.buyerLotPerOrdNumSum += dayLotPerOrdNum * Math.abs(dayOrdNum);
            total.buyerLotPerOrdNumWeight += Math.abs(dayOrdNum);
          }
          if (dayNoSplit > 0 && dayLotPerNoSplit !== undefined && dayLotPerNoSplit !== null) {
            total.buyerLotPerNoSplitSum += dayLotPerNoSplit * dayNoSplit;
            total.buyerLotPerNoSplitWeight += dayNoSplit;
          }


        }

        // Aggregate Sell section by SCode
        const sellStock = dayData.SCode || '';
        if (sellStock) {
          if (!totalSellDataByStock.has(sellStock)) {
            totalSellDataByStock.set(sellStock, {
              sellerVol: 0,
              sellerValue: 0,
              sellerAvg: 0,
              sellerFreq: 0,
              sellerOrdNum: 0,
              sellerNoSplit: 0,
              sellerLot: 0,
              sellerLotPerFreq: 0,
              sellerLotPerOrdNum: 0,
              sellerLotPerNoSplit: 0,
              sellerAvgCount: 0,
              sellerLotPerFreqSum: 0,
              sellerLotPerFreqWeight: 0,
              sellerLotPerOrdNumSum: 0,
              sellerLotPerOrdNumWeight: 0,
              sellerLotPerNoSplitSum: 0,
              sellerLotPerNoSplitWeight: 0,
            });
          }
          const total = totalSellDataByStock.get(sellStock)!;
          const dayFreq = Number(dayData.SFreq) || Number(dayData.TransactionCount) || 0;
          // Use NewSellerOrdNum for aggregation, fallback to SOrdNum if not available
          const dayOrdNum = Number(dayData.NewSellerOrdNum !== undefined ? dayData.NewSellerOrdNum : dayData.SOrdNum) || 0;
          const dayNoSplit = Number(dayData.SNoSplit) || 0;
          const dayLot = Number(dayData.SLot) || 0;
          // Use Lot/F and Lot/ON from CSV only - no manual calculation
          const dayLotPerFreq = dayData.SLotPerFreq;
          const dayLotPerOrdNum = dayData.SLotPerOrdNum;
          const dayLotPerNoSplit = dayData.SLotPerNoSplit;

          total.sellerVol += Number(dayData.SellerVol) || 0;
          total.sellerValue += Number(dayData.SellerValue) || 0;
          total.sellerFreq += dayFreq;
          total.sellerOrdNum += dayOrdNum; // This now uses NewSellerOrdNum
          total.sellerNoSplit += dayNoSplit;
          total.sellerLot += dayLot;

          // Weighted average for Lot/F and Lot/ON
          if (dayFreq > 0 && dayLotPerFreq !== undefined && dayLotPerFreq !== null) {
            total.sellerLotPerFreqSum += dayLotPerFreq * dayFreq;
            total.sellerLotPerFreqWeight += dayFreq;
          }
          if (dayOrdNum !== 0 && dayLotPerOrdNum !== undefined && dayLotPerOrdNum !== null) {
            total.sellerLotPerOrdNumSum += dayLotPerOrdNum * Math.abs(dayOrdNum);
            total.sellerLotPerOrdNumWeight += Math.abs(dayOrdNum);
          }
          if (dayNoSplit > 0 && dayLotPerNoSplit !== undefined && dayLotPerNoSplit !== null) {
            total.sellerLotPerNoSplitSum += dayLotPerNoSplit * dayNoSplit;
            total.sellerLotPerNoSplitWeight += dayNoSplit;
          }


        }
      });
    });

    // Calculate final averages and Lot/F, Lot/ON
    totalBuyDataByStock.forEach((total) => {
      // FIXED: Total Avg = Total Value / (Total Lot * 100)
      total.buyerAvg = total.buyerLot > 0 ? total.buyerValue / (total.buyerLot * 100) : 0;
      total.buyerLotPerFreq = total.buyerLotPerFreqWeight > 0 ? total.buyerLotPerFreqSum / total.buyerLotPerFreqWeight : 0;
      total.buyerLotPerOrdNum = total.buyerLotPerOrdNumWeight > 0 ? total.buyerLotPerOrdNumSum / total.buyerLotPerOrdNumWeight : 0;
      total.buyerLotPerNoSplit = total.buyerLotPerNoSplitWeight > 0 ? total.buyerLotPerNoSplitSum / total.buyerLotPerNoSplitWeight : 0;
    });
    totalSellDataByStock.forEach((total) => {
      // FIXED: Total Avg = Total Value / (Total Lot * 100)
      total.sellerAvg = total.sellerLot > 0 ? total.sellerValue / (total.sellerLot * 100) : 0;
      total.sellerLotPerFreq = total.sellerLotPerFreqWeight > 0 ? total.sellerLotPerFreqSum / total.sellerLotPerFreqWeight : 0;
      total.sellerLotPerOrdNum = total.sellerLotPerOrdNumWeight > 0 ? total.sellerLotPerOrdNumSum / total.sellerLotPerOrdNumWeight : 0;
      total.sellerLotPerNoSplit = total.sellerLotPerNoSplitWeight > 0 ? total.sellerLotPerNoSplitSum / total.sellerLotPerNoSplitWeight : 0;
    });

    // Sort stocks by total buyer value (highest to lowest) for Total column
    const sortedTotalBuyStocks = Array.from(totalBuyDataByStock.entries())
      .sort((a, b) => b[1].buyerLot - a[1].buyerLot)
      .map(([stock]) => stock);

    // For backward compatibility, create combined totalDataByStock
    const totalDataByStock = new Map<string, {
      buyerVol: number;
      buyerValue: number;
      buyerAvg: number;
      buyerFreq: number;
      buyerOrdNum: number;
      buyerLot: number;
      buyerLotPerFreq: number;
      buyerLotPerOrdNum: number;
      sellerVol: number;
      sellerValue: number;
      sellerAvg: number;
      sellerFreq: number;
      sellerOrdNum: number;
      sellerLot: number;
      sellerLotPerFreq: number;
      sellerLotPerOrdNum: number;
      buyerAvgCount: number;
      sellerAvgCount: number;
    }>();

    // Combine Buy and Sell data (for backward compatibility with rendering code)
    Array.from(totalBuyDataByStock.entries()).forEach(([stock, buyData]) => {
      totalDataByStock.set(stock, {
        buyerVol: buyData.buyerVol,
        buyerValue: buyData.buyerValue,
        buyerAvg: buyData.buyerAvg,
        buyerFreq: buyData.buyerFreq,
        buyerOrdNum: buyData.buyerOrdNum,
        buyerLot: buyData.buyerLot,
        buyerLotPerFreq: buyData.buyerLotPerFreq,
        buyerLotPerOrdNum: buyData.buyerLotPerOrdNum,
        sellerVol: 0,
        sellerValue: 0,
        sellerAvg: 0,
        sellerFreq: 0,
        sellerOrdNum: 0,
        sellerLot: 0,
        sellerLotPerFreq: 0,
        sellerLotPerOrdNum: 0,
        buyerAvgCount: buyData.buyerAvgCount,
        sellerAvgCount: 0,
      });
    });

    Array.from(totalSellDataByStock.entries()).forEach(([stock, sellData]) => {
      const existing = totalDataByStock.get(stock);
      if (existing) {
        existing.sellerVol = sellData.sellerVol;
        existing.sellerValue = sellData.sellerValue;
        existing.sellerAvg = sellData.sellerAvg;
        existing.sellerFreq = sellData.sellerFreq;
        existing.sellerOrdNum = sellData.sellerOrdNum;
        existing.sellerLot = sellData.sellerLot;
        existing.sellerLotPerFreq = sellData.sellerLotPerFreq;
        existing.sellerLotPerOrdNum = sellData.sellerLotPerOrdNum;
        existing.sellerAvgCount = sellData.sellerAvgCount;
      } else {
        totalDataByStock.set(stock, {
          buyerVol: 0,
          buyerValue: 0,
          buyerAvg: 0,
          buyerFreq: 0,
          buyerOrdNum: 0,
          buyerLot: 0,
          buyerLotPerFreq: 0,
          buyerLotPerOrdNum: 0,
          sellerVol: sellData.sellerVol,
          sellerValue: sellData.sellerValue,
          sellerAvg: sellData.sellerAvg,
          sellerFreq: sellData.sellerFreq,
          sellerOrdNum: sellData.sellerOrdNum,
          sellerLot: sellData.sellerLot,
          sellerLotPerFreq: sellData.sellerLotPerFreq,
          sellerLotPerOrdNum: sellData.sellerLotPerOrdNum,
          buyerAvgCount: 0,
          sellerAvgCount: sellData.sellerAvgCount,
        });
      }
    });

    const sortedTotalStocks = sortedTotalBuyStocks; // Use Buy stocks for backward compatibility

    // Calculate total Net data aggregated across all dates for each stock - for NET table
    // Aggregate per section separately: Net Buy section by NBCode, Net Sell section by NSCode
    const totalNetBuyDataByStock = new Map<string, {
      netBuyVol: number;
      netBuyValue: number;
      netBuyAvg: number;
      netBuyFreq: number;
      netBuyOrdNum: number;
      netBuyNoSplit: number;
      netBuyLot: number;
      netBuyLotPerFreq: number;
      netBuyLotPerOrdNum: number;
      netBuyLotPerNoSplit: number;
      netBuyAvgCount: number;
      netBuyLotPerFreqSum: number; // For weighted average calculation
      netBuyLotPerFreqWeight: number; // For weighted average calculation
      netBuyLotPerOrdNumSum: number; // For weighted average calculation
      netBuyLotPerOrdNumWeight: number; // For weighted average calculation
      netBuyLotPerNoSplitSum: number; // For weighted average calculation
      netBuyLotPerNoSplitWeight: number; // For weighted average calculation
    }>();

    const totalNetSellDataByStock = new Map<string, {
      netSellVol: number;
      netSellValue: number;
      netSellAvg: number;
      netSellFreq: number;
      netSellOrdNum: number;
      netSellNoSplit: number;
      netSellLot: number;
      netSellLotPerFreq: number;
      netSellLotPerOrdNum: number;
      netSellLotPerNoSplit: number;
      netSellAvgCount: number;
      netSellLotPerFreqSum: number; // For weighted average calculation
      netSellLotPerFreqWeight: number; // For weighted average calculation
      netSellLotPerOrdNumSum: number; // For weighted average calculation
      netSellLotPerOrdNumWeight: number; // For weighted average calculation
      netSellLotPerNoSplitSum: number; // For weighted average calculation
      netSellLotPerNoSplitWeight: number; // For weighted average calculation
    }>();

    datesToProcess.forEach(date => {
      const dateData = transactionData.get(date) || [];
      dateData.forEach(dayData => {
        // Aggregate Net Buy section by NBCode
        const netBuyStock = dayData.NBCode || '';
        if (netBuyStock) {
          if (!totalNetBuyDataByStock.has(netBuyStock)) {
            totalNetBuyDataByStock.set(netBuyStock, {
              netBuyVol: 0,
              netBuyValue: 0,
              netBuyAvg: 0,
              netBuyFreq: 0,
              netBuyOrdNum: 0,
              netBuyNoSplit: 0,
              netBuyLot: 0,
              netBuyLotPerFreq: 0,
              netBuyLotPerOrdNum: 0,
              netBuyLotPerNoSplit: 0,
              netBuyAvgCount: 0,
              netBuyLotPerFreqSum: 0,
              netBuyLotPerFreqWeight: 0,
              netBuyLotPerOrdNumSum: 0,
              netBuyLotPerOrdNumWeight: 0,
              netBuyLotPerNoSplitSum: 0,
              netBuyLotPerNoSplitWeight: 0,
            });
          }
          const total = totalNetBuyDataByStock.get(netBuyStock)!;
          const nbVol = dayData.NetBuyVol || 0;
          const nbVal = dayData.NBVal || dayData.NetBuyValue || 0;
          const nbFreq = dayData.NBFreq || 0;
          const nbOrdNum = dayData.NBOrdNum || 0;
          const nbNoSplit = Number(dayData.NBNoSplit) || 0;
          const nbLot = Number(dayData.NBLot) || 0;
          // Use Lot/F and Lot/ON from CSV only - no manual calculation
          const nbLotPerFreq = dayData.NBLotPerFreq;
          const nbLotPerOrdNum = dayData.NBLotPerOrdNum;
          const nbLotPerNoSplit = dayData.NBLotPerNoSplit;

          total.netBuyVol += nbVol;
          total.netBuyValue += nbVal;
          total.netBuyFreq += nbFreq;
          total.netBuyOrdNum += nbOrdNum;
          total.netBuyNoSplit += nbNoSplit;
          total.netBuyLot += nbLot;

          // Weighted average for Lot/F and Lot/ON (using absolute values for Net)
          if (nbFreq !== 0 && nbLotPerFreq !== undefined && nbLotPerFreq !== null) {
            total.netBuyLotPerFreqSum += nbLotPerFreq * Math.abs(nbFreq);
            total.netBuyLotPerFreqWeight += Math.abs(nbFreq);
          }
          if (nbOrdNum !== 0 && nbLotPerOrdNum !== undefined && nbLotPerOrdNum !== null) {
            total.netBuyLotPerOrdNumSum += nbLotPerOrdNum * Math.abs(nbOrdNum);
            total.netBuyLotPerOrdNumWeight += Math.abs(nbOrdNum);
          }
          if (nbNoSplit !== 0 && nbLotPerNoSplit !== undefined && nbLotPerNoSplit !== null) {
            total.netBuyLotPerNoSplitSum += nbLotPerNoSplit * Math.abs(nbNoSplit);
            total.netBuyLotPerNoSplitWeight += Math.abs(nbNoSplit);
          }


        }

        // Aggregate Net Sell section by NSCode
        const netSellStock = dayData.NSCode || '';
        if (netSellStock) {
          if (!totalNetSellDataByStock.has(netSellStock)) {
            totalNetSellDataByStock.set(netSellStock, {
              netSellVol: 0,
              netSellValue: 0,
              netSellAvg: 0,
              netSellFreq: 0,
              netSellOrdNum: 0,
              netSellNoSplit: 0,
              netSellLot: 0,
              netSellLotPerFreq: 0,
              netSellLotPerOrdNum: 0,
              netSellLotPerNoSplit: 0,
              netSellAvgCount: 0,
              netSellLotPerFreqSum: 0,
              netSellLotPerFreqWeight: 0,
              netSellLotPerOrdNumSum: 0,
              netSellLotPerOrdNumWeight: 0,
              netSellLotPerNoSplitSum: 0,
              netSellLotPerNoSplitWeight: 0,
            });
          }
          const total = totalNetSellDataByStock.get(netSellStock)!;
          const nsVol = dayData.NetSellVol || 0;
          const nsVal = dayData.NSVal || dayData.NetSellValue || 0;
          const nsFreq = dayData.NSFreq || 0;
          const nsOrdNum = dayData.NSOrdNum || 0;
          const nsNoSplit = Number(dayData.NSNoSplit) || 0;
          const nsLot = Number(dayData.NSLot) || 0;
          // Use Lot/F and Lot/ON from CSV only - no manual calculation
          const nsLotPerFreq = dayData.NSLotPerFreq;
          const nsLotPerOrdNum = dayData.NSLotPerOrdNum;
          const nsLotPerNoSplit = dayData.NSLotPerNoSplit;

          total.netSellVol += nsVol;
          total.netSellValue += nsVal;
          total.netSellFreq += nsFreq;
          total.netSellOrdNum += nsOrdNum;
          total.netSellNoSplit += nsNoSplit;
          total.netSellLot += nsLot;

          // Weighted average for Lot/F and Lot/ON (using absolute values for Net)
          if (nsFreq !== 0 && nsLotPerFreq !== undefined && nsLotPerFreq !== null) {
            total.netSellLotPerFreqSum += nsLotPerFreq * Math.abs(nsFreq);
            total.netSellLotPerFreqWeight += Math.abs(nsFreq);
          }
          if (nsOrdNum !== 0 && nsLotPerOrdNum !== undefined && nsLotPerOrdNum !== null) {
            total.netSellLotPerOrdNumSum += nsLotPerOrdNum * Math.abs(nsOrdNum);
            total.netSellLotPerOrdNumWeight += Math.abs(nsOrdNum);
          }
          if (nsNoSplit !== 0 && nsLotPerNoSplit !== undefined && nsLotPerNoSplit !== null) {
            total.netSellLotPerNoSplitSum += nsLotPerNoSplit * Math.abs(nsNoSplit);
            total.netSellLotPerNoSplitWeight += Math.abs(nsNoSplit);
          }


        }
      });
    });

    // Calculate final averages and Lot/F, Lot/ON
    totalNetBuyDataByStock.forEach((total) => {
      // FIXED: Total Avg = Total Value / (Total Lot * 100)
      const netBuyLotVolume = total.netBuyLot * 100;
      total.netBuyAvg = Math.abs(netBuyLotVolume) > 0 ? total.netBuyValue / netBuyLotVolume : 0;
      total.netBuyLotPerFreq = total.netBuyLotPerFreqWeight > 0 ? total.netBuyLotPerFreqSum / total.netBuyLotPerFreqWeight : 0;
      total.netBuyLotPerOrdNum = total.netBuyLotPerOrdNumWeight > 0 ? total.netBuyLotPerOrdNumSum / total.netBuyLotPerOrdNumWeight : 0;
      total.netBuyLotPerNoSplit = total.netBuyLotPerNoSplitWeight > 0 ? total.netBuyLotPerNoSplitSum / total.netBuyLotPerNoSplitWeight : 0;
    });
    totalNetSellDataByStock.forEach((total) => {
      // FIXED: Total Avg = Total Value / (Total Lot * 100)
      const netSellLotVolume = total.netSellLot * 100;
      total.netSellAvg = Math.abs(netSellLotVolume) > 0 ? total.netSellValue / netSellLotVolume : 0;
      total.netSellLotPerFreq = total.netSellLotPerFreqWeight > 0 ? total.netSellLotPerFreqSum / total.netSellLotPerFreqWeight : 0;
      total.netSellLotPerOrdNum = total.netSellLotPerOrdNumWeight > 0 ? total.netSellLotPerOrdNumSum / total.netSellLotPerOrdNumWeight : 0;
      total.netSellLotPerNoSplit = total.netSellLotPerNoSplitWeight > 0 ? total.netSellLotPerNoSplitSum / total.netSellLotPerNoSplitWeight : 0;
    });

    // Sort stocks by total net sell value (highest to lowest) for Total column
    const sortedTotalNetSellStocks = Array.from(totalNetSellDataByStock.entries())
      .sort((a, b) => b[1].netSellLot - a[1].netSellLot)
      .map(([stock]) => stock);

    // For backward compatibility, create combined totalNetDataByStock
    const totalNetDataByStock = new Map<string, {
      netBuyVol: number;
      netBuyValue: number;
      netBuyAvg: number;
      netBuyFreq: number;
      netBuyOrdNum: number;
      netBuyLot: number;
      netBuyLotPerFreq: number;
      netBuyLotPerOrdNum: number;
      netSellVol: number;
      netSellValue: number;
      netSellAvg: number;
      netSellFreq: number;
      netSellOrdNum: number;
      netSellLot: number;
      netSellLotPerFreq: number;
      netSellLotPerOrdNum: number;
      netBuyAvgCount: number;
      netSellAvgCount: number;
    }>();

    // Combine Net Buy and Net Sell data (for backward compatibility with rendering code)
    Array.from(totalNetBuyDataByStock.entries()).forEach(([stock, netBuyData]) => {
      totalNetDataByStock.set(stock, {
        netBuyVol: netBuyData.netBuyVol,
        netBuyValue: netBuyData.netBuyValue,
        netBuyAvg: netBuyData.netBuyAvg,
        netBuyFreq: netBuyData.netBuyFreq,
        netBuyOrdNum: netBuyData.netBuyOrdNum,
        netBuyLot: netBuyData.netBuyLot,
        netBuyLotPerFreq: netBuyData.netBuyLotPerFreq,
        netBuyLotPerOrdNum: netBuyData.netBuyLotPerOrdNum,
        netSellVol: 0,
        netSellValue: 0,
        netSellAvg: 0,
        netSellFreq: 0,
        netSellOrdNum: 0,
        netSellLot: 0,
        netSellLotPerFreq: 0,
        netSellLotPerOrdNum: 0,
        netBuyAvgCount: netBuyData.netBuyAvgCount,
        netSellAvgCount: 0,
      });
    });

    Array.from(totalNetSellDataByStock.entries()).forEach(([stock, netSellData]) => {
      const existing = totalNetDataByStock.get(stock);
      if (existing) {
        existing.netSellVol = netSellData.netSellVol;
        existing.netSellValue = netSellData.netSellValue;
        existing.netSellAvg = netSellData.netSellAvg;
        existing.netSellFreq = netSellData.netSellFreq;
        existing.netSellOrdNum = netSellData.netSellOrdNum;
        existing.netSellLot = netSellData.netSellLot;
        existing.netSellLotPerFreq = netSellData.netSellLotPerFreq;
        existing.netSellLotPerOrdNum = netSellData.netSellLotPerOrdNum;
        existing.netSellAvgCount = netSellData.netSellAvgCount;
      } else {
        totalNetDataByStock.set(stock, {
          netBuyVol: 0,
          netBuyValue: 0,
          netBuyAvg: 0,
          netBuyFreq: 0,
          netBuyOrdNum: 0,
          netBuyLot: 0,
          netBuyLotPerFreq: 0,
          netBuyLotPerOrdNum: 0,
          netSellVol: netSellData.netSellVol,
          netSellValue: netSellData.netSellValue,
          netSellAvg: netSellData.netSellAvg,
          netSellFreq: netSellData.netSellFreq,
          netSellOrdNum: netSellData.netSellOrdNum,
          netSellLot: netSellData.netSellLot,
          netSellLotPerFreq: netSellData.netSellLotPerFreq,
          netSellLotPerOrdNum: netSellData.netSellLotPerOrdNum,
          netBuyAvgCount: 0,
          netSellAvgCount: netSellData.netSellAvgCount,
        });
      }
    });

    const sortedTotalNetStocks = sortedTotalNetSellStocks; // Use Net Sell stocks for backward compatibility

    return {
      uniqueStocks,
      filteredStocks,
      sortedStocksByDate,
      sortedNetStocksByDate,
      totalDataByStock,
      totalNetDataByStock,
      sortedTotalStocks,
      sortedTotalNetStocks,
      // Section-specific data (4 sections independent)
      buyStocksByDate,
      sellStocksByDate,
      netBuyStocksByDate,
      netSellStocksByDate,
      totalBuyDataByStock,
      totalSellDataByStock,
      totalNetBuyDataByStock,
      totalNetSellDataByStock
    };
  }, [transactionData]); // CRITICAL: Only depend on transactionData - sector filtering will be applied at render time, not during computation

  // Memoize stock color cache for O(1) lookup (moved outside render for better performance)
  // Sector colors mapping (shared for stock colors and sector display)
  const sectorColors: { [sector: string]: string } = useMemo(() => ({
    'Financials': '#FFFFFF', // White
    'Energy': '#4169E1', // Royal Blue
    'Basic Materials': '#00BFFF', // Sky Blue
    'Consumer Cyclicals': '#800080', // Deep Purple
    'Consumer Non-Cyclicals': '#FFD700', // Gold
    'Healthcare': '#FF8C00', // Orange
    'Industrials': '#00FFFF', // Cyan
    'Infrastructures': '#8B4513', // Brown
    'Properties & Real Estate': '#708090', // Slate Gray
    'Technology': '#FF69B4', // Hot Pink
    'Transportation & Logistic': '#C3B091' // Khaki
  }), []);

  // Helper function to get sector color
  const getSectorColor = useCallback((sector: string): string => {
    if (sector === 'IDX') {
      // IDX Composite uses a special color (can be customized)
      return '#4169E1'; // Royal Blue (same as Energy for now)
    }
    return sectorColors[sector] || '#FFFFFF';
  }, [sectorColors]);

  const stockColorCache = useMemo(() => {
    const cache = new Map<string, { color: string; className: string }>();

    return {
      get: (stockCode: string, stockToSectorMap: { [stock: string]: string }): { color: string; className: string } => {
        if (!stockCode) return { color: '#FFFFFF', className: '' };
        const cacheKey = stockCode.toUpperCase();
        if (cache.has(cacheKey)) {
          return cache.get(cacheKey)!;
        }
        const stockSector = stockToSectorMap[cacheKey];
        if (!stockSector) {
          const result = { color: '#FFFFFF', className: '' };
          cache.set(cacheKey, result);
          return result;
        }
        const color = sectorColors[stockSector] || '#FFFFFF';
        const result = { color, className: '' };
        cache.set(cacheKey, result);
        return result;
      }
    };
  }, [sectorColors]);

  // Memoize sorted stocks for Total column to avoid recalculating on every render
  const sortedTotalBuyStocksMemo = useMemo(() => {
    return Array.from(totalBuyDataByStock.entries())
      .sort((a, b) => b[1].buyerLot - a[1].buyerLot)
      .map(([stock]) => stock);
  }, [totalBuyDataByStock]);

  const sortedTotalSellStocksMemo = useMemo(() => {
    return Array.from(totalSellDataByStock.entries())
      .sort((a, b) => b[1].sellerLot - a[1].sellerLot)
      .map(([stock]) => stock);
  }, [totalSellDataByStock]);

  const allNetDataForTotalMemo = useMemo(() => {
    // For consolidated ALL/{SECTOR}_ALL pseudo-broker rows, BuyerValue(stock) ==
    // SellerValue(stock) always (whole-market conservation), so Buy - Sell is
    // always 0. Use the row's own NetBuy*/NetSell* totals directly instead.
    const isAllBrokerMode = appliedPivotFilter === 'Broker' && appliedSelectedBrokers.includes('ALL');

    if (isAllBrokerMode) {
      const netBuy = Array.from(totalNetBuyDataByStock.entries())
        .filter(([, d]) => Math.abs(d.netBuyLot || 0) > 0 || Math.abs(d.netBuyValue || 0) > 0)
        .map(([stock, d]) => ({
          stock,
          netValue: d.netBuyValue,
          netLot: d.netBuyLot,
          netVol: d.netBuyVol,
          netAvg: d.netBuyAvg,
          netFreq: d.netBuyFreq,
          netOrdNum: d.netBuyOrdNum,
          buyLotPerFreq: d.netBuyLotPerFreq,
          buyLotPerOrdNum: d.netBuyLotPerOrdNum,
          sellLotPerFreq: 0,
          sellLotPerOrdNum: 0,
          buyValue: d.netBuyValue,
          sellValue: 0,
          netBuyNoSplit: d.netBuyNoSplit,
          netBuyLotPerNoSplit: d.netBuyLotPerNoSplit,
        }))
        .sort((a, b) => b.netLot - a.netLot);

      const netSell = Array.from(totalNetSellDataByStock.entries())
        .filter(([, d]) => Math.abs(d.netSellLot || 0) > 0 || Math.abs(d.netSellValue || 0) > 0)
        .map(([stock, d]) => ({
          stock,
          netValue: -d.netSellValue,
          netLot: -d.netSellLot,
          netVol: -d.netSellVol,
          netAvg: d.netSellAvg,
          netFreq: d.netSellFreq,
          netOrdNum: d.netSellOrdNum,
          buyLotPerFreq: 0,
          buyLotPerOrdNum: 0,
          sellLotPerFreq: d.netSellLotPerFreq,
          sellLotPerOrdNum: d.netSellLotPerOrdNum,
          buyValue: 0,
          sellValue: d.netSellValue,
          netSellNoSplit: d.netSellNoSplit,
          netSellLotPerNoSplit: d.netSellLotPerNoSplit,
        }))
        .sort((a, b) => Math.abs(b.netLot) - Math.abs(a.netLot));

      return { netBuy, netSell };
    }

    const allStocksSet = new Set<string>();
    totalBuyDataByStock.forEach((_, stock) => allStocksSet.add(stock));
    totalSellDataByStock.forEach((_, stock) => allStocksSet.add(stock));

    const data = Array.from(allStocksSet).map(stock => {
      const buyData = totalBuyDataByStock.get(stock);
      const sellData = totalSellDataByStock.get(stock);
      const buyValue = buyData?.buyerValue || 0;
      const buyLot = buyData?.buyerLot || 0;
      const buyVol = buyData?.buyerVol || 0;
      const buyFreq = buyData?.buyerFreq || 0;
      const buyOrdNum = buyData?.buyerOrdNum || 0;
      const buyLotPerFreq = buyData?.buyerLotPerFreq || 0;
      const buyLotPerOrdNum = buyData?.buyerLotPerOrdNum || 0;
      const sellValue = sellData?.sellerValue || 0;
      const sellLot = sellData?.sellerLot || 0;
      const sellVol = sellData?.sellerVol || 0;
      const sellFreq = sellData?.sellerFreq || 0;
      const sellOrdNum = sellData?.sellerOrdNum || 0;
      const sellLotPerFreq = sellData?.sellerLotPerFreq || 0;
      const sellLotPerOrdNum = sellData?.sellerLotPerOrdNum || 0;
      const netValue = buyValue - sellValue;
      const netLot = buyLot - sellLot;
      const netAvg = Math.abs(netLot * 100) !== 0 ? Math.abs(netValue) / Math.abs(netLot * 100) : 0;
      const netFreq = buyFreq - sellFreq;
      const netOrdNum = buyOrdNum - sellOrdNum;
      const buyNoSplit = buyData?.buyerNoSplit || 0;
      const sellNoSplit = sellData?.sellerNoSplit || 0;
      const netBuyNoSplit = buyNoSplit - sellNoSplit;
      const netSellNoSplit = sellNoSplit - buyNoSplit;
      const netBuyLotPerNoSplit = buyData?.buyerLotPerNoSplit || 0;
      const netSellLotPerNoSplit = sellData?.sellerLotPerNoSplit || 0;

      return {
        stock, netValue, netLot, netVol: buyVol - sellVol, netAvg, netFreq, netOrdNum,
        buyLotPerFreq, buyLotPerOrdNum, sellLotPerFreq, sellLotPerOrdNum, buyValue, sellValue,
        netBuyNoSplit, netBuyLotPerNoSplit, netSellNoSplit, netSellLotPerNoSplit
      };
    });

    const netBuy = data.filter(item => item.netValue > 0 && (Math.abs(item.netLot) > 0 || Math.abs(item.netValue) > 0))
      .sort((a, b) => b.netLot - a.netLot);
    const netSell = data.filter(item => item.netValue < 0 && (Math.abs(item.netLot) > 0 || Math.abs(item.netValue) > 0))
      .sort((a, b) => Math.abs(b.netLot) - Math.abs(a.netLot));

    return { netBuy, netSell };
  }, [totalBuyDataByStock, totalSellDataByStock, totalNetBuyDataByStock, totalNetSellDataByStock, appliedPivotFilter, appliedSelectedBrokers]);

  // Block filter: daftar item (saham/broker sesuai pivot) + peta warna.
  // Aturan sama dengan BrokerSummary: Default = auto top-5, Manual = pilihan user.
  const availableBlockItems = useMemo(() => {
    const set = new Set<string>();
    allNetDataForTotalMemo.netBuy.forEach(i => { if (i.stock) set.add(i.stock); });
    allNetDataForTotalMemo.netSell.forEach(i => { if (i.stock) set.add(i.stock); });
    sortedTotalBuyStocksMemo.forEach(s => { if (s) set.add(s); });
    sortedTotalSellStocksMemo.forEach(s => { if (s) set.add(s); });
    const arr = Array.from(set);
    arr.sort((a, b) => {
      const aShort = a.length <= 4 ? 0 : 1;
      const bShort = b.length <= 4 ? 0 : 1;
      if (aShort !== bShort) return aShort - bShort;
      return a.localeCompare(b);
    });
    return arr;
  }, [allNetDataForTotalMemo, sortedTotalBuyStocksMemo, sortedTotalSellStocksMemo]);

  const filteredBlockItems = useMemo(() => {
    const q = blockSearchInput.trim().toUpperCase();
    if (!q) return availableBlockItems;
    return availableBlockItems.filter(b => b.toUpperCase().includes(q));
  }, [availableBlockItems, blockSearchInput]);

  const blockStyleMaps = useMemo(() => {
    const netBuyBgMap = new Map<string, string>();
    const netSellUnderlineMap = new Map<string, string>();
    const bgColors = [
      'hsl(0, 70%, 50%)',    // Merah (Red)
      'hsl(50, 100%, 30%)',  // Kuning (Yellow)
      'hsl(120, 70%, 30%)',  // Hijau (Green)
      'hsl(210, 70%, 50%)',  // Biru (Blue)
      'hsl(25, 80%, 40%)'    // Coklat (Brown)
    ];
    if (blockFilter === 'Default') {
      allNetDataForTotalMemo.netBuy.slice(0, 5).forEach((b, i) => { netBuyBgMap.set(b.stock, bgColors[i]!); });
      allNetDataForTotalMemo.netSell.slice(0, 5).forEach((b, i) => { netSellUnderlineMap.set(b.stock, bgColors[i]!); });
    } else if (blockFilter === 'Manual' && selectedBlockItems.length > 0) {
      const manualColors = [bgColors[0]!, bgColors[1]!, bgColors[2]!];
      selectedBlockItems.forEach((code, i) => {
        if (allNetDataForTotalMemo.netBuy.some(b => b.stock === code)) netBuyBgMap.set(code, manualColors[i % manualColors.length]!);
        if (allNetDataForTotalMemo.netSell.some(b => b.stock === code)) netSellUnderlineMap.set(code, manualColors[i % manualColors.length]!);
      });
    }
    return { netBuyBgMap, netSellUnderlineMap };
  }, [blockFilter, selectedBlockItems, allNetDataForTotalMemo]);

  // Gaya Block plek BrokerSummary lama: bg solid untuk net buy, underline GRADIENT
  // tipis untuk net sell (bukan border tebal).
  const getTxBlockStyle = (code: string): React.CSSProperties | undefined => {
    if (!code) return undefined;
    const bg = blockStyleMaps.netBuyBgMap.get(code);
    if (bg) return { backgroundColor: bg, color: 'white' };
    const ul = blockStyleMaps.netSellUnderlineMap.get(code);
    if (ul) return {
      backgroundImage: `linear-gradient(${ul}, ${ul})`,
      backgroundSize: '100% 3px',
      backgroundPosition: 'bottom',
      backgroundRepeat: 'no-repeat'
    };
    return undefined;
  };

  // Range turunan untuk multi-range (display dimatikan sampai Show diklik)
  const allExtraRanges = extraRanges;
  const displayedAllRangesDates = useMemo(() => displayedRangesMeta.map(r => generateTradingDays(r.startDate, r.endDate)), [displayedRangesMeta]);
  const displayedSortedRangeLabels = useMemo(() => displayedRangesMeta.map(r => formatRangeLabel(r.startDate, r.endDate)), [displayedRangesMeta]);
  const isMultiRangeDisplayed = displayedRangesMeta.length > 1;

  // Agregat per-range (Value + Net) — hanya dihitung saat multi-range tampil.
  const rangeAggregates = useMemo(() => {
    if (!isMultiRangeDisplayed) return [];
    const isAllBroker = appliedPivotFilter === 'Broker' && appliedSelectedBrokers.includes('ALL');
    return displayedAllRangesDates.map(dates => {
      const t = aggregateTxRangeTotals(dates, transactionData);
      const buyList = Array.from(t.buyMap.entries()).sort((a, b) => b[1].buyerLot - a[1].buyerLot).map(([s]) => s);
      const sellList = Array.from(t.sellMap.entries()).sort((a, b) => b[1].sellerLot - a[1].sellerLot).map(([s]) => s);
      const nets = isAllBroker ? buildAllBrokerNetLists(t.netBuyMap, t.netSellMap) : buildNettedStockLists(t.buyMap, t.sellMap);
      return { ...t, buyList, sellList, netBuyList: nets.netBuy, netSellList: nets.netSell };
    });
  }, [isMultiRangeDisplayed, displayedAllRangesDates, transactionData, appliedPivotFilter, appliedSelectedBrokers]);

  // Urutan lebar kolom per grup (BY/SL ramping w-4, kolom value w-6) — tiru
  // BrokerSummary agar rapat. Dipakai <colgroup> di kedua tabel supaya
  // struktur kolom Value & NET identik persis.
  const txColClassSequence = useMemo(() => {
    const buySellVals = ['w-6', 'w-6', 'w-6', ...(showFrequency ? ['w-6', 'w-6'] : []), ...(showOrder ? ['w-6', 'w-6'] : []), ...(showNoSplit ? ['w-6', 'w-6'] : [])];
    return ['w-4', ...buySellVals, 'w-4', 'w-4', ...buySellVals];
  }, [showFrequency, showOrder, showNoSplit]);
  // Value (GROSS) sequence adds Non-Split columns; NET keeps base sequence unchanged.
  const txValueColClassSequence = useMemo(() => {
    const buySellVals = ['w-6', 'w-6', 'w-6', ...(showFrequency ? ['w-6', 'w-6'] : []), ...(showOrder ? ['w-6', 'w-6'] : []), ...(showNoSplit ? ['w-6', 'w-6'] : [])];
    return ['w-4', ...buySellVals, 'w-4', 'w-4', ...buySellVals];
  }, [showFrequency, showOrder, showNoSplit]);
  const renderTxColGroup = (groupCount: number, keyPrefix: string) => {
    const seq = keyPrefix === 'v' ? txValueColClassSequence : txColClassSequence;
    return (
      <colgroup>{Array.from({ length: groupCount }).flatMap((_, g) => seq.map((c, i) => <col key={`${keyPrefix}-${g}-${i}`} className={c} />))}</colgroup>
    );
  };

  // Fetch OHLC for AI Level 1 Smart Money calculation (sama kayak BrokerSummary lama)
  useEffect(() => {
    // Only fetch if popup is open, we have a selected ticker, and selected dates
    if (!showAIPopup || selectedTickers.length === 0 || selectedDates.length === 0) return;

    const fetchOhlc = async () => {
      try {
        const tickerRaw = selectedTickers[0];
        if (!tickerRaw) {
          setClosePriceForAI(null);
          return;
        }
        // Skip for index/sectors
        if (tickerRaw === 'IDX' || tickerRaw.startsWith('[SECTOR]')) {
          setClosePriceForAI(null);
          return;
        }

        const ticker = tickerRaw;

        // Find sector via stockToSectorMap (code -> sector)
        const sector = stockToSectorMap[ticker.toUpperCase()] || '';

        if (!sector) {
          console.warn(`[NewBrokerSummary AI] No sector found for ticker ${ticker}`);
          return;
        }

        const tokenRes = await api.getBrokerSummarySasToken();
        if (!tokenRes.success || !tokenRes.data) {
          console.warn(`[NewBrokerSummary AI] Failed to get SAS token`);
          return;
        }

        const { containerUrl, sasToken } = tokenRes.data;
        const blobPath = `ohlc/stocks/${sector}/${ticker}.parquet`;

        const { loadOhlcByPath } = await import('../../services/duckdbService');
        const rows = await loadOhlcByPath(blobPath, containerUrl, sasToken);

        if (!rows || rows.length === 0) return;

        // the dates in selectedDates are like "YYYY-MM-DD"
        const targetDate = selectedDates[selectedDates.length - 1];
        if (!targetDate) return;

        let match = rows.find((r: any) => {
          const rDate = r.Date || r.date || r.time || r.DateStr;
          if (rDate instanceof Date) {
            return rDate.toISOString().startsWith(targetDate);
          } else if (typeof rDate === 'string') {
            return rDate.startsWith(targetDate);
          }
          return false;
        });

        // fallback to most recent date <= targetDate
        if (!match) {
           const targetTime = new Date(targetDate).getTime();
           let bestMatch = rows[0];
           let bestTimeDiff = Infinity;
           for (const r of rows) {
             const rDate = (r as any).Date || (r as any).date || (r as any).time || (r as any).DateStr;
             let time = 0;
             if (rDate instanceof Date) time = rDate.getTime();
             else if (typeof rDate === 'string') time = new Date(rDate).getTime();

             if (time && time <= targetTime) {
               const diff = targetTime - time;
               if (diff >= 0 && diff < bestTimeDiff) {
                 bestTimeDiff = diff;
                 bestMatch = r;
               }
             }
           }
           match = bestTimeDiff !== Infinity ? bestMatch : rows[rows.length - 1];
        }

        if (match) {
           const closeVal = (match as any).Close !== undefined ? (match as any).Close : (match as any).close;
           setClosePriceForAI(Number(closeVal));
        }

      } catch (err) {
        console.error(`[NewBrokerSummary AI] Error fetching OHLC data for Smart Money`, err);
      }
    };

    fetchOhlc();
  }, [showAIPopup, selectedTickers, selectedDates, stockToSectorMap]);

  // AI Level 1 logic (sama kayak BrokerSummary lama; satuan Lot disesuaikan model Transaction)
  // Builder per-broker: VALUE pakai Top 10 value, NET pakai Top 10 net — urutan sama persis
  // dengan kolom Total masing-masing section sehingga baris sejajar 1:1
  const buildAiStatsForBrokers = (brokers: string[], src?: {
    buyMap: Map<string, { buyerLot: number; buyerValue: number }>;
    sellMap: Map<string, { sellerLot: number; sellerValue: number }>;
    netBuyList: Array<{ stock: string; netLot: number; netValue: number; netAvg: number }>;
    netSellList: Array<{ stock: string; netLot: number; netValue: number; netAvg: number }>;
  }) => {
    const buyMap = src?.buyMap ?? totalBuyDataByStock;
    const sellMap = src?.sellMap ?? totalSellDataByStock;
    const netBuyList = src?.netBuyList ?? allNetDataForTotalMemo.netBuy;
    const netSellList = src?.netSellList ?? allNetDataForTotalMemo.netSell;

    // Compute grand market totals (buyerLot sudah dalam Lot, tidak dibagi 100)
    let marketTVal = 0;
    let marketTLot = 0;
    buyMap.forEach(d => {
      marketTVal += d.buyerValue;
      marketTLot += d.buyerLot;
    });
    let marketSellTLot = 0;
    sellMap.forEach(d => {
      marketSellTLot += d.sellerLot;
    });

    const stats: any[] = [];

    brokers.forEach(broker => {
      const netData = netBuyList.find(b => b.stock === broker) || null;
      const grossBuyData = buyMap.get(broker);
      const grossSellData = sellMap.get(broker);

      if (!grossBuyData) return;
      // netLot di model Transaction sudah dalam Lot (di Summary dibagi 100 karena shares)
      const netBuyLot = netData ? netData.netLot : 0;
      const grossBuyLot = grossBuyData.buyerLot;

      // Accumulation: lot buy net / lot buy gross (%)
      const accumulation = grossBuyLot > 0 ? (netBuyLot / grossBuyLot) * 100 : 0;

      // Consistency: top 10 buy per day (%)
      let daysInTop10 = 0;
      selectedDates.forEach(date => {
        const dailyNetData = netBuyStocksByDate.get(date) || [];
        const top10Daily = dailyNetData.slice(0, 10).map(b => b.stock);
        if (top10Daily.includes(broker)) {
          daysInTop10++;
        }
      });
      const consistency = selectedDates.length > 0 ? (daysInTop10 / selectedDates.length) * 100 : 0;

      // Buy streak: consecutive Top-10 BUY days from the most recent date backwards
      let buyStreak = 0;
      for (let i = selectedDates.length - 1; i >= 0; i--) {
        const d = selectedDates[i]!;
        const daily = netBuyStocksByDate.get(d) || [];
        const top10 = daily.slice(0, 10).map(b => b.stock);
        if (top10.includes(broker)) buyStreak++;
        else break;
      }
      // Any-buy streak (lebih longgar: sekadar net buyer, tidak harus Top 10)
      let anyBuyStreak = 0;
      for (let i = selectedDates.length - 1; i >= 0; i--) {
        const d = selectedDates[i]!;
        const daily = netBuyStocksByDate.get(d) || [];
        const anyBuy = daily.map(b => b.stock);
        if (anyBuy.includes(broker)) anyBuyStreak++;
        else break;
      }

      // Domination buy/sell/total (basis Lot, sama kayak Dom lama):
      // Dom B = gross buy lot broker / TLot market, Dom S = gross sell lot broker / TLot sell market
      const grossSellLot = grossSellData ? grossSellData.sellerLot : 0;
      const domBuy = marketTLot > 0 ? (grossBuyLot / marketTLot) * 100 : 0;
      const domSell = marketSellTLot > 0 ? (grossSellLot / marketSellTLot) * 100 : 0;
      const domNet = domBuy - domSell;
      const domTotal = domBuy + domSell;

      // Big Money: (buy gross val + sell gross val) / (tval * 2) (%)
      const brokerGrossBuyVal = grossBuyData.buyerValue;
      const brokerGrossSellVal = grossSellData ? grossSellData.sellerValue : 0;
      const bigMoney = marketTVal > 0 ? ((brokerGrossBuyVal + brokerGrossSellVal) / (marketTVal * 2)) * 100 : 0;

      // Smart Money: (1 - (avg buy / close price)) * 100 (avg per lembar)
      const avgBuy = netData && Math.abs(netData.netLot) > 0
        ? Math.abs(netData.netValue) / Math.abs(netData.netLot * 100)
        : (netData?.netAvg || 0);

      const smartMoney = closePriceForAI && closePriceForAI > 0 && avgBuy > 0
        ? (1 - (avgBuy / closePriceForAI)) * 100
        : 0;

      // Floating (Rp): (close - avgBuy) × netBuyLot (shares) — netLot dikali 100 karena dalam Lot
      const floatingNominal = closePriceForAI && closePriceForAI > 0 && avgBuy > 0 && netData
        ? (closePriceForAI - avgBuy) * Math.abs(netData.netLot) * 100
        : 0;

      // Fallback net sell (REALIZED): kalau tidak ada posisi net buy, hitung dari net sell —
      // Float = (avgSell - close)/close, P/L = (avgSell - close) × netSellLot (shares)
      const netSellEntry = netSellList.find(b => b.stock === broker) || null;
      const netSellLot = Math.abs(netSellEntry?.netLot || 0);
      const netSellValue = Math.abs(netSellEntry?.netValue || 0);
      const avgSell = netSellLot > 0
        ? netSellValue / (netSellLot * 100)
        : Math.abs(netSellEntry?.netAvg || 0);
      const hasNetBuy = !!(closePriceForAI && closePriceForAI > 0 && avgBuy > 0);
      const isRealized = !hasNetBuy && !!(closePriceForAI && closePriceForAI > 0 && avgSell > 0 && netSellLot > 0);
      const realizedPct = isRealized ? ((avgSell - (closePriceForAI as number)) / (closePriceForAI as number)) * 100 : 0;
      const realizedNominal = isRealized ? ((avgSell - (closePriceForAI as number)) * netSellLot * 100) : 0;
      const floatText = hasNetBuy ? smartMoney.toFixed(1) + '%' : (isRealized ? realizedPct.toFixed(1) + '%' : '-');
      const floatNum = hasNetBuy ? smartMoney : realizedPct;
      const plText = hasNetBuy ? formatNumber(floatingNominal) : (isRealized ? formatNumber(realizedNominal) : '-');
      const plNum = hasNetBuy ? floatingNominal : realizedNominal;

      stats.push({
        broker,
        accumulation: Math.round(accumulation).toString() + '%',
        accumulationRaw: accumulation,
        consistency: Math.round(consistency).toString() + '%',
        consistencyRaw: consistency,
        daysInTop10,
        totalDays: selectedDates.length,
        domBuy: domBuy.toFixed(1) + '%',
        domSell: domSell.toFixed(1) + '%',
        domNet: domNet.toFixed(1) + '%',
        domTotal: domTotal.toFixed(1) + '%',
        bigMoney: bigMoney.toFixed(1) + '%',
        smartMoney: floatText,
        rawSmartMoney: floatNum,
        floating: plText,
        rawFloating: plNum,
        isRealized,
        buyStreak,
        anyBuyStreak,
        isLateStarter: buyStreak >= 2 && buyStreak < selectedDates.length
      });
    });

    return stats;
  };

  // Top Value: dari row 1 sampai broker VALUE terakhir yang terdokumentasi di Top 5 Net Buy
  // (urutan sama persis dengan kolom Total buy sehingga baris sejajar 1:1).
  // Contoh: broker net-buy terbawah (mis. SQ) ada di row 12 VALUE → AI VALUE sebanyak 12 row.
  // Hanya Top 5 Net Buy yang dipakai — Top 10 melebar terlalu jauh (sampai row 20+).
  const aiValueStats = useMemo(() => {
    if (sortedTotalBuyStocksMemo.length === 0) return [];
    const netBuyTop5 = new Set<string>();
    allNetDataForTotalMemo.netBuy.slice(0, 5).forEach(b => { if (b.stock) netBuyTop5.add(b.stock); });
    let lastIdx = -1;
    sortedTotalBuyStocksMemo.forEach((s, idx) => { if (netBuyTop5.has(s)) lastIdx = idx; });
    const count = lastIdx >= 0 ? lastIdx + 1 : 10;
    return buildAiStatsForBrokers(sortedTotalBuyStocksMemo.slice(0, count));
  }, [sortedTotalBuyStocksMemo, allNetDataForTotalMemo, totalBuyDataByStock, totalSellDataByStock, netBuyStocksByDate, selectedDates, closePriceForAI]);

  // Top 10 Net: urutan sama persis dengan kolom Total net buy
  const aiNetStats = useMemo(() => {
    if (!allNetDataForTotalMemo || allNetDataForTotalMemo.netBuy.length === 0) return [];
    return buildAiStatsForBrokers(allNetDataForTotalMemo.netBuy.slice(0, 10).map(b => b.stock));
  }, [allNetDataForTotalMemo, totalBuyDataByStock, totalSellDataByStock, netBuyStocksByDate, selectedDates, closePriceForAI]);

  // AI per tanggal: Top 10 net buy tanggal itu saja (gross & market dari tanggal itu;
  // Cons/streak tetap lintas tanggal, SM/Float pakai close terakhir seperti tabel AI Total)
  const aiDateStats = useMemo(() => {
    if (!aiDateOverlay) return [];
    // Cabang range multi-range: "range:{rIdx}" — Top 10 net buy range itu
    if (aiDateOverlay.startsWith('range:')) {
      const agg = rangeAggregates[Number(aiDateOverlay.slice(6))];
      if (!agg) return [];
      const topBrokers = (agg.netBuyList || []).slice(0, 10).map(n => n.stock).filter(s => !!s);
      const brokers = topBrokers.length > 0
        ? topBrokers
        : (agg.buyList || []).slice(0, 10);
      return buildAiStatsForBrokers(brokers, { buyMap: agg.buyMap, sellMap: agg.sellMap, netBuyList: agg.netBuyList, netSellList: agg.netSellList });
    }
    const d = aiDateOverlay;
    const buyRows = buyStocksByDate.get(d) || [];
    const sellRows = sellStocksByDate.get(d) || [];
    const netRows = netBuyStocksByDate.get(d) || [];
    const netSellRows = netSellStocksByDate.get(d) || [];
    if (buyRows.length === 0 && netRows.length === 0) return [];
    const buyMap = new Map<string, { buyerLot: number; buyerValue: number }>();
    buyRows.forEach(r => {
      const code = r.stock || '';
      if (!code) return;
      const cur = buyMap.get(code) || { buyerLot: 0, buyerValue: 0 };
      cur.buyerLot += Number(r.data?.BLot) || 0;
      cur.buyerValue += Number(r.data?.BuyerValue) || 0;
      buyMap.set(code, cur);
    });
    const sellMap = new Map<string, { sellerLot: number; sellerValue: number }>();
    sellRows.forEach(r => {
      const code = r.stock || '';
      if (!code) return;
      const cur = sellMap.get(code) || { sellerLot: 0, sellerValue: 0 };
      cur.sellerLot += Number(r.data?.SLot) || 0;
      cur.sellerValue += Number(r.data?.SellerValue) || 0;
      sellMap.set(code, cur);
    });
    const netBuyList = netRows.map(n => ({
      stock: n.stock,
      netLot: Number(n.data?.NBLot) || 0,
      netValue: Number(n.data?.NBVal) || 0,
      netAvg: Number(n.data?.NBAvg) || 0,
    }));
    const netSellList = netSellRows.map(n => ({
      stock: n.stock,
      netLot: Number(n.data?.NSLot) || 0,
      netValue: Number(n.data?.NSVal) || 0,
      netAvg: Number(n.data?.NSAvg) || 0,
    }));
    const topBrokers = netBuyList.slice(0, 10).map(n => n.stock).filter(s => !!s);
    const brokers = topBrokers.length > 0
      ? topBrokers
      : [...buyMap.entries()].sort((a, b) => b[1].buyerLot - a[1].buyerLot).slice(0, 10).map(([k]) => k);
    return buildAiStatsForBrokers(brokers, { buyMap, sellMap, netBuyList, netSellList });
  }, [aiDateOverlay, buyStocksByDate, sellStocksByDate, netBuyStocksByDate, netSellStocksByDate, selectedDates, closePriceForAI, rangeAggregates]);

  // Overlay VALUE per tanggal: urutan gross-buy tanggal itu (sama persis dengan
  // kolom buy grup tanggal itu), sampai broker Top-5-net-buy terakhir — sejajar 1:1.
  const aiDateValueStats = useMemo(() => {
    if (!aiDateOverlay) return [];
    // Cabang range multi-range: "range:{rIdx}"
    if (aiDateOverlay.startsWith('range:')) {
      const agg = rangeAggregates[Number(aiDateOverlay.slice(6))];
      if (!agg || (agg.buyList || []).length === 0) return [];
      const top5 = new Set((agg.netBuyList || []).slice(0, 5).map(n => n.stock).filter(s => !!s));
      let lastIdx = -1;
      agg.buyList.forEach((s, idx) => { if (top5.has(s)) lastIdx = idx; });
      const count = lastIdx >= 0 ? lastIdx + 1 : 10;
      return buildAiStatsForBrokers(agg.buyList.slice(0, count), { buyMap: agg.buyMap, sellMap: agg.sellMap, netBuyList: agg.netBuyList, netSellList: agg.netSellList });
    }
    const d = aiDateOverlay;
    const buyRows = buyStocksByDate.get(d) || [];
    const sellRows = sellStocksByDate.get(d) || [];
    const netRows = netBuyStocksByDate.get(d) || [];
    const netSellRows = netSellStocksByDate.get(d) || [];
    if (buyRows.length === 0) return [];
    const buyMap = new Map<string, { buyerLot: number; buyerValue: number }>();
    buyRows.forEach(r => {
      const code = r.stock || '';
      if (!code) return;
      const cur = buyMap.get(code) || { buyerLot: 0, buyerValue: 0 };
      cur.buyerLot += Number(r.data?.BLot) || 0;
      cur.buyerValue += Number(r.data?.BuyerValue) || 0;
      buyMap.set(code, cur);
    });
    const sellMap = new Map<string, { sellerLot: number; sellerValue: number }>();
    sellRows.forEach(r => {
      const code = r.stock || '';
      if (!code) return;
      const cur = sellMap.get(code) || { sellerLot: 0, sellerValue: 0 };
      cur.sellerLot += Number(r.data?.SLot) || 0;
      cur.sellerValue += Number(r.data?.SellerValue) || 0;
      sellMap.set(code, cur);
    });
    const netBuyList = netRows.map(n => ({
      stock: n.stock,
      netLot: Number(n.data?.NBLot) || 0,
      netValue: Number(n.data?.NBVal) || 0,
      netAvg: Number(n.data?.NBAvg) || 0,
    }));
    const netSellList = netSellRows.map(n => ({
      stock: n.stock,
      netLot: Number(n.data?.NSLot) || 0,
      netValue: Number(n.data?.NSVal) || 0,
      netAvg: Number(n.data?.NSAvg) || 0,
    }));
    const buyOrdered = [...buyMap.entries()].sort((a, b) => b[1].buyerLot - a[1].buyerLot).map(([k]) => k);
    const top5 = new Set(netBuyList.slice(0, 5).map(n => n.stock).filter(s => !!s));
    let lastIdx = -1;
    buyOrdered.forEach((s, idx) => { if (top5.has(s)) lastIdx = idx; });
    const count = lastIdx >= 0 ? lastIdx + 1 : 10;
    return buildAiStatsForBrokers(buyOrdered.slice(0, count), { buyMap, sellMap, netBuyList, netSellList });
  }, [aiDateOverlay, buyStocksByDate, sellStocksByDate, netBuyStocksByDate, netSellStocksByDate, selectedDates, closePriceForAI, rangeAggregates]);

  // AI stats sebagai "tabel baru" di kanan Total — tinggi header & row disamakan tabel utama
  // (py-[1px], text-[12px] leading-tight, thead 2 baris) agar baris sejajar 1:1.
  // stats = Top 10 sesuai urutan kolom Total section masing-masing (Value / Net).
  const renderAiStatsTable = (stats: any[], groupLabel: string, onClose: () => void = () => setShowAIPopup(false)) => {
    const pctColor = (v: number) => v > 75 ? 'text-[#00ff00]' : v > 50 ? 'text-yellow-500' : 'text-red-500';
    return (
      <div className="shrink-0 self-start ml-2 border-l-2 border-r-2 border-b-2 border-white bg-popover">
        <table className={`${getFontSizeClass()} border-collapse leading-tight tabular-nums`} style={{ tableLayout: 'fixed', width: 'max-content' }}>
          <colgroup>
            <col style={{ width: 50 }} />
            <col style={{ width: 44 }} />
            <col style={{ width: 44 }} />
            <col style={{ width: 50 }} />
            <col style={{ width: 50 }} />
            <col style={{ width: 50 }} />
            <col style={{ width: 50 }} />
            <col style={{ width: 56 }} />
            <col style={{ width: 72 }} />
            <col style={{ width: 36 }} />
            <col style={{ width: 28 }} />
          </colgroup>
          <thead className="bg-[#3a4252] text-white sticky top-0 z-20">
            <tr className="border-t-2 border-white">
              <th colSpan={11} className="relative text-center py-[1px] px-2 font-bold text-white whitespace-nowrap">
                {groupLabel}
                <button onClick={onClose} className="absolute right-1 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground font-normal" title="Tutup">
                  &times;
                </button>
              </th>
            </tr>
            <tr>
              <th className="py-[1px] px-2 text-left font-semibold border-r border-white/20 whitespace-nowrap">Broker</th>
              <th className="py-[1px] px-2 text-center font-semibold border-r border-white/20 whitespace-nowrap">Acc</th>
              <th className="py-[1px] px-2 text-center font-semibold border-r border-white/20 whitespace-nowrap">Cons</th>
              <th className="py-[1px] px-2 text-center font-semibold border-r border-white/20 whitespace-nowrap">Dom B</th>
              <th className="py-[1px] px-2 text-center font-semibold border-r border-white/20 whitespace-nowrap">Dom S</th>
              <th className="py-[1px] px-2 text-center font-semibold border-r border-white/20 whitespace-nowrap">Dom N</th>
              <th className="py-[1px] px-2 text-center font-semibold border-r border-white/20 whitespace-nowrap">Dom T</th>
              <th className="py-[1px] px-2 text-center font-semibold border-r border-white/20 whitespace-nowrap">Float</th>
              <th className="py-[1px] px-2 text-center font-semibold border-r border-white/20 whitespace-nowrap" title="Floating PnL (Rp) = (Close - AvgBuy) × NetBuyVol (unrealized); tanpa posisi net buy → Realized = (AvgSell - Close) × NetSellVol (warna redup)">P/L</th>
              <th className="py-[1px] px-2 text-center font-semibold border-r border-white/20 whitespace-nowrap">Class</th>
              <th className="py-[1px] px-2 text-center font-semibold whitespace-nowrap">AI</th>
            </tr>
          </thead>
          <tbody>
            {stats.map((stat, i) => {
              const accRaw = (stat as any).accumulationRaw ?? 0;
              const consRaw = (stat as any).consistencyRaw ?? 0;
              // Realized (dari net sell) warnanya dibedakan: hijau/redup, bukan neon
              const smCls = stat.isRealized
                ? (stat.rawSmartMoney > 0 ? 'text-green-400' : stat.rawSmartMoney < 0 ? 'text-red-400' : '')
                : (stat.rawSmartMoney > 0 ? 'text-[#00ff00]' : stat.rawSmartMoney < 0 ? 'text-[#ff0000]' : '');
              const plCls = stat.isRealized
                ? (stat.rawFloating > 0 ? 'text-green-400' : stat.rawFloating < 0 ? 'text-red-400' : '')
                : (stat.rawFloating > 0 ? 'text-[#00ff00]' : stat.rawFloating < 0 ? 'text-[#ff0000]' : '');
              return (
              <tr
                key={stat.broker}
                className={i % 2 === 0 ? 'bg-background' : 'bg-muted/30'}
                style={{ height: COLUMN_WIDTHS.ROW_HEIGHT }}
              >
                <td className="py-[1px] px-2 border-r border-white/20 font-medium whitespace-nowrap">
                  <span className="inline-flex items-center">
                    {stat.broker}
                    {stat.buyStreak >= 2 && stat.buyStreak < stat.totalDays ? (
                      <span
                        className="ml-0.5 text-amber-400 font-bold cursor-help leading-none"
                        title={`Late start accumulation: Buy last ${stat.buyStreak} days in a row`}
                      >
                        *
                      </span>
                    ) : stat.anyBuyStreak >= 2 && stat.anyBuyStreak < stat.totalDays && stat.buyStreak < 2 ? (
                      <span
                        className="ml-0.5 text-amber-300 font-bold cursor-help leading-none"
                        title={`Buy streak: last ${stat.anyBuyStreak} days in a row`}
                      >
                        *
                      </span>
                    ) : null}
                  </span>
                </td>
                <td className={`py-[1px] px-2 border-r border-white/20 text-right font-medium whitespace-nowrap ${pctColor(accRaw)}`}>{stat.accumulation}</td>
                <td className={`py-[1px] px-2 border-r border-white/20 text-right font-medium whitespace-nowrap ${pctColor(consRaw)}`}>{stat.consistency}</td>
                <td className="py-[1px] px-2 border-r border-white/20 text-right whitespace-nowrap">{stat.domBuy}</td>
                <td className="py-[1px] px-2 border-r border-white/20 text-right whitespace-nowrap">{stat.domSell}</td>
                <td className="py-[1px] px-2 border-r border-white/20 text-right whitespace-nowrap">{stat.domNet}</td>
                <td className="py-[1px] px-2 border-r border-white/20 text-right whitespace-nowrap">{stat.domTotal}</td>
                <td className={`py-[1px] px-2 border-r border-white/20 text-right font-medium whitespace-nowrap ${smCls}`} title={stat.isRealized ? 'Realisasi (dari net sell)' : undefined}>{stat.smartMoney}</td>
                <td className={`py-[1px] px-2 border-r border-white/20 text-right font-medium whitespace-nowrap ${plCls}`} title={stat.isRealized ? 'Realisasi (dari net sell)' : undefined}>{stat.floating}</td>
                <td className="py-[1px] px-2 border-r border-white/20 text-center whitespace-nowrap"></td>
                {i === 0 && (
                  <td rowSpan={stats.length} className="py-[1px] px-2 text-center align-top whitespace-nowrap"></td>
                )}
              </tr>
              );
            })}
            {stats.length === 0 && (
              <tr>
                    <td colSpan={11} className="py-3 text-center text-muted-foreground italic">No data available</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };


  const dateSummariesMemo = useMemo(() => {
    const summaries = new Map<string, {
      buyTotalValue: number;
      buyTotalLot: number;
      buyForeignValue: number;
      sellTotalValue: number;
      sellTotalLot: number;
      sellForeignValue: number;
      buyAvgPrice: number;
      sellAvgPrice: number;
    }>();

    transactionData.forEach((dateData, date) => {
      let buyTotalValue = 0, buyTotalLot = 0, buyForeignValue = 0;
      let sellTotalValue = 0, sellTotalLot = 0, sellForeignValue = 0;

      dateData.forEach(item => {
        const buyVal = Number(item.BuyerValue) || 0;
        const sellVal = Number(item.SellerValue) || 0;
        const buyLot = Number(item.BLot) || 0;
        const sellLot = Number(item.SLot) || 0;

        buyTotalValue += buyVal;
        buyTotalLot += buyLot;
        sellTotalValue += sellVal;
        sellTotalLot += sellLot;

        const bCode = (item.BCode || '').toUpperCase();
        const sCode = (item.SCode || '').toUpperCase();
        if (bCode && FOREIGN_BROKERS.includes(bCode)) buyForeignValue += buyVal;
        if (sCode && FOREIGN_BROKERS.includes(sCode)) sellForeignValue += sellVal;
      });

      summaries.set(date, {
        buyTotalValue, buyTotalLot, buyForeignValue,
        sellTotalValue, sellTotalLot, sellForeignValue,
        buyAvgPrice: buyTotalLot > 0 ? buyTotalValue / (buyTotalLot * 100) : 0,
        sellAvgPrice: sellTotalLot > 0 ? sellTotalValue / (sellTotalLot * 100) : 0
      });
    });

    return summaries;
  }, [transactionData]);

  // Calculate max rows for virtual scrolling - use max rows from all sections (Buy, Sell, Net Buy, Net Sell)
  // FIXED: Use all dates from buyStocksByDate/sellStocksByDate/etc, not selectedDates to prevent re-calculation on input change
  const maxRows = useMemo(() => {
    let maxBuyRows = 0;
    let maxSellRows = 0;
    let maxNetBuyRows = 0;
    let maxNetSellRows = 0;
    // Use all dates from the Maps, not selectedDates (to prevent re-calculation on input change)
    const allDates = new Set([
      ...Array.from(buyStocksByDate.keys()),
      ...Array.from(sellStocksByDate.keys()),
      ...Array.from(netBuyStocksByDate.keys()),
      ...Array.from(netSellStocksByDate.keys())
    ]);
    allDates.forEach(date => {
      const buyData = buyStocksByDate.get(date) || [];
      const sellData = sellStocksByDate.get(date) || [];
      const netBuyData = netBuyStocksByDate.get(date) || [];
      const netSellData = netSellStocksByDate.get(date) || [];
      maxBuyRows = Math.max(maxBuyRows, buyData.length);
      maxSellRows = Math.max(maxSellRows, sellData.length);
      maxNetBuyRows = Math.max(maxNetBuyRows, netBuyData.length);
      maxNetSellRows = Math.max(maxNetSellRows, netSellData.length);
    });
    const result = Math.max(maxBuyRows, maxSellRows, maxNetBuyRows, maxNetSellRows);
    return result;
  }, [buyStocksByDate, sellStocksByDate, netBuyStocksByDate, netSellStocksByDate]); // FIXED: Removed selectedDates - only recalculate when data changes, not when input changes

  // Calculate visible row indices for virtual scrolling
  // OPTIMIZED: Simplify - always calculate if maxRows > 0
  const visibleRowIndices = useMemo(() => {
    if (maxRows === 0) return [];

    const maxVisible = Math.min(visibleRowCount, maxRows);
    const indices: number[] = [];
    for (let i = 0; i < maxVisible; i++) {
      indices.push(i);
    }
    return indices;
  }, [visibleRowCount, maxRows]);

  // Lazy loading with Intersection Observer - load more rows when scrolling near bottom
  // FIXED: Separate observers for Value and Net tables so they work independently
  useEffect(() => {
    if (!isDataReady || maxRows === 0) return;

    // Only set up observer if we haven't reached max rows yet
    // Use ref (not state) so this guard doesn't add visibleRowCount to effect deps
    if (visibleRowCountRef.current >= maxRows) return;

    const valueSentinel = valueTableSentinelRef.current;
    const netSentinel = netTableSentinelRef.current;
    const valueContainer = valueTableContainerRef.current;
    const netContainer = netTableContainerRef.current;

    if (!valueSentinel || !netSentinel || !valueContainer || !netContainer) return;

    // Handler for loading more rows
    const loadMoreRows = () => {
      setVisibleRowCount((prev) => {
        // Use functional update to get latest maxRows value
        const currentMaxRows = maxRows;
        if (currentMaxRows === 0 || prev >= currentMaxRows) return prev;
        const newCount = Math.min(prev + MAX_DISPLAY_ROWS, currentMaxRows);
        return newCount;
      });
    };

    // Create separate intersection observer for Value table
    const valueObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            loadMoreRows();
          }
        });
      },
      {
        root: valueContainer, // Use Value table container as root
        rootMargin: '200px', // Trigger 200px before sentinel comes into view
        threshold: 0.1
      }
    );

    // Create separate intersection observer for Net table
    const netObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            loadMoreRows();
          }
        });
      },
      {
        root: netContainer, // Use Net table container as root
        rootMargin: '200px', // Trigger 200px before sentinel comes into view
        threshold: 0.1
      }
    );

    // Observe sentinels with their respective observers
    if (valueSentinel) valueObserver.observe(valueSentinel);
    if (netSentinel) netObserver.observe(netSentinel);

    return () => {
      valueObserver.disconnect();
      netObserver.disconnect();
    };
  }, [isDataReady, maxRows]); // visibleRowCount intentionally excluded — loadMoreRows functional update already guards against overflow



  const renderHorizontalView = useCallback(() => {
    // FIXED: Don't hide data when input changes - only check isDataReady
    // Keep showing existing data even if user changes input (until Show button is clicked)
    if (!isDataReady) {
      return null;
    }

    // NEW: Filter selectedDates to show only days with data
    // This allows skipping empty data days in the UI tables
    const visibleDates = selectedDates.filter(date => {
      const buyData = buyStocksByDate.get(date) || [];
      const sellData = sellStocksByDate.get(date) || [];
      const netBuyData = netBuyStocksByDate.get(date) || [];
      const netSellData = netSellStocksByDate.get(date) || [];

      return buyData.length > 0 || sellData.length > 0 || netBuyData.length > 0 || netSellData.length > 0;
    });

    // VALUE Table - Shows Buyer and Seller data
    const renderValueTable = () => {
      // Determine if we should show only Total column (when > 7 visible days selected)
      const showOnlyTotal = visibleDates.length > 30;

      // Helper function to get stock color class based on sector (using memoized cache)
      // CRITICAL: This should always work regardless of ticker selection - coloring is independent of filtering
      const getStockColorClass = (stockCode: string): { color: string; className: string } => {
        return stockColorCache.get(stockCode, stockToSectorMap);
      };

      // For VALUE table: Get max row count across all dates for Buy and Sell sections separately
      // CRITICAL: Filtering is done in loadTransactionData, so no need to filter here
      // Data is already filtered by ticker or sector at fetch time

      // CRITICAL: Total column ALWAYS uses sorted stocks from aggregated data
      // Reuse memoized sorted stocks to avoid recalculation
      const sortedTotalBuyStocks = sortedTotalBuyStocksMemo;
      const sortedTotalSellStocks = sortedTotalSellStocksMemo;

      let maxBuyRows = sortedTotalBuyStocks.length;
      let maxSellRows = sortedTotalSellStocks.length;

      // Multi-range: baris mengikuti grup range terpanjang + grand total
      if (isMultiRangeDisplayed) {
        rangeAggregates.forEach(agg => {
          maxBuyRows = Math.max(maxBuyRows, agg.buyList.length);
          maxSellRows = Math.max(maxSellRows, agg.sellList.length);
        });
      }

      if (!showOnlyTotal) {
        // When showing per-date, also check visible date rows to ensure we show all data
        visibleDates.forEach(date => {
          const buyData = buyStocksByDate.get(date) || [];
          const sellData = sellStocksByDate.get(date) || [];
          maxBuyRows = Math.max(maxBuyRows, buyData.length);
          maxSellRows = Math.max(maxSellRows, sellData.length);
        });
      }
      const tableMaxRows = Math.max(maxBuyRows, maxSellRows);

      // Respect lazy-loading limit: cap to visibleRowIndices, but don't exceed tableMaxRows
      const rowIndices = tableMaxRows === 0
        ? []
        : visibleRowIndices.length > tableMaxRows
          ? visibleRowIndices.slice(0, tableMaxRows)
          : visibleRowIndices;

      // Calculate total columns for "No Data Available" message
      // Base columns: BCode/Broker, BVal, BLot, BAvg, #, SCode/Stock, SVal, SLot, SAvg = 9
      // Optional: BFreq, Lot/F (2), BOr, Lot/Or (2), BNS, Lot/NS (2), SFreq, Lot/F (2), SOr, Lot/Or (2), SNS, Lot/NS (2) = 12
      const baseCols = 9;
      const optionalCols = (showFrequency ? 4 : 0) + (showOrder ? 4 : 0) + (showNoSplit ? 4 : 0);
      const colsPerDate = baseCols + optionalCols;
      const totalCols = showOnlyTotal && !isMultiRangeDisplayed ? colsPerDate : (isMultiRangeDisplayed ? (rangeAggregates.length + 1) * colsPerDate : (visibleDates.length * colsPerDate + colsPerDate));

      // Renderer satu grup kolom agregat (Total / per-range) — diekstrak dari
      // IIFE kolom Total agar dipakai ulang untuk tiap range tanpa duplikasi.
      // buyMap/sellMap: peta agregat (grand total atau per-range).
      const renderValueTotalGroup = (
        rowIdx: number,
        buyList: string[],
        sellList: string[],
        buyMap: Map<string, { buyerValue: number; buyerFreq: number; buyerOrdNum: number; buyerNoSplit?: number; buyerLot: number; buyerLotPerFreq: number; buyerLotPerOrdNum: number; buyerLotPerNoSplit?: number; buyerAvg: number }>,
        sellMap: Map<string, { sellerValue: number; sellerFreq: number; sellerOrdNum: number; sellerNoSplit?: number; sellerLot: number; sellerLotPerFreq: number; sellerLotPerOrdNum: number; sellerLotPerNoSplit?: number; sellerAvg: number }>,
        leftCls: string,
        rightCls: string
      ) => {
        // Get Buy stock code at this row index (always from sorted stocks)
        const buyStockCode = buyList[rowIdx] || '';
        const totalBuyBCode = buyStockCode || '-';

        // Get Sell stock code at this row index (always from sorted stocks)
        const sellStockCode = sellList[rowIdx] || '';
        const totalSellSCode = sellStockCode || '-';

        // Get aggregated data for Buy and Sell
        // Use Lot/F and Lot/ON from aggregated data (calculated from CSV values)
        const buyTotalData = buyStockCode ? buyMap.get(buyStockCode) : null;
        const sellTotalData = sellStockCode ? sellMap.get(sellStockCode) : null;

        // Get aggregated values (always use from totalBuyDataByStock and totalSellDataByStock for consistency)
        const totalBuyValue = buyTotalData ? buyTotalData.buyerValue : 0;
        const totalBuyFreq = buyTotalData ? buyTotalData.buyerFreq : 0;
        const totalBuyOrdNum = buyTotalData ? buyTotalData.buyerOrdNum : 0;
        const totalBuyNoSplit = buyTotalData?.buyerNoSplit || 0;
        const totalSellValue = sellTotalData ? sellTotalData.sellerValue : 0;
        const totalSellFreq = sellTotalData ? sellTotalData.sellerFreq : 0;
        const totalSellOrdNum = sellTotalData ? sellTotalData.sellerOrdNum : 0;
        const totalSellNoSplit = sellTotalData?.sellerNoSplit || 0;

        const totalBuyLot = buyTotalData?.buyerLot || 0;
        const totalSellLot = sellTotalData?.sellerLot || 0;
        const totalBuyLotPerFreq = buyTotalData?.buyerLotPerFreq || 0;
        const totalSellLotPerFreq = sellTotalData?.sellerLotPerFreq || 0;
        const totalBuyLotPerOrdNum = buyTotalData?.buyerLotPerOrdNum || 0;
        const totalSellLotPerOrdNum = sellTotalData?.sellerLotPerOrdNum || 0;
        const totalBuyLotPerNoSplitRaw = buyTotalData?.buyerLotPerNoSplit || 0;
        const totalSellLotPerNoSplitRaw = sellTotalData?.sellerLotPerNoSplit || 0;
        const totalBuyLotPerNoSplit = totalBuyLotPerNoSplitRaw !== 0 ? totalBuyLotPerNoSplitRaw : (totalBuyNoSplit > 0 ? totalBuyLot / totalBuyNoSplit : 0);
        const totalSellLotPerNoSplit = totalSellLotPerNoSplitRaw !== 0 ? totalSellLotPerNoSplitRaw : (totalSellNoSplit > 0 ? totalSellLot / totalSellNoSplit : 0);

        // Calculate final averages (use from aggregated data)
        const finalBuyAvg = buyTotalData ? buyTotalData.buyerAvg : 0;
        const finalSellAvg = sellTotalData ? sellTotalData.sellerAvg : 0;

        // Hide Total row if both Buy and Sell are empty
        if (totalBuyBCode === '-' && totalSellSCode === '-') {
          return (
            <td className={`text-center py-[1px] px-[6.5px] text-gray-400 ${leftCls}`} colSpan={baseCols + optionalCols}>
              -
            </td>
          );
        }

        // Get color classes for codes based on pivot type
        // For Stock pivot: BCode/SCode are broker codes, use getBrokerColorClass
        // For Broker pivot: BCode/SCode are stock codes, use getStockColorClass
        const buyBCodeColor = totalBuyBCode !== '-'
          ? (pivotFilter === 'Stock'
            ? getBrokerColorClass(totalBuyBCode)
            : getStockColorClass(totalBuyBCode))
          : { color: '#FFFFFF', className: '' };
        const sellSCodeColor = totalSellSCode !== '-'
          ? (pivotFilter === 'Stock'
            ? getBrokerColorClass(totalSellSCode)
            : getStockColorClass(totalSellSCode))
          : { color: '#FFFFFF', className: '' };

        return (
          <React.Fragment>
            {/* Buyer Total Columns */}
            {totalBuyBCode !== '-' && Math.abs(totalBuyLot) > 0 ? (
              <>
                <td className={`text-center py-[1px] px-[6.5px] ${buyBCodeColor.className} ${leftCls}`} style={{ minWidth: '24px', maxWidth: '24px', color: buyBCodeColor.color, ...getTxBlockStyle(totalBuyBCode), fontVariantNumeric: 'tabular-nums' }}>
                  {totalBuyBCode}
                </td>
                <td className="text-right py-[1px] px-[3.5px] text-[#00ff00]" style={{ ...getTxBlockStyle(totalBuyBCode), fontVariantNumeric: 'tabular-nums' }}>{formatLot(totalBuyLot)}</td>
                <td className="text-right py-[1px] px-[5.5px] text-[#00ff00]" style={{ ...getTxBlockStyle(totalBuyBCode), fontVariantNumeric: 'tabular-nums' }}>{formatValue(totalBuyValue)}</td>
                <td className="text-right py-[1px] px-[5.5px] text-[#00ff00]" style={{ ...getTxBlockStyle(totalBuyBCode), fontVariantNumeric: 'tabular-nums' }}>{formatAverage(finalBuyAvg)}</td>
                {showFrequency && <td className="text-right py-[1px] px-[3.5px] text-lime-400" style={{ ...getTxBlockStyle(totalBuyBCode), fontVariantNumeric: 'tabular-nums' }}>{totalBuyFreq}</td>}
                {showFrequency && <td className="text-right py-[1px] px-[3.5px] text-lime-400" style={{ ...getTxBlockStyle(totalBuyBCode), fontVariantNumeric: 'tabular-nums' }}>{formatLotPerFreqOrOrdNum(totalBuyLotPerFreq)}</td>}
                {showOrder && <td className="text-right py-[1px] px-[3.5px] text-teal-400" style={{ ...getTxBlockStyle(totalBuyBCode), fontVariantNumeric: 'tabular-nums' }}>{totalBuyOrdNum}</td>}
                {showOrder && <td className="text-right py-[1px] px-[3.5px] text-teal-400" style={{ ...getTxBlockStyle(totalBuyBCode), fontVariantNumeric: 'tabular-nums' }}>{formatLotPerFreqOrOrdNum(totalBuyLotPerOrdNum)}</td>}
                {showNoSplit && <td className="text-right py-[1px] px-[3.5px] text-green-300" style={{ ...getTxBlockStyle(totalBuyBCode), fontVariantNumeric: 'tabular-nums' }}>{totalBuyNoSplit}</td>}
                {showNoSplit && <td className="text-right py-[1px] px-[3.5px] text-green-300" style={{ ...getTxBlockStyle(totalBuyBCode), fontVariantNumeric: 'tabular-nums' }}>{formatLotPerFreqOrOrdNum(totalBuyLotPerNoSplit)}</td>}
              </>
            ) : (
              <>
                <td className={`text-center py-[1px] px-[6.5px] text-gray-400 ${leftCls}`} style={{ overflow: 'hidden', fontVariantNumeric: 'tabular-nums' }}>-</td>
                <td className="text-right py-[1px] px-[3.5px] text-gray-400" style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>
                <td className="text-right py-[1px] px-[3.5px] text-gray-400" style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>
                {!showFrequency && !showOrder && !showNoSplit ? (
                  <td className="text-right py-[1px] px-[3.5px] text-gray-400 border-r-2 border-white" style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>
                ) : (
                  <>
                    <td className="text-right py-[1px] px-[3.5px] text-gray-400" style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>
                    {showFrequency && <td className="text-right py-[1px] px-[3.5px] text-gray-400" style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                    {showFrequency && <td className={`text-right py-[1px] px-[3.5px] text-gray-400 ${!showOrder && !showNoSplit ? 'border-r-2 border-white' : ''}`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                    {showOrder && <td className="text-right py-[1px] px-[3.5px] text-gray-400" style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                    {showOrder && <td className={`text-right py-[1px] px-[3.5px] text-gray-400 ${!showNoSplit ? 'border-r-2 border-white' : ''}`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                    {showNoSplit && <td className="text-right py-[1px] px-[3.5px] text-gray-400" style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                    {showNoSplit && <td className={`text-right py-[1px] px-[3.5px] text-gray-400 border-r-2 border-white`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                  </>
                )}
              </>
            )}
            {/* Separator */}
            <td className="text-center py-[1px] px-[7.4px] text-white bg-[#3a4252] whitespace-nowrap" style={{ fontVariantNumeric: 'tabular-nums' }}>{rowIdx + 1}</td>
            {/* Seller Total Columns */}
            {totalSellSCode !== '-' && Math.abs(totalSellLot) > 0 ? (
              <>
                <td className={`text-center py-[1px] px-[6.5px] ${sellSCodeColor.className}`} style={{ minWidth: '24px', maxWidth: '24px', color: sellSCodeColor.color, ...getTxBlockStyle(totalSellSCode), fontVariantNumeric: 'tabular-nums' }}>
                  {totalSellSCode}
                </td>
                <td className="text-right py-[1px] px-[3.5px] text-[#ff0000]" style={{ ...getTxBlockStyle(totalSellSCode), fontVariantNumeric: 'tabular-nums' }}>{formatLot(totalSellLot)}</td>
                <td className="text-right py-[1px] px-[5.5px] text-[#ff0000]" style={{ ...getTxBlockStyle(totalSellSCode), fontVariantNumeric: 'tabular-nums' }}>{formatValue(totalSellValue)}</td>
                {!showFrequency && !showOrder && !showNoSplit ? (
                  <td className={`text-right py-[1px] px-[5.5px] text-[#ff0000] ${rightCls}`} style={{ ...getTxBlockStyle(totalSellSCode), fontVariantNumeric: 'tabular-nums' }}>{formatAverage(finalSellAvg)}</td>
                ) : (
                  <>
                    <td className="text-right py-[1px] px-[5.5px] text-[#ff0000]" style={{ ...getTxBlockStyle(totalSellSCode), fontVariantNumeric: 'tabular-nums' }}>{formatAverage(finalSellAvg)}</td>
                    {showFrequency && <td className="text-right py-[1px] px-[3.5px] text-amber-500" style={{ ...getTxBlockStyle(totalSellSCode), fontVariantNumeric: 'tabular-nums' }}>{totalSellFreq}</td>}
                    {showFrequency && <td className={`text-right py-[1px] px-[3.5px] text-amber-500 ${!showOrder && !showNoSplit ? rightCls : ''}`} style={{ ...getTxBlockStyle(totalSellSCode), fontVariantNumeric: 'tabular-nums' }}>{formatLotPerFreqOrOrdNum(totalSellLotPerFreq)}</td>}
                    {showOrder && <td className="text-right py-[1px] px-[3.5px] text-[#e5c3d1]" style={{ ...getTxBlockStyle(totalSellSCode), fontVariantNumeric: 'tabular-nums' }}>{totalSellOrdNum}</td>}
                    {showOrder && <td className={`text-right py-[1px] px-[3.5px] text-[#e5c3d1] ${!showNoSplit ? rightCls : ''}`} style={{ ...getTxBlockStyle(totalSellSCode), fontVariantNumeric: 'tabular-nums' }}>
                      {formatLotPerFreqOrOrdNum(totalSellLotPerOrdNum)}
                    </td>}
                    {showNoSplit && <td className="text-right py-[1px] px-[3.5px] text-red-300" style={{ ...getTxBlockStyle(totalSellSCode), fontVariantNumeric: 'tabular-nums' }}>{totalSellNoSplit}</td>}
                    {showNoSplit && <td className={`text-right py-[1px] px-[3.5px] text-red-300 ${rightCls}`} style={{ ...getTxBlockStyle(totalSellSCode), fontVariantNumeric: 'tabular-nums' }}>{formatLotPerFreqOrOrdNum(totalSellLotPerNoSplit)}</td>}
                  </>
                )}
              </>
            ) : (
              <>
                <td className="text-center py-[1px] px-[6.5px] text-gray-400" style={{ overflow: 'hidden', boxSizing: 'border-box', fontVariantNumeric: 'tabular-nums' }}>-</td>
                <td className="text-right py-[1px] px-[3.5px] text-gray-400" style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>
                <td className="text-right py-[1px] px-[3.5px] text-gray-400" style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>
                {!showFrequency && !showOrder && !showNoSplit ? (
                  <td className={`text-right py-[1px] px-[3.5px] text-gray-400 ${rightCls}`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>
                ) : (
                  <>
                    <td className="text-right py-[1px] px-[3.5px] text-gray-400" style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>
                    {showFrequency && <td className="text-right py-[1px] px-[3.5px] text-gray-400" style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                    {showFrequency && <td className={`text-right py-[1px] px-[3.5px] text-gray-400 ${!showOrder && !showNoSplit ? rightCls : ''}`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                    {showOrder && <td className="text-right py-[1px] px-[3.5px] text-gray-400" style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                    {showOrder && <td className={`text-right py-[1px] px-[3.5px] text-gray-400 ${!showNoSplit ? rightCls : ''}`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                    {showNoSplit && <td className="text-right py-[1px] px-[3.5px] text-gray-400" style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                    {showNoSplit && <td className={`text-right py-[1px] px-[3.5px] text-gray-400 ${rightCls}`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                  </>
                )}
              </>
            )}
          </React.Fragment>
        );
      };

      return (
        <div className="w-full flex-1 flex flex-col min-h-0">
          <div className="bg-muted/50 px-4 py-1.5 border-y border-border flex items-center justify-between">
            <h3 className="font-semibold text-sm">VALUE - {selectedTickers.length > 0 ? selectedTickers.join(', ') : ''} - {boardFilter || 'All'}</h3>
          </div>
          <div className={`${(showOnlyTotal && !isMultiRangeDisplayed) ? 'flex justify-center' : 'w-full max-w-full'} flex-1 min-h-0`}>
            <div ref={valueTableContainerRef} className={`${(showOnlyTotal && !isMultiRangeDisplayed) ? 'w-auto' : 'w-full max-w-full'} overflow-x-auto overflow-y-auto border-l-2 border-r-2 border-b-2 border-white h-full`} style={{ scrollBehavior: 'auto' }}>
              <div className="flex items-stretch w-max min-w-full">
              <table ref={valueTableRef} className={`${showOnlyTotal ? 'min-w-0' : 'min-w-0'} ${getFontSizeClass()} border-collapse leading-tight`} style={{ borderSpacing: 0, tableLayout: 'fixed', width: 'max-content' }}>
                {renderTxColGroup(isMultiRangeDisplayed ? rangeAggregates.length + 1 : (showOnlyTotal ? 1 : visibleDates.length + 1), 'v')}

                <thead className="bg-[#3a4252] sticky top-0 z-20">
                  {/* Date Header Row */}
                  <tr className="border-t-2 border-white">
                    {!showOnlyTotal && !isMultiRangeDisplayed && visibleDates.map((date, dateIndex) => (
                      <th
                        key={date}
                        className={`relative text-center py-[1px] px-[7.4px] font-bold text-white whitespace-nowrap ${dateIndex === 0 ? 'border-l-2 border-white' : 'before:absolute before:left-[-5px] before:top-0 before:bottom-0 before:w-[10px] before:bg-white before:content-[""]'}`}
                        colSpan={colsPerDate}
                      >
                        {dateIndex !== 0 && (
                          <span aria-hidden="true" style={{ position: 'absolute', left: -5, top: 0, bottom: 0, width: 10, backgroundColor: '#fff' }} />
                        )}
                        {formatDisplayDate(date)}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!canUseAiFeatures) {
                              showToast({ type: 'warning', title: 'Menu khusus bootcamp' });
                              return;
                            }
                            setAiDateOverlay(prev => prev === date ? null : date);
                          }}
                          className={`absolute right-1 top-1/2 -translate-y-1/2 px-1 h-[18px] rounded-[4px] text-[10px] font-normal border flex items-center ${aiDateOverlay === date ? 'border-indigo-400 text-white bg-indigo-500/40' : 'border-white text-white hover:bg-white/10 bg-[#3a4252]/60'}`}
                          title="AI tanggal ini — menu khusus bootcamp"
                        >
                          <span className="inline-flex items-end leading-none rounded-[3px] bg-purple-500/30 px-0.5"><span className="font-bold bg-gradient-to-r from-[#00ff00] to-[#ff0000] bg-clip-text text-transparent drop-shadow-[0_0_4px_rgba(168,85,247,0.9)]">Ai</span></span>
                        </button>
                        {aiDateOverlay === date && (
                          <div ref={(el) => { if (el) el.scrollIntoView({ block: 'nearest', inline: 'nearest' }); }} className="absolute left-full top-0 z-50 ml-1 max-h-[70vh] overflow-y-auto font-normal" onClick={(e) => e.stopPropagation()}>
                            {renderAiStatsTable(aiDateValueStats, formatDisplayDate(date), () => setAiDateOverlay(null))}
                          </div>
                        )}
                      </th>
                    ))}
                    {isMultiRangeDisplayed && (
                      <>
                        {displayedSortedRangeLabels.map((label, rIdx) => (
                          <th key={`v-hdr-${rIdx}`} className={`relative text-center py-[1px] px-[7.4px] font-bold text-white whitespace-nowrap ${rIdx === 0 ? 'border-l-2 border-white' : 'before:absolute before:left-[-5px] before:top-0 before:bottom-0 before:w-[10px] before:bg-white before:content-[""]'} border-r-[10px] border-white`} colSpan={colsPerDate}>
                            {rIdx !== 0 && (
                              <span aria-hidden="true" style={{ position: 'absolute', left: -5, top: 0, bottom: 0, width: 10, backgroundColor: '#fff' }} />
                            )}
                            {label}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!canUseAiFeatures) {
                                  showToast({ type: 'warning', title: 'Menu khusus bootcamp' });
                                  return;
                                }
                                const k = `range:${rIdx}`;
                                setAiDateOverlay(prev => prev === k ? null : k);
                              }}
                              className={`absolute right-1 top-1/2 -translate-y-1/2 px-1 h-[18px] rounded-[4px] text-[10px] font-normal border flex items-center ${aiDateOverlay === `range:${rIdx}` ? 'border-indigo-400 text-white bg-indigo-500/40' : 'border-white text-white hover:bg-white/10 bg-[#3a4252]/60'}`}
                              title="AI range ini — menu khusus bootcamp"
                            >
                              <span className="inline-flex items-end leading-none rounded-[3px] bg-purple-500/30 px-0.5"><span className="font-bold bg-gradient-to-r from-[#00ff00] to-[#ff0000] bg-clip-text text-transparent drop-shadow-[0_0_4px_rgba(168,85,247,0.9)]">Ai</span></span>
                            </button>
                            {aiDateOverlay === `range:${rIdx}` && (
                              <div ref={(el) => { if (el) el.scrollIntoView({ block: 'nearest', inline: 'nearest' }); }} className="absolute left-full top-0 z-50 ml-1 max-h-[70vh] overflow-y-auto font-normal" onClick={(e) => e.stopPropagation()}>
                                {renderAiStatsTable(aiDateValueStats, label, () => setAiDateOverlay(null))}
                              </div>
                            )}
                          </th>
                        ))}
                      </>
                    )}
                    <th className={`relative text-center py-[1px] px-[3.8px] font-bold text-white border-r-2 border-white ${(showOnlyTotal || visibleDates.length === 0) && !isMultiRangeDisplayed ? 'border-l-2 border-white' : 'before:absolute before:left-[-5px] before:top-0 before:bottom-0 before:w-[10px] before:bg-white before:content-[""]'}`}                         colSpan={colsPerDate}>
                      {((showOnlyTotal || visibleDates.length === 0) && !isMultiRangeDisplayed) ? null : (
                        <span aria-hidden="true" style={{ position: 'absolute', left: -5, top: 0, bottom: 0, width: 10, backgroundColor: '#fff' }} />
                      )}
                      Total
                    </th>
                  </tr>
                  {/* Column Header Row */}
                  <tr className="bg-[#3a4252]">
                    {!showOnlyTotal && !isMultiRangeDisplayed && visibleDates.map((date, dateIndex) => (
                      <React.Fragment key={date}>
                        {/* Buyer Columns */}
                        <th className={`relative text-center py-[1px] px-[7.4px] font-bold text-white ${dateIndex === 0 ? 'border-l-2 border-white' : 'before:absolute before:left-[-5px] before:top-0 before:bottom-0 before:w-[10px] before:bg-white before:content-[""]'}`} title={formatDisplayDate(date)}>{dateIndex !== 0 && (<span aria-hidden="true" style={{ position: 'absolute', left: -5, top: 0, bottom: 0, width: 10, backgroundColor: '#fff' }} />)}BY</th>
                        <th className="text-center py-[1px] px-[7.4px] font-bold text-white" title={formatDisplayDate(date)} style={{ }}>BLot</th>
                        <th className="text-center py-[1px] px-[7.4px] font-bold text-white" title={formatDisplayDate(date)} style={{ }}>BVal</th>
                        <th className="text-center py-[1px] px-[7.4px] font-bold text-white" title={formatDisplayDate(date)} style={{ }}>BAvg</th>
                        {showFrequency && <th className="text-center py-[1px] px-[7.4px] font-bold text-white bg-[#3a4252] bg-clip-padding" title={formatDisplayDate(date)} style={{ }}>BFreq</th>}
                        {showFrequency && <th className="text-center py-[1px] px-[7.4px] font-bold text-white bg-[#3a4252] bg-clip-padding whitespace-nowrap" title={formatDisplayDate(date)} style={{ }}>Lot/F</th>}
                        {showOrder && <th className="text-center py-[1px] px-[7.4px] font-bold text-white bg-[#3a4252] bg-clip-padding whitespace-nowrap" title={formatDisplayDate(date)} style={{ }}>BOrd</th>}
                        {showOrder && <th className="text-center py-[1px] px-[7.4px] font-bold text-white bg-[#3a4252] bg-clip-padding whitespace-nowrap" title={formatDisplayDate(date)} style={{ }}>Lot/Ord</th>}
                        {showNoSplit && <th className="text-center py-[1px] px-[7.4px] font-bold text-white bg-[#3a4252] bg-clip-padding" title={formatDisplayDate(date)} style={{ }}>BNon</th>}
                        {showNoSplit && <th className="text-center py-[1px] px-[7.4px] font-bold text-white bg-[#3a4252] bg-clip-padding whitespace-nowrap" title={formatDisplayDate(date)} style={{ }}>Lot/Non</th>}
                        {/* Separator */}
                        <th className="text-center py-[1px] px-[7.4px] font-bold text-white bg-[#3a4252] whitespace-nowrap" title={formatDisplayDate(date)} style={{ }}>#</th>
                        {/* Seller Columns */}
                        <th className="text-center py-[1px] px-[7.4px] font-bold text-white" title={formatDisplayDate(date)} style={{ overflow: 'hidden' }}>SL</th>
                        <th className="text-center py-[1px] px-[7.4px] font-bold text-white bg-[#3a4252] bg-clip-padding" title={formatDisplayDate(date)} style={{ }}>SLot</th>
                        <th className="text-center py-[1px] px-[7.4px] font-bold text-white bg-[#3a4252] bg-clip-padding" title={formatDisplayDate(date)} style={{ }}>SVal</th>
                        {!showFrequency && !showOrder && !showNoSplit ? (
                          <th className="text-center py-[1px] px-[7.4px] font-bold text-white bg-[#3a4252] bg-clip-padding" title={formatDisplayDate(date)} style={{ }}>SAvg</th>
                        ) : (
                          <>
                            <th className="text-center py-[1px] px-[7.4px] font-bold text-white bg-[#3a4252] bg-clip-padding" title={formatDisplayDate(date)} style={{ }}>SAvg</th>
                            {showFrequency && <th className="text-center py-[1px] px-[7.4px] font-bold text-white bg-[#3a4252] bg-clip-padding" title={formatDisplayDate(date)} style={{ }}>SFreq</th>}
                            {showFrequency && <th className={`text-center py-[1px] px-[7.4px] font-bold text-white whitespace-nowrap bg-[#3a4252] bg-clip-padding}`} title={formatDisplayDate(date)} style={{ }}>Lot/F</th>}
                            {showOrder && <th className="text-center py-[1px] px-[7.4px] font-bold text-white whitespace-nowrap bg-[#3a4252] bg-clip-padding" title={formatDisplayDate(date)} style={{ }}>SOrd</th>}
                            {showOrder && <th className={`text-center py-[1px] px-[7.4px] font-bold text-white whitespace-nowrap bg-[#3a4252] bg-clip-padding`} title={formatDisplayDate(date)} style={{ }}>Lot/Ord</th>}
                            {showNoSplit && <th className="text-center py-[1px] px-[7.4px] font-bold text-white bg-[#3a4252] bg-clip-padding" title={formatDisplayDate(date)} style={{ }}>SNon</th>}
                            {showNoSplit && <th className="text-center py-[1px] px-[7.4px] font-bold text-white whitespace-nowrap bg-[#3a4252] bg-clip-padding" title={formatDisplayDate(date)} style={{ }}>Lot/Non</th>}
                          </>
                        )}
                      </React.Fragment>
                    ))}
                    {/* Total Columns (diulang per grup kolom saat multi-range) */}
                    {(isMultiRangeDisplayed ? [...Array(rangeAggregates.length + 1).keys()] : [-1]).map((gg) => {
                      const gIsLeftmost = (!isMultiRangeDisplayed && (showOnlyTotal || visibleDates.length === 0)) || (isMultiRangeDisplayed && gg === 0);
                      const gRight = (!isMultiRangeDisplayed || gg === rangeAggregates.length) ? 'border-r-2 border-white' : '';
                      return (
                      <React.Fragment key={gg}>
                    <th className={`relative text-center py-[1px] px-[6.5px] font-bold text-white ${gIsLeftmost ? 'border-l-2 border-white' : 'before:absolute before:left-[-5px] before:top-0 before:bottom-0 before:w-[10px] before:bg-white before:content-[""]'}`}>{!gIsLeftmost && (<span aria-hidden="true" style={{ position: 'absolute', left: -5, top: 0, bottom: 0, width: 10, backgroundColor: '#fff' }} />)}BY</th>
                    <th className="text-center py-[1px] px-[6.5px] font-bold text-white" style={{ }}>BLot</th>
                    <th className="text-center py-[1px] px-[6.5px] font-bold text-white" style={{ }}>BVal</th>
                    <th className="text-center py-[1px] px-[6.5px] font-bold text-white" style={{ }}>BAvg</th>
                    {showFrequency && <th className="text-center py-[1px] px-[6.5px] font-bold text-white bg-[#3a4252] bg-clip-padding" style={{ }}>BFreq</th>}
                    {showFrequency && <th className="text-center py-[1px] px-[6.5px] font-bold text-white bg-[#3a4252] bg-clip-padding whitespace-nowrap" style={{ }}>Lot/F</th>}
                    {showOrder && <th className="text-center py-[1px] px-[6.5px] font-bold text-white bg-[#3a4252] bg-clip-padding whitespace-nowrap" style={{ }}>BOrd</th>}
                    {showOrder && <th className="text-center py-[1px] px-[6.5px] font-bold text-white bg-[#3a4252] bg-clip-padding whitespace-nowrap" style={{ }}>Lot/Ord</th>}
                    {showNoSplit && <th className="text-center py-[1px] px-[6.5px] font-bold text-white bg-[#3a4252] bg-clip-padding" style={{ }}>BNon</th>}
                    {showNoSplit && <th className="text-center py-[1px] px-[6.5px] font-bold text-white bg-[#3a4252] bg-clip-padding whitespace-nowrap" style={{ }}>Lot/Non</th>}
                    <th className="text-center py-[1px] px-[7.4px] font-bold text-white bg-[#3a4252] whitespace-nowrap" style={{ }}>#</th>
                    <th className="text-center py-[1px] px-[6.5px] font-bold text-white" style={{ overflow: 'hidden', boxSizing: 'border-box' }}>SL</th>
                    <th className="text-center py-[1px] px-[6.5px] font-bold text-white" style={{ }}>SLot</th>
                    <th className="text-center py-[1px] px-[6.5px] font-bold text-white" style={{ }}>SVal</th>
                    {!showFrequency && !showOrder && !showNoSplit ? (
                      <th className="text-center py-[1px] px-[6.5px] font-bold text-white ${gRight}" style={{ }}>SAvg</th>
                    ) : (
                      <>
                        <th className="text-center py-[1px] px-[6.5px] font-bold text-white" style={{ }}>SAvg</th>
                        {showFrequency && <th className="text-center py-[1px] px-[6.5px] font-bold text-white" style={{ }}>SFreq</th>}
                        {showFrequency && <th className={`text-center py-[1px] px-[6.5px] font-bold text-white bg-[#3a4252] bg-clip-padding whitespace-nowrap ${!showOrder && !showNoSplit ? gRight : ''}`} style={{ }}>Lot/F</th>}
                        {showOrder && <th className="text-center py-[1px] px-[6.5px] font-bold text-white whitespace-nowrap" style={{ }}>SOrd</th>}
                        {showOrder && <th className={`text-center py-[1px] px-[6.5px] font-bold text-white bg-[#3a4252] bg-clip-padding whitespace-nowrap ${!showNoSplit ? gRight : ''}`} style={{ }}>Lot/Ord</th>}
                        {showNoSplit && <th className="text-center py-[1px] px-[6.5px] font-bold text-white" style={{ }}>SNon</th>}
                        {showNoSplit && <th className={`text-center py-[1px] px-[6.5px] font-bold text-white bg-[#3a4252] bg-clip-padding whitespace-nowrap ${gRight}`} style={{ }}>Lot/Non</th>}
                      </>
                    )}
                  
                      </React.Fragment>
                      );
                    })}</tr>
                </thead>
                <tbody>
                  {tableMaxRows === 0 || transactionData.size === 0 ? (
                    // If no data, show "No Data Available" message
                    <tr className="border-b-2 border-white" style={{ height: COLUMN_WIDTHS.ROW_HEIGHT }}>
                      <td
                        colSpan={totalCols}
                        className="text-center py-[2.06px] text-muted-foreground font-bold"
                        style={{ width: '100%', maxWidth: '100%', minWidth: 0 }}
                      >
                        No Data Available
                      </td>
                    </tr>
                  ) : rowIndices.length > 0 ? rowIndices.map((rowIdx: number) => {
                    return (
                      <tr key={rowIdx} style={{ height: COLUMN_WIDTHS.ROW_HEIGHT }}>
                        {!showOnlyTotal && !isMultiRangeDisplayed && visibleDates.map((date: string, dateIndex: number) => {
                          // Get Buy data at this row index for this date
                          // CRITICAL: Data is already filtered in loadTransactionData
                          const buyDataForDate = buyStocksByDate.get(date) || [];
                          const buyRowData = buyDataForDate[rowIdx];

                          // Get Sell data at this row index for this date
                          // CRITICAL: Data is already filtered in loadTransactionData
                          const sellDataForDate = sellStocksByDate.get(date) || [];
                          const sellRowData = sellDataForDate[rowIdx];

                          return (
                            <React.Fragment key={date}>
                              {/* Buyer Columns - from buyStocksByDate */}
                              {buyRowData ? (
                                <>
                                  {(() => {
                                    const dayData = buyRowData.data;
                                    // Use BLot directly from CSV (column 1), no fallback
                                    const buyerLot = dayData.BLot ?? 0;
                                    // Use BVal (BuyerValue) from CSV column 2
                                    const buyerVal = dayData.BuyerValue || 0;
                                    // Use BAvg from CSV column 3, or calculate from BLot and BVal if BAvg is 0/missing
                                    const buyerAvg = (dayData.BuyerAvg && dayData.BuyerAvg !== 0)
                                      ? dayData.BuyerAvg
                                      : (buyerLot > 0 ? buyerVal / (buyerLot * 100) : 0);

                                    // Use BFreq from CSV column 4
                                    const buyerFreq = dayData.BFreq || 0;
                                    // Use BLotPerFreq directly from CSV column 5, not calculated
                                    // const buyerLotPerFreq = dayData.BLotPerFreq !== undefined ? dayData.BLotPerFreq : (buyerFreq > 0 ? buyerLot / buyerFreq : 0);
                                    const buyerLotPerFreq = dayData.BLotPerFreq;
                                    // Use NewBuyerOrdNum for BOR display, fallback to BOrdNum if not available
                                    const buyerOrdNum = dayData.NewBuyerOrdNum !== undefined ? dayData.NewBuyerOrdNum : (dayData.BOrdNum || 0);
                                    // CRITICAL: For Broker pivot, use BLotPerOrdNum directly from CSV column 7, not calculated
                                    // This is the Lot/Or value for Buy section
                                    const buyerLotPerOrdNum = dayData.BLotPerOrdNum ?? 0;

                                    const bCode = dayData.BCode || buyRowData.stock;
                                    // For Stock pivot: BCode is a broker code, use getBrokerColorClass
                                    // For Broker pivot: BCode is a stock code, use getStockColorClass
                                    const bCodeColor = pivotFilter === 'Stock'
                                      ? getBrokerColorClass(bCode)
                                      : getStockColorClass(bCode);
                                    return (
                                      <>
                                        <td className={`text-center py-[1px] px-[7.4px] ${bCodeColor.className} ${dateIndex === 0 ? 'border-l-2 border-white' : ''}`} style={{ minWidth: '24px', maxWidth: '24px', color: bCodeColor.color, ...getTxBlockStyle(bCode), fontVariantNumeric: 'tabular-nums' }}>
                                          {bCode}
                                        </td>
                                        <td className="text-right py-[1px] px-[3.5px] text-[#00ff00]" style={{ ...getTxBlockStyle(bCode), fontVariantNumeric: 'tabular-nums' }}>{formatLot(buyerLot)}</td>
                                        <td className="text-right py-[1px] px-[5.5px] text-[#00ff00]" style={{ ...getTxBlockStyle(bCode), fontVariantNumeric: 'tabular-nums' }}>{formatValue(buyerVal)}</td>
                                        <td className="text-right py-[1px] px-[5.5px] text-[#00ff00]" style={{ ...getTxBlockStyle(bCode), fontVariantNumeric: 'tabular-nums' }}>{formatAverage(buyerAvg ?? 0)}</td>
                                        {showFrequency && <td className="text-right py-[1px] px-[3.5px] text-lime-400" style={{ ...getTxBlockStyle(bCode), fontVariantNumeric: 'tabular-nums' }}>{buyerFreq}</td>}
                                        {showFrequency && <td className="text-right py-[1px] px-[3.5px] text-lime-400" style={{ ...getTxBlockStyle(bCode), fontVariantNumeric: 'tabular-nums' }}>{formatLotPerFreqOrOrdNum(buyerLotPerFreq ?? 0)}</td>}
                                        {showOrder && <td className="text-right py-[1px] px-[3.5px] text-teal-400" style={{ ...getTxBlockStyle(bCode), fontVariantNumeric: 'tabular-nums' }}>{buyerOrdNum}</td>}
                                        {showOrder && <td className="text-right py-[1px] px-[3.5px] text-teal-400" style={{ ...getTxBlockStyle(bCode), fontVariantNumeric: 'tabular-nums' }}>{formatLotPerFreqOrOrdNum(buyerLotPerOrdNum ?? 0)}</td>}
                                        {showNoSplit && <td className="text-right py-[1px] px-[3.5px] text-green-300" style={{ ...getTxBlockStyle(bCode), fontVariantNumeric: 'tabular-nums' }}>{dayData.BNoSplit ? dayData.BNoSplit : '-'}</td>}
                                        {showNoSplit && <td className="text-right py-[1px] px-[3.5px] text-green-300" style={{ ...getTxBlockStyle(bCode), fontVariantNumeric: 'tabular-nums' }}>{dayData.BNoSplit ? formatLotPerFreqOrOrdNum(dayData.BLotPerNoSplit ?? 0) : '-'}</td>}
                                      </>
                                    );
                                  })()}
                                </>
                              ) : (
                                // Show empty cells if no Buy data at this row index
                                <>
                                  <td className={`text-center py-[1px] px-[7.4px] text-gray-400 ${dateIndex === 0 ? 'border-l-2 border-white' : ''}`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>
                                  <td className="text-right py-[1px] px-[3.5px] text-gray-400" style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>
                                  <td className="text-right py-[1px] px-[3.5px] text-gray-400" style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>
                                  <td className="text-right py-[1px] px-[3.5px] text-gray-400" style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>
                                  {showFrequency && <td className="text-right py-[1px] px-[3.5px] text-gray-400" style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                                  {showFrequency && <td className="text-right py-[1px] px-[3.5px] text-gray-400" style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                                  {showOrder && <td className="text-right py-[1px] px-[3.5px] text-gray-400" style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                                  {showOrder && <td className="text-right py-[1px] px-[3.5px] text-gray-400" style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                                  {showNoSplit && <td className="text-right py-[1px] px-[3.5px] text-gray-400" style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                                  {showNoSplit && <td className="text-right py-[1px] px-[3.5px] text-gray-400" style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                                </>
                              )}
                              {/* Separator */}
                              <td className="text-center py-[1px] px-[7.4px] text-white bg-[#3a4252] whitespace-nowrap" style={{ fontVariantNumeric: 'tabular-nums' }}>{rowIdx + 1}</td>
                              {/* Seller Columns - from sellStocksByDate */}
                              {sellRowData ? (
                                <>
                                  {(() => {
                                    const dayData = sellRowData.data;
                                    // Use SLot directly from CSV (column 9), no fallback
                                    const sellerLot = dayData.SLot ?? 0;
                                    // Use SVal (SellerValue) from CSV column 10
                                    const sellerVal = dayData.SellerValue || 0;
                                    // Use SAvg from CSV column 11, or calculate from SLot and SVal if SAvg is 0/missing
                                    const sellerAvg = (dayData.SellerAvg && dayData.SellerAvg !== 0)
                                      ? dayData.SellerAvg
                                      : (sellerLot > 0 ? sellerVal / (sellerLot * 100) : 0);
                                    // Use SFreq from CSV column 12
                                    const sellerFreq = dayData.SFreq || 0;
                                    // Use SLotPerFreq directly from CSV column 13, no fallback
                                    const sellerLotPerFreq = dayData.SLotPerFreq;
                                    // Use NewSellerOrdNum for SOR display, fallback to SOrdNum if not available
                                    const sellerOrdNum = dayData.NewSellerOrdNum !== undefined ? dayData.NewSellerOrdNum : (dayData.SOrdNum || 0);
                                    // CRITICAL: For Broker pivot, use SLotPerOrdNum directly from CSV column 15, not calculated
                                    // This is the Lot/Or value for Sell section
                                    const sellerLotPerOrdNum = dayData.SLotPerOrdNum ?? 0;

                                    // OPTIMIZED: Removed debug logging to improve performance

                                    const sCode = String(dayData.SCode || sellRowData.stock || '');
                                    // For Stock pivot: SCode is a broker code, use getBrokerColorClass
                                    // For Broker pivot: SCode is a stock code, use getStockColorClass
                                    const sCodeColor = pivotFilter === 'Stock'
                                      ? getBrokerColorClass(sCode)
                                      : getStockColorClass(sCode);
                                    return (
                                      <>
                                        <td className={`text-center py-[1px] px-[6.5px] ${sCodeColor.className}`} style={{ minWidth: '24px', maxWidth: '24px', color: sCodeColor.color, ...getTxBlockStyle(sCode), fontVariantNumeric: 'tabular-nums' }}>{sCode}</td>
                                        <td className="text-right py-[1px] px-[3.5px] text-[#ff0000]" style={{ ...getTxBlockStyle(sCode), fontVariantNumeric: 'tabular-nums' }}>{formatLot(sellerLot)}</td>
                                        <td className="text-right py-[1px] px-[5.5px] text-[#ff0000]" style={{ ...getTxBlockStyle(sCode), fontVariantNumeric: 'tabular-nums' }}>{formatValue(sellerVal)}</td>
                                        {!showFrequency && !showOrder && !showNoSplit ? (
                                          <td className={`text-right py-[1px] px-[5.5px] text-[#ff0000] ${dateIndex < selectedDates.length - 1 ? 'border-r-[10px] border-white' : ''} ${dateIndex === selectedDates.length - 1 ? 'border-r-[10px] border-white' : ''}`} style={{ ...getTxBlockStyle(sCode), fontVariantNumeric: 'tabular-nums' }}>{formatAverage(sellerAvg ?? 0)}</td>
                                        ) : (
                                          <>
                                            <td className="text-right py-[1px] px-[5.5px] text-[#ff0000]" style={{ ...getTxBlockStyle(sCode), fontVariantNumeric: 'tabular-nums' }}>{formatAverage(sellerAvg ?? 0)}</td>
                                            {showFrequency && <td className="text-right py-[1px] px-[3.5px] text-amber-500" style={{ ...getTxBlockStyle(sCode), fontVariantNumeric: 'tabular-nums' }}>{sellerFreq}</td>}
                                            {showFrequency && <td className={`text-right py-[1px] px-[3.5px] text-amber-500 ${!showOrder && !showNoSplit && dateIndex < selectedDates.length - 1 ? 'border-r-[10px] border-white' : ''} ${!showOrder && !showNoSplit && dateIndex === selectedDates.length - 1 ? 'border-r-[10px] border-white' : ''}`} style={{ ...getTxBlockStyle(sCode), fontVariantNumeric: 'tabular-nums' }}>{formatLotPerFreqOrOrdNum(sellerLotPerFreq ?? 0)}</td>}
                                            {showOrder && <td className="text-right py-[1px] px-[3.5px] text-[#e5c3d1]" style={{ ...getTxBlockStyle(sCode), fontVariantNumeric: 'tabular-nums' }}>{sellerOrdNum}</td>}
                                            {showOrder && <td className={`text-right py-[1px] px-[3.5px] text-[#e5c3d1] ${!showNoSplit && dateIndex < selectedDates.length - 1 ? 'border-r-[10px] border-white' : ''} ${!showNoSplit && dateIndex === selectedDates.length - 1 ? 'border-r-[10px] border-white' : ''}`} style={{ ...getTxBlockStyle(sCode), fontVariantNumeric: 'tabular-nums' }}>
                                              {formatLotPerFreqOrOrdNum(sellerLotPerOrdNum ?? 0)}
                                            </td>}
                                            {showNoSplit && <td className="text-right py-[1px] px-[3.5px] text-red-300" style={{ ...getTxBlockStyle(sCode), fontVariantNumeric: 'tabular-nums' }}>{dayData.SNoSplit ? dayData.SNoSplit : '-'}</td>}
                                            {showNoSplit && <td className={`text-right py-[1px] px-[3.5px] text-red-300 ${dateIndex < selectedDates.length - 1 ? 'border-r-[10px] border-white' : ''} ${dateIndex === selectedDates.length - 1 ? 'border-r-[10px] border-white' : ''}`} style={{ ...getTxBlockStyle(sCode), fontVariantNumeric: 'tabular-nums' }}>{dayData.SNoSplit ? formatLotPerFreqOrOrdNum(dayData.SLotPerNoSplit ?? 0) : '-'}</td>}
                                          </>
                                        )}
                                      </>
                                    );
                                  })()}
                                </>
                              ) : (
                                // Show empty cells if no Sell data at this row index
                                <>
                                  <td className="text-center py-[1px] px-[6.5px] text-gray-400" style={{ overflow: 'hidden', fontVariantNumeric: 'tabular-nums' }}>-</td>
                                  <td className="text-right py-[1px] px-[3.5px] text-gray-400" style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>
                                  <td className="text-right py-[1px] px-[3.5px] text-gray-400" style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>
                                  {!showFrequency && !showOrder && !showNoSplit ? (
                                    <td className={`text-right py-[1px] px-[3.5px] text-gray-400 ${dateIndex < selectedDates.length - 1 ? 'border-r-[10px] border-white' : ''} ${dateIndex === selectedDates.length - 1 ? 'border-r-[10px] border-white' : ''}`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>
                                  ) : (
                                    <>
                                      <td className="text-right py-[1px] px-[3.5px] text-gray-400" style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>
                                      {showFrequency && <td className="text-right py-[1px] px-[3.5px] text-gray-400" style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                                      {showFrequency && <td className={`text-right py-[1px] px-[3.5px] text-gray-400 ${!showOrder && !showNoSplit && dateIndex < selectedDates.length - 1 ? 'border-r-[10px] border-white' : ''} ${!showOrder && !showNoSplit && dateIndex === selectedDates.length - 1 ? 'border-r-[10px] border-white' : ''}`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                                      {showOrder && <td className="text-right py-[1px] px-[3.5px] text-gray-400" style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                                      {showOrder && <td className={`text-right py-[1px] px-[3.5px] text-gray-400 ${!showNoSplit && dateIndex < selectedDates.length - 1 ? 'border-r-[10px] border-white' : ''} ${!showNoSplit && dateIndex === selectedDates.length - 1 ? 'border-r-[10px] border-white' : ''}`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                                      {showNoSplit && <td className="text-right py-[1px] px-[3.5px] text-gray-400" style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                                      {showNoSplit && <td className={`text-right py-[1px] px-[3.5px] text-gray-400 ${dateIndex < selectedDates.length - 1 ? 'border-r-[10px] border-white' : ''} ${dateIndex === selectedDates.length - 1 ? 'border-r-[10px] border-white' : ''}`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                                    </>
                                  )}
                                </>
                              )}
                            </React.Fragment>
                          );
                        })}
                        {/* Total Column - aggregate Buy and Sell data separately by stock code */}
                        {isMultiRangeDisplayed ? (
                          <React.Fragment>
                            {rangeAggregates.map((agg, rIdx) => (
                              <React.Fragment key={`v-range-${rIdx}`}>
                                {renderValueTotalGroup(rowIdx, agg.buyList, agg.sellList, agg.buyMap, agg.sellMap, rIdx === 0 ? 'border-l-2 border-white' : 'border-l-[10px] border-white', 'border-r-[10px] border-white')}
                              </React.Fragment>
                            ))}
                            <React.Fragment key="v-grand">
                              {renderValueTotalGroup(rowIdx, sortedTotalBuyStocksMemo, sortedTotalSellStocksMemo, totalBuyDataByStock, totalSellDataByStock, 'border-l-[10px] border-white', 'border-r-2 border-white')}
                            </React.Fragment>
                          </React.Fragment>
                        ) : (
                          <React.Fragment>
                            {renderValueTotalGroup(rowIdx, sortedTotalBuyStocksMemo, sortedTotalSellStocksMemo, totalBuyDataByStock, totalSellDataByStock, showOnlyTotal || selectedDates.length === 0 ? 'border-l-2 border-white' : 'border-l-[10px] border-white', 'border-r-2 border-white')}
                          </React.Fragment>
                        )}
                      </tr>
                    );
                  }) : null}
                  {/* Lazy loading sentinel - triggers load more when scrolled into view */}
                  {visibleRowCount < maxRows && rowIndices.length > 0 && (
                    <tr>
                      <td colSpan={totalCols} ref={valueTableSentinelRef} className="h-10">
                        {/* Invisible sentinel for intersection observer */}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              {showAIPopup && renderAiStatsTable(aiValueStats, `Top ${aiValueStats.length} Value`)}
                <div aria-hidden="true" className="w-0 shrink-0 self-stretch border-l-2 border-white" />
              </div>
            </div>
          </div>
        </div>
      );
    };

    // NET Table - Shows Net Buy/Sell data
    const renderNetTable = () => {
      // Determine if we should show only Total column (when > 7 visible days selected)
      const showOnlyTotal = visibleDates.length > 30;

      // Helper function to get stock color class based on sector (same as Value table)
      // CRITICAL: This should always work regardless of ticker selection - coloring is independent of filtering
      const getStockColorClass = (stockCode: string): { color: string; className: string } => {
        if (!stockCode) return { color: '#FFFFFF', className: '' };
        const stockSector = stockToSectorMap[stockCode.toUpperCase()];
        if (!stockSector) return { color: '#FFFFFF', className: '' };

        // Color mapping based on sector - Using 11 distinct colors from provided palette
        // NO GREEN/RED to avoid conflict with Buy/Sell colors (same as Value table)
        // Colors: Royal Blue, Sky Blue, Deep Purple, Gold, Orange, Cyan, Brown, Slate Gray, Hot Pink, Khaki, White
        const sectorColors: { [sector: string]: string } = {
          'Financials': '#FFFFFF', // White
          'Energy': '#4169E1', // Royal Blue
          'Basic Materials': '#00BFFF', // Sky Blue
          'Consumer Cyclicals': '#800080', // Deep Purple
          'Consumer Non-Cyclicals': '#FFD700', // Gold
          'Healthcare': '#FF8C00', // Orange
          'Industrials': '#00FFFF', // Cyan
          'Infrastructures': '#8B4513', // Brown
          'Properties & Real Estate': '#708090', // Slate Gray
          'Technology': '#FF69B4', // Hot Pink
          'Transportation & Logistic': '#C3B091' // Khaki
        };

        const color = sectorColors[stockSector] || '#FFFFFF';
        return { color, className: '' };
      };

      // For NET table Total column: Net Buy/Net Sell per stock, aggregated across
      // selected dates. Sourced from allNetDataForTotalMemo, which both handles
      // the regular Buy-Sell case and the consolidated ALL/{SECTOR}_ALL case
      // (where Buy - Sell is always 0 and NetBuy*/NetSell* totals must be used directly).
      const { netBuy: netBuyStocksForTotal, netSell: netSellStocksForTotal } = allNetDataForTotalMemo;

      // HIGHLIGHTING LOGIC: Block filter (Default = auto top-5, Manual = pilihan user).
      // Warna & aturan sama dengan BrokerSummary; gaya visual tetap ala Transaction
      // (bg solid untuk net buy, garis bawah 4px untuk net sell).
      // Helper to get highlighting style
      const getRankStyle = (code: string): React.CSSProperties => {
        if (!code) return {};
        return getTxBlockStyle(code) ?? {};
      };

      // Renderer satu grup kolom NET agregat (Total / per-range) — dipakai ulang
      // untuk tiap range tanpa duplikasi. Bentuk item sama dengan allNetDataForTotalMemo.
      const renderNetTotalGroup = (
        rowIdx: number,
        netBuyList: Array<{ stock: string; netValue: number; netLot: number; netAvg: number; netFreq: number; netOrdNum: number; buyLotPerFreq: number; buyLotPerOrdNum: number; sellLotPerFreq: number; sellLotPerOrdNum: number; netBuyNoSplit?: number; netBuyLotPerNoSplit?: number; netSellNoSplit?: number; netSellLotPerNoSplit?: number }>,
        netSellList: Array<{ stock: string; netValue: number; netLot: number; netAvg: number; netFreq: number; netOrdNum: number; buyLotPerFreq: number; buyLotPerOrdNum: number; sellLotPerFreq: number; sellLotPerOrdNum: number; netBuyNoSplit?: number; netBuyLotPerNoSplit?: number; netSellNoSplit?: number; netSellLotPerNoSplit?: number }>,
        leftCls: string,
        rightCls: string
      ) => {
        // Get stock at this row index for Buy side and Sell side separately
        const buyStockData = netBuyList[rowIdx] || null;
        const sellStockData = netSellList[rowIdx] || null;

        // If both are empty, hide row
        if (!buyStockData && !sellStockData) {
          return (
            <td className={`text-center py-[1px] px-[6.5px] text-gray-400 ${leftCls}`} colSpan={netColsPerDate}>
              -
            </td>
          );
        }

        // Prepare Buy side data
        const buyStockCode = buyStockData?.stock || '';
        const buyNetValue = buyStockData?.netValue || 0;
        const buyNetLot = buyStockData?.netLot || 0;
        const buyNetAvg = buyStockData?.netAvg || 0;
        const buyNetFreq = buyStockData?.netFreq || 0;
        const buyNetOrdNum = buyStockData?.netOrdNum || 0;
        // FIXED: Use Buy Lot/F and Buy Lot/Or from B/S data
        const buyNetLotPerFreq = buyStockData?.buyLotPerFreq || 0;
        const buyNetLotPerOrdNum = buyStockData?.buyLotPerOrdNum || 0;

        // Prepare Sell side data
        const sellStockCode = sellStockData?.stock || '';
        const sellNetValue = sellStockData?.netValue || 0;
        const sellNetLot = sellStockData?.netLot || 0;
        const sellNetAvg = sellStockData?.netAvg || 0;
        const sellNetFreq = sellStockData?.netFreq || 0;
        const sellNetOrdNum = sellStockData?.netOrdNum || 0;
        // FIXED: Use Sell Lot/F and Sell Lot/Or from B/S data
        const sellNetLotPerFreq = sellStockData?.sellLotPerFreq || 0;
        const sellNetLotPerOrdNum = sellStockData?.sellLotPerOrdNum || 0;
        const buyNoSplitTotal = buyStockData?.netBuyNoSplit ?? 0;
        const buyLotPerNSTotal = buyStockData?.netBuyLotPerNoSplit ?? 0;
        const sellNoSplitTotal = sellStockData?.netSellNoSplit ?? 0;
        const sellLotPerNSTotal = sellStockData?.netSellLotPerNoSplit ?? 0;

        // Get color classes for codes based on pivot type
        // For Stock pivot: NBCode/NSCode are broker codes, use getBrokerColorClass
        // For Broker pivot: NBCode/NSCode are stock codes, use getStockColorClass
        const buyCodeColor = buyStockCode !== ''
          ? (pivotFilter === 'Stock'
            ? getBrokerColorClass(buyStockCode)
            : getStockColorClass(buyStockCode))
          : { color: '#FFFFFF', className: '' };

        const sellCodeColor = sellStockCode !== ''
          ? (pivotFilter === 'Stock'
            ? getBrokerColorClass(sellStockCode)
            : getStockColorClass(sellStockCode))
          : { color: '#FFFFFF', className: '' };

        // Color for values: Net Buy values are green, Net Sell values are red
        const totalNetBuyColor = 'text-[#00ff00]';
        const totalNetSellColor = 'text-[#ff0000]';
        // Color for frequency columns: Net Buy frequency is neon green, Net Sell frequency is bright pink
        const totalNetBuyFreqColor = 'text-lime-400';
        const totalNetSellFreqColor = 'text-amber-500';
        // Color for order columns: Net Buy order is teal (more contrast than green-600), Net Sell order is rose (more contrast than red-600)
        const totalNetBuyOrderColor = 'text-teal-400';
        const totalNetSellOrderColor = 'text-[#e5c3d1]';

        return (
          <React.Fragment>
            {/* Net Buy Total Columns - Show if we have Buy stock at this row */}
            {buyStockData && Math.abs(buyNetLot) > 0 ? (
              <>
                <td className={`text-center py-[1px] px-[6.5px] ${buyCodeColor.className} ${leftCls}`} style={{ minWidth: '24px', maxWidth: '24px', color: buyCodeColor.color, fontVariantNumeric: 'tabular-nums', ...getRankStyle(buyStockCode) }}>
                  {buyStockCode}
                </td>
                <td className={`text-right py-[1px] px-[3.5px] ${totalNetBuyColor}`} style={{ fontVariantNumeric: 'tabular-nums', ...getRankStyle(buyStockCode) }}>
                  {formatLot(buyNetLot)}
                </td>
                <td className={`text-right py-[1px] px-[5.5px] ${totalNetBuyColor}`} style={{ fontVariantNumeric: 'tabular-nums', ...getRankStyle(buyStockCode) }}>
                  {formatValue(buyNetValue)}
                </td>
                <td className={`text-right py-[1px] px-[5.5px] ${totalNetBuyColor}`} style={{ fontVariantNumeric: 'tabular-nums', ...getRankStyle(buyStockCode) }}>
                  {formatAverage(buyNetAvg)}
                </td>
                {showFrequency && <td className={`text-right py-[1px] px-[5.5px] ${totalNetBuyFreqColor}`} style={{ fontVariantNumeric: 'tabular-nums', ...getRankStyle(buyStockCode) }}>
                  {buyNetFreq}
                </td>}
                {showFrequency && <td className={`text-right py-[1px] px-[3.5px] ${totalNetBuyFreqColor}`} style={{ fontVariantNumeric: 'tabular-nums', ...getRankStyle(buyStockCode) }}>
                  {formatLotPerFreqOrOrdNum(buyNetLotPerFreq)}
                </td>}
                {showOrder && <td className={`text-right py-[1px] px-[3.5px] ${totalNetBuyOrderColor}`} style={{ fontVariantNumeric: 'tabular-nums', ...getRankStyle(buyStockCode) }}>
                  {buyNetOrdNum}
                </td>}
                {showOrder && <td className={`text-right py-[1px] px-[3.5px] ${totalNetBuyOrderColor}`} style={{ fontVariantNumeric: 'tabular-nums', ...getRankStyle(buyStockCode) }}>
                  {formatLotPerFreqOrOrdNum(buyNetLotPerOrdNum)}
                </td>}
                {showNoSplit && <td className={`text-right py-[1px] px-[5.5px] ${totalNetBuyFreqColor}`} style={{ fontVariantNumeric: 'tabular-nums', ...getRankStyle(buyStockCode) }}>
                  {buyNoSplitTotal ?? 0}
                </td>}
                {showNoSplit && <td className={`text-right py-[1px] px-[3.5px] ${totalNetBuyFreqColor}`} style={{ fontVariantNumeric: 'tabular-nums', ...getRankStyle(buyStockCode) }}>
                  {formatLotPerFreqOrOrdNum(buyLotPerNSTotal ?? 0)}
                </td>}
              </>
            ) : (
              <>
                <td className={`text-center py-[1px] px-[6.5px] text-gray-400 ${leftCls}`} style={{ overflow: 'hidden', fontVariantNumeric: 'tabular-nums' }}>-</td>
                <td className={`text-right py-[1px] px-[3.5px] text-gray-400`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>
                <td className={`text-right py-[1px] px-[3.5px] text-gray-400`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>
                {!showFrequency && !showOrder && !showNoSplit ? (
                  <td className={`text-right py-[1px] px-[3.5px] text-gray-400 border-r-2 border-white`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>
                ) : (
                  <>
                    <td className={`text-right py-[1px] px-[3.5px] text-gray-400`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>
                    {showFrequency && <td className={`text-right py-[1px] px-[3.5px] text-gray-400`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                    {showFrequency && <td className={`text-right py-[1px] px-[3.5px] text-gray-400 ${!showOrder ? 'border-r-2 border-white' : ''}`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                    {showOrder && <td className={`text-right py-[1px] px-[3.5px] text-gray-400`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                    {showOrder && <td className={`text-right py-[1px] px-[3.5px] text-gray-400 border-r-2 border-white`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                    {showNoSplit && <td className={`text-right py-[1px] px-[3.5px] text-gray-400`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                    {showNoSplit && <td className={`text-right py-[1px] px-[3.5px] text-gray-400`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                  </>
                )}
              </>
            )}
            {/* Separator */}
            <td className="text-center py-[1px] px-[7.4px] text-white bg-[#3a4252]">{rowIdx + 1}</td>
            {/* Net Sell Total Columns - Show if we have Sell stock at this row */}
            {sellStockData && Math.abs(sellNetLot) > 0 ? (
              <>
                <td className={`text-center py-[1px] px-[6.5px] ${sellCodeColor.className}`} style={{ minWidth: '24px', maxWidth: '24px', color: sellCodeColor.color, fontVariantNumeric: 'tabular-nums', ...getRankStyle(sellStockCode) }}>
                  {sellStockCode}
                </td>
                <td className={`text-right py-[1px] px-[3.5px] ${totalNetSellColor}`} style={{ fontVariantNumeric: 'tabular-nums', ...getRankStyle(sellStockCode) }}>
                  {formatLot(-sellNetLot)}
                </td>
                <td className={`text-right py-[1px] px-[5.5px] ${totalNetSellColor}`} style={{ fontVariantNumeric: 'tabular-nums', ...getRankStyle(sellStockCode) }}>
                  {formatValue(-sellNetValue)}
                </td>
                {!showFrequency && !showOrder && !showNoSplit ? (
                  <td className={`text-right py-[1px] px-[5.5px] ${totalNetSellColor} ${rightCls}`} style={{ fontVariantNumeric: 'tabular-nums', ...getRankStyle(sellStockCode) }}>
                    {formatAverage(Math.abs(sellNetAvg))}
                  </td>
                ) : (
                  <>
                    <td className={`text-right py-[1px] px-[5.5px] ${totalNetSellColor}`} style={{ fontVariantNumeric: 'tabular-nums', ...getRankStyle(sellStockCode) }}>
                      {formatAverage(Math.abs(sellNetAvg))}
                    </td>
                    {showFrequency && <td className={`text-right py-[1px] px-[5.5px] ${totalNetSellFreqColor}`} style={{ fontVariantNumeric: 'tabular-nums', ...getRankStyle(sellStockCode) }}>
                      {-sellNetFreq}
                    </td>}
                    {showFrequency && <td className={`text-right py-[1px] px-[3.5px] ${totalNetSellFreqColor} ${!showOrder && !showNoSplit ? rightCls : ''}`} style={{ fontVariantNumeric: 'tabular-nums', ...getRankStyle(sellStockCode) }}>
                      {formatLotPerFreqOrOrdNum(sellNetLotPerFreq)}
                    </td>}
                    {showOrder && <td className={`text-right py-[1px] px-[3.5px] ${totalNetSellOrderColor}`} style={{ fontVariantNumeric: 'tabular-nums', ...getRankStyle(sellStockCode) }}>
                      {-sellNetOrdNum}
                    </td>}
                    {showOrder && <td className={`text-right py-[1px] px-[3.5px] ${totalNetSellOrderColor} ${!showNoSplit ? rightCls : ''}`} style={{ fontVariantNumeric: 'tabular-nums', ...getRankStyle(sellStockCode) }}>
                      {formatLotPerFreqOrOrdNum(sellNetLotPerOrdNum)}
                    </td>}
                    {showNoSplit && <td className={`text-right py-[1px] px-[5.5px] ${totalNetSellFreqColor}`} style={{ fontVariantNumeric: 'tabular-nums', ...getRankStyle(sellStockCode) }}>
                      {sellNoSplitTotal ?? 0}
                    </td>}
                    {showNoSplit && <td className={`text-right py-[1px] px-[3.5px] ${totalNetSellFreqColor} ${rightCls}`} style={{ fontVariantNumeric: 'tabular-nums', ...getRankStyle(sellStockCode) }}>
                      {formatLotPerFreqOrOrdNum(sellLotPerNSTotal ?? 0)}
                    </td>}
                  </>
                )}
              </>
            ) : (
              <>
                <td className={`text-center py-[1px] px-[6.5px] text-gray-400`} style={{ overflow: 'hidden', boxSizing: 'border-box', fontVariantNumeric: 'tabular-nums' }}>-</td>
                <td className="text-right py-[1px] px-[3.5px] text-gray-400" style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>
                <td className="text-right py-[1px] px-[3.5px] text-gray-400" style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>
                {!showFrequency && !showOrder && !showNoSplit ? (
                  <td className={`text-right py-[1px] px-[3.5px] text-gray-400 ${rightCls}`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>
                ) : (
                  <>
                    <td className="text-right py-[1px] px-[3.5px] text-gray-400" style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>
                    {showFrequency && <td className="text-right py-[1px] px-[3.5px] text-gray-400" style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                    {showFrequency && <td className={`text-right py-[1px] px-[3.5px] text-gray-400 ${!showOrder && !showNoSplit ? rightCls : ''}`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                    {showOrder && <td className="text-right py-[1px] px-[3.5px] text-gray-400" style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                    {showOrder && <td className={`text-right py-[1px] px-[3.5px] text-gray-400 ${!showNoSplit ? rightCls : ''}`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                    {showNoSplit && <td className={`text-right py-[1px] px-[3.5px] text-gray-400`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                    {showNoSplit && <td className={`text-right py-[1px] px-[3.5px] text-gray-400 ${rightCls}`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                  </>
                )}
              </>
            )}
          </React.Fragment>
        );
      };

      // For Total column, use the max of Buy and Sell stocks count
      const maxNetTotalRows = Math.max(netBuyStocksForTotal.length, netSellStocksForTotal.length);

      // For backward compatibility, keep sortedTotalNetBuyStocks and sortedTotalNetSellStocks for per-date rendering
      const sortedTotalNetBuyStocks = Array.from(totalNetBuyDataByStock.entries())
        .filter(([, data]) => Math.abs(data.netBuyLot || 0) > 0)
        .sort((a, b) => (b[1].netBuyLot || 0) - (a[1].netBuyLot || 0))
        .map(([stock]) => stock);

      const sortedTotalNetSellStocks = Array.from(totalNetSellDataByStock.entries())
        .filter(([, data]) => Math.abs(data.netSellLot || 0) > 0)
        .sort((a, b) => (b[1].netSellLot || 0) - (a[1].netSellLot || 0))
        .map(([stock]) => stock);

      let maxNetBuyRows = sortedTotalNetBuyStocks.length;
      let maxNetSellRows = sortedTotalNetSellStocks.length;

      // Multi-range: baris mengikuti grup range terpanjang + grand total
      if (isMultiRangeDisplayed) {
        rangeAggregates.forEach(agg => {
          maxNetBuyRows = Math.max(maxNetBuyRows, agg.netBuyList.length);
          maxNetSellRows = Math.max(maxNetSellRows, agg.netSellList.length);
        });
      }

      if (!showOnlyTotal && !isMultiRangeDisplayed) {
        // When showing per-date, also check visible date rows to ensure we show all data
        visibleDates.forEach(date => {
          const netBuyData = netBuyStocksByDate.get(date) || [];
          const netSellData = netSellStocksByDate.get(date) || [];
          maxNetBuyRows = Math.max(maxNetBuyRows, netBuyData.length);
          maxNetSellRows = Math.max(maxNetSellRows, netSellData.length);
        });
      }
      // For Total column, use maxNetTotalRows; for per-date, use max of Buy and Sell rows
      const tableMaxRows = showOnlyTotal ? maxNetTotalRows : Math.max(maxNetBuyRows, maxNetSellRows);

      // Respect lazy-loading limit: cap to visibleRowIndices, but don't exceed tableMaxRows
      const rowIndices = tableMaxRows === 0
        ? []
        : visibleRowIndices.length > tableMaxRows
          ? visibleRowIndices.slice(0, tableMaxRows)
          : visibleRowIndices;

      // Calculate total columns for "No Data Available" message (NET table)
      // Base columns: BCode/Broker, BVal, BLot, BAvg, #, SCode/Stock, SVal, SLot, SAvg = 9
      const netBaseCols = 9;
      const netOptionalCols = (showFrequency ? 4 : 0) + (showOrder ? 4 : 0) + (showNoSplit ? 4 : 0);
      const netColsPerDate = netBaseCols + netOptionalCols;
      const netTotalCols = showOnlyTotal && !isMultiRangeDisplayed ? netColsPerDate : (isMultiRangeDisplayed ? (rangeAggregates.length + 1) * netColsPerDate : (visibleDates.length * netColsPerDate + netColsPerDate));

      // Footer summary ala BrokerSummary lama: satu set TVal/FNVal/TLot/Avg per grup
      // (tidak dibagi dua buy/sell). TVal=(buy+sell)/2, TLot=(lot buy+lot sell)/2 (Lot),
      // Net=foreign-investor net dari fetch khusus inv=F (fallback: net kode broker asing).
      const sumNetLabel = appliedInvFilter === 'D' ? 'DNVal' : 'FNVal';
      const sumNetLegs = (rows: BrokerTransactionData[]) => {
        let v = 0;
        rows.forEach(r => {
          v += (Number((r as any).NBVal ?? (r as any).NetBuyValue) || 0) - (Number((r as any).NSVal ?? (r as any).NetSellValue) || 0);
        });
        return v;
      };
      const computeTxFooterSummary = (dates: string[]) => {
        let buyVal = 0, sellVal = 0, buyLot = 0, sellLot = 0;
        dates.forEach(date => {
          const s = dateSummariesMemo.get(date);
          if (!s) return;
          buyVal += s.buyTotalValue; sellVal += s.sellTotalValue;
          buyLot += s.buyTotalLot; sellLot += s.sellTotalLot;
        });
        const totalValue = (buyVal + sellVal) / 2;
        const totalLot = (buyLot + sellLot) / 2;
        let netValue: number | null;
        if (appliedInvFilter === 'D') {
          // Data utama sudah domestic-only: net langsung dari kaki net-nya
          let nv = 0;
          dates.forEach(date => {
            nv += sumNetLegs(transactionData.get(date) || []);
          });
          netValue = nv;
        } else if (appliedInvFilter === 'F') {
          // Data utama sudah foreign-only: net langsung dari kaki net-nya
          let nv = 0;
          dates.forEach(date => {
            nv += sumNetLegs(transactionData.get(date) || []);
          });
          netValue = nv;
        } else {
          // All: HANYA dari fetch khusus investor asing (inv=F).
          // Tidak ada fallback kode broker — mending kosong daripada salah.
          let fNet = 0;
          let hasForeignData = false;
          dates.forEach(date => {
            const fr = foreignTxByDate.get(date);
            if (fr && fr.length > 0) {
              hasForeignData = true;
              fNet += sumNetLegs(fr);
            }
          });
          netValue = hasForeignData ? fNet : null;
        }
        return { totalValue, netValue, totalLot, avgPrice: totalLot > 0 ? totalValue / (totalLot * 100) : 0 };
      };
      // Distribusi span kolom footer 2-2-3-2 ala Summary, diskala ke lebar grup
      const sumSpanT = Math.round(netColsPerDate * 2 / 9);
      const sumSpanN = Math.round(netColsPerDate * 2 / 9);
      const sumSpanL = Math.round(netColsPerDate * 3 / 9);
      const sumSpanA = netColsPerDate - sumSpanT - sumSpanN - sumSpanL;


      return (
        <div className="w-full flex-1 flex flex-col min-h-0 mt-1">
          <div className="bg-muted/50 px-4 py-1.5 border-y border-border flex items-center justify-between">
            <h3 className="font-semibold text-sm">NET - {selectedTickers.length > 0 ? selectedTickers.join(', ') : ''} - {boardFilter || 'All'}</h3>
          </div>
          <div className={`${(showOnlyTotal && !isMultiRangeDisplayed) ? 'flex justify-center' : 'w-full max-w-full'} flex-1 min-h-0`}>
            <div ref={netTableContainerRef} className={`${(showOnlyTotal && !isMultiRangeDisplayed) ? 'w-auto' : 'w-full max-w-full'} overflow-x-auto overflow-y-auto border-l-2 border-r-2 border-b-2 border-white h-full`} style={{ scrollBehavior: 'auto' }}>
              <div className="flex items-stretch w-max min-w-full">
              <table ref={netTableRef} className={`${showOnlyTotal ? 'min-w-0' : 'min-w-0'} ${getFontSizeClass()} border-collapse leading-tight`} style={{ borderSpacing: 0, tableLayout: 'fixed', width: 'max-content' }}>
                {renderTxColGroup(isMultiRangeDisplayed ? rangeAggregates.length + 1 : (showOnlyTotal ? 1 : visibleDates.length + 1), 'n')}

                <thead className="bg-[#3a4252] sticky top-0 z-20">
                  {/* Date Header Row */}
                  <tr className="border-t-2 border-white">
                    {!showOnlyTotal && !isMultiRangeDisplayed && visibleDates.map((date, dateIndex) => (
                      <th
                        key={date}
                        className={`relative text-center py-[1px] px-[7.4px] font-bold text-white whitespace-nowrap ${dateIndex === 0 ? 'border-l-2 border-white' : 'before:absolute before:left-[-5px] before:top-0 before:bottom-0 before:w-[10px] before:bg-white before:content-[""]'}`}
                        colSpan={netColsPerDate}
                      >
                        {dateIndex !== 0 && (
                          <span aria-hidden="true" style={{ position: 'absolute', left: -5, top: 0, bottom: 0, width: 10, backgroundColor: '#fff' }} />
                        )}
                        {formatDisplayDate(date)}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!canUseAiFeatures) {
                              showToast({ type: 'warning', title: 'Menu khusus bootcamp' });
                              return;
                            }
                            setAiDateOverlay(prev => prev === date ? null : date);
                          }}
                          className={`absolute right-1 top-1/2 -translate-y-1/2 px-1 h-[18px] rounded-[4px] text-[10px] font-normal border flex items-center ${aiDateOverlay === date ? 'border-indigo-400 text-white bg-indigo-500/40' : 'border-white text-white hover:bg-white/10 bg-[#3a4252]/60'}`}
                          title="AI tanggal ini — menu khusus bootcamp"
                        >
                          <span className="inline-flex items-end leading-none rounded-[3px] bg-purple-500/30 px-0.5"><span className="font-bold bg-gradient-to-r from-[#00ff00] to-[#ff0000] bg-clip-text text-transparent drop-shadow-[0_0_4px_rgba(168,85,247,0.9)]">Ai</span></span>
                        </button>
                        {aiDateOverlay === date && (
                          <div ref={(el) => { if (el) el.scrollIntoView({ block: 'nearest', inline: 'nearest' }); }} className="absolute left-full top-0 z-50 ml-1 max-h-[70vh] overflow-y-auto font-normal" onClick={(e) => e.stopPropagation()}>
                            {renderAiStatsTable(aiDateStats, formatDisplayDate(date), () => setAiDateOverlay(null))}
                          </div>
                        )}
                      </th>
                    ))}
                    {isMultiRangeDisplayed && (
                      <>
                        {displayedSortedRangeLabels.map((label, rIdx) => (
                          <th key={`n-hdr-${rIdx}`} className={`relative text-center py-[1px] px-[7.4px] font-bold text-white whitespace-nowrap ${rIdx === 0 ? 'border-l-2 border-white' : 'before:absolute before:left-[-5px] before:top-0 before:bottom-0 before:w-[10px] before:bg-white before:content-[""]'} border-r-[10px] border-white`} colSpan={netColsPerDate}>
                            {rIdx !== 0 && (
                              <span aria-hidden="true" style={{ position: 'absolute', left: -5, top: 0, bottom: 0, width: 10, backgroundColor: '#fff' }} />
                            )}
                            {label}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!canUseAiFeatures) {
                                  showToast({ type: 'warning', title: 'Menu khusus bootcamp' });
                                  return;
                                }
                                const k = `range:${rIdx}`;
                                setAiDateOverlay(prev => prev === k ? null : k);
                              }}
                              className={`absolute right-1 top-1/2 -translate-y-1/2 px-1 h-[18px] rounded-[4px] text-[10px] font-normal border flex items-center ${aiDateOverlay === `range:${rIdx}` ? 'border-indigo-400 text-white bg-indigo-500/40' : 'border-white text-white hover:bg-white/10 bg-[#3a4252]/60'}`}
                              title="AI range ini — menu khusus bootcamp"
                            >
                              <span className="inline-flex items-end leading-none rounded-[3px] bg-purple-500/30 px-0.5"><span className="font-bold bg-gradient-to-r from-[#00ff00] to-[#ff0000] bg-clip-text text-transparent drop-shadow-[0_0_4px_rgba(168,85,247,0.9)]">Ai</span></span>
                            </button>
                            {aiDateOverlay === `range:${rIdx}` && (
                              <div ref={(el) => { if (el) el.scrollIntoView({ block: 'nearest', inline: 'nearest' }); }} className="absolute left-full top-0 z-50 ml-1 max-h-[70vh] overflow-y-auto font-normal" onClick={(e) => e.stopPropagation()}>
                                {renderAiStatsTable(aiDateStats, label, () => setAiDateOverlay(null))}
                              </div>
                            )}
                          </th>
                        ))}
                      </>
                    )}
                    <th className={`relative text-center py-[1px] px-[3.8px] font-bold text-white border-r-2 border-white ${(showOnlyTotal || visibleDates.length === 0) && !isMultiRangeDisplayed ? 'border-l-2 border-white' : 'before:absolute before:left-[-5px] before:top-0 before:bottom-0 before:w-[10px] before:bg-white before:content-[""]'}`}                         colSpan={netColsPerDate}>
                      {((showOnlyTotal || visibleDates.length === 0) && !isMultiRangeDisplayed) ? null : (
                        <span aria-hidden="true" style={{ position: 'absolute', left: -5, top: 0, bottom: 0, width: 10, backgroundColor: '#fff' }} />
                      )}
                      Total
                    </th>
                  </tr>
                  {/* Column Header Row */}
                  <tr className="bg-[#3a4252]">
                    {!showOnlyTotal && !isMultiRangeDisplayed && visibleDates.map((date, dateIndex) => (
                      <React.Fragment key={date}>
                        {/* Net Buy Columns (from CSV columns 17-23) */}
                        <th className={`relative text-center py-[1px] px-[7.4px] font-bold text-white ${dateIndex === 0 ? 'border-l-2 border-white' : 'before:absolute before:left-[-5px] before:top-0 before:bottom-0 before:w-[10px] before:bg-white before:content-[""]'}`} title={formatDisplayDate(date)}>{dateIndex !== 0 && (<span aria-hidden="true" style={{ position: 'absolute', left: -5, top: 0, bottom: 0, width: 10, backgroundColor: '#fff' }} />)}BY</th>
                        <th className="text-center py-[1px] px-[7.4px] font-bold text-white" title={formatDisplayDate(date)} style={{ }}>BLot</th>
                        <th className="text-center py-[1px] px-[7.4px] font-bold text-white" title={formatDisplayDate(date)} style={{ }}>BVal</th>
                        <th className="text-center py-[1px] px-[7.4px] font-bold text-white" title={formatDisplayDate(date)} style={{ }}>BAvg</th>
                        {showFrequency && <th className="text-center py-[1px] px-[7.4px] font-bold text-white bg-[#3a4252] bg-clip-padding" title={formatDisplayDate(date)} style={{ }}>BFreq</th>}
                        {showFrequency && <th className="text-center py-[1px] px-[7.4px] font-bold text-white bg-[#3a4252] bg-clip-padding whitespace-nowrap" title={formatDisplayDate(date)} style={{ }}>Lot/F</th>}
                        {showOrder && <th className="text-center py-[1px] px-[7.4px] font-bold text-white bg-[#3a4252] bg-clip-padding whitespace-nowrap" title={formatDisplayDate(date)} style={{ }}>BOrd</th>}
                        {showOrder && <th className="text-center py-[1px] px-[7.4px] font-bold text-white bg-[#3a4252] bg-clip-padding whitespace-nowrap" title={formatDisplayDate(date)} style={{ }}>Lot/Ord</th>}
                        {showNoSplit && <th className="text-center py-[1px] px-[7.4px] font-bold text-white bg-[#3a4252] bg-clip-padding" title={formatDisplayDate(date)} style={{ }}>BNon</th>}
                        {showNoSplit && <th className="text-center py-[1px] px-[7.4px] font-bold text-white bg-[#3a4252] bg-clip-padding whitespace-nowrap" title={formatDisplayDate(date)} style={{ }}>Lot/Non</th>}
                        {/* Separator */}
                        <th className="text-center py-[1px] px-[7.4px] font-bold text-white bg-[#3a4252] whitespace-nowrap" title={formatDisplayDate(date)} style={{ }}>#</th>
                        {/* Net Sell Columns (from CSV columns 24-30) */}
                        <th className="text-center py-[1px] px-[7.4px] font-bold text-white" title={formatDisplayDate(date)} style={{ overflow: 'hidden' }}>SL</th>
                        <th className="text-center py-[1px] px-[7.4px] font-bold text-white" title={formatDisplayDate(date)} style={{ }}>SLot</th>
                        <th className="text-center py-[1px] px-[7.4px] font-bold text-white" title={formatDisplayDate(date)} style={{ }}>SVal</th>
                        {!showFrequency && !showOrder && !showNoSplit ? (
                          <th className={`text-center py-[1px] px-[7.4px] font-bold text-white`} title={formatDisplayDate(date)} style={{ }}>SAvg</th>
                        ) : (
                          <>
                            <th className="text-center py-[1px] px-[7.4px] font-bold text-white" title={formatDisplayDate(date)} style={{ }}>SAvg</th>
                            {showFrequency && <th className="text-center py-[1px] px-[7.4px] font-bold text-white" title={formatDisplayDate(date)} style={{ }}>SFreq</th>}
                            {showFrequency && <th className={`text-center py-[1px] px-[7.4px] font-bold text-white bg-[#3a4252] bg-clip-padding whitespace-nowrap`} title={formatDisplayDate(date)} style={{ }}>Lot/F</th>}
                            {showOrder && <th className="text-center py-[1px] px-[7.4px] font-bold text-white whitespace-nowrap" title={formatDisplayDate(date)} style={{ }}>SOrd</th>}
                            {showOrder && <th className={`text-center py-[1px] px-[7.4px] font-bold text-white bg-[#3a4252] bg-clip-padding whitespace-nowrap`} title={formatDisplayDate(date)} style={{ }}>Lot/Ord</th>}
                            {showNoSplit && <th className="text-center py-[1px] px-[7.4px] font-bold text-white" title={formatDisplayDate(date)} style={{ }}>SNon</th>}
                            {showNoSplit && <th className={`text-center py-[1px] px-[7.4px] font-bold text-white whitespace-nowrap bg-[#3a4252] bg-clip-padding`} title={formatDisplayDate(date)} style={{ }}>Lot/Non</th>}
                          </>
                        )}
                      </React.Fragment>
                    ))}
                    {/* Total Columns (diulang per grup kolom saat multi-range) */}
                    {(isMultiRangeDisplayed ? [...Array(rangeAggregates.length + 1).keys()] : [-1]).map((gg) => {
                      const gIsLeftmost = (!isMultiRangeDisplayed && (showOnlyTotal || visibleDates.length === 0)) || (isMultiRangeDisplayed && gg === 0);
                      const gRight = (!isMultiRangeDisplayed || gg === rangeAggregates.length) ? 'border-r-2 border-white' : '';
                      return (
                      <React.Fragment key={gg}>
                    <th className={`relative text-center py-[1px] px-[6.5px] font-bold text-white ${gIsLeftmost ? 'border-l-2 border-white' : 'before:absolute before:left-[-5px] before:top-0 before:bottom-0 before:w-[10px] before:bg-white before:content-[""]'}`}>{!gIsLeftmost && (<span aria-hidden="true" style={{ position: 'absolute', left: -5, top: 0, bottom: 0, width: 10, backgroundColor: '#fff' }} />)}BY</th>
                    <th className="text-center py-[1px] px-[6.5px] font-bold text-white" style={{ }}>BLot</th>
                    <th className="text-center py-[1px] px-[6.5px] font-bold text-white" style={{ }}>BVal</th>
                    <th className="text-center py-[1px] px-[6.5px] font-bold text-white" style={{ }}>BAvg</th>
                    {showFrequency && <th className="text-center py-[1px] px-[6.5px] font-bold text-white bg-[#3a4252] bg-clip-padding" style={{ }}>BFreq</th>}
                    {showFrequency && <th className="text-center py-[1px] px-[6.5px] font-bold text-white bg-[#3a4252] bg-clip-padding whitespace-nowrap" style={{ }}>Lot/F</th>}
                    {showOrder && <th className="text-center py-[1px] px-[6.5px] font-bold text-white bg-[#3a4252] bg-clip-padding whitespace-nowrap" style={{ }}>BOrd</th>}
                    {showOrder && <th className="text-center py-[1px] px-[6.5px] font-bold text-white bg-[#3a4252] bg-clip-padding whitespace-nowrap" style={{ }}>Lot/Ord</th>}
                    {showNoSplit && <th className="text-center py-[1px] px-[6.5px] font-bold text-white bg-[#3a4252] bg-clip-padding" style={{ }}>BNon</th>}
                    {showNoSplit && <th className="text-center py-[1px] px-[6.5px] font-bold text-white bg-[#3a4252] bg-clip-padding whitespace-nowrap" style={{ }}>Lot/Non</th>}
                    <th className="text-center py-[1px] px-[7.4px] font-bold text-white bg-[#3a4252] whitespace-nowrap" style={{ }}>#</th>
                    <th className="text-center py-[1px] px-[6.5px] font-bold text-white" style={{ overflow: 'hidden', boxSizing: 'border-box' }}>SL</th>
                    <th className="text-center py-[1px] px-[6.5px] font-bold text-white" style={{ }}>SLot</th>
                    <th className="text-center py-[1px] px-[6.5px] font-bold text-white" style={{ }}>SVal</th>
                    {!showFrequency && !showOrder && !showNoSplit ? (
                      <th className={`text-center py-[1px] px-[6.5px] font-bold text-white ${gRight}`} style={{ }}>SAvg</th>
                    ) : (
                      <>
                        <th className="text-center py-[1px] px-[6.5px] font-bold text-white" style={{ }}>SAvg</th>
                        {showFrequency && <th className="text-center py-[1px] px-[6.5px] font-bold text-white" style={{ }}>SFreq</th>}
                        {showFrequency && <th className={`text-center py-[1px] px-[6.5px] font-bold text-white bg-[#3a4252] bg-clip-padding whitespace-nowrap ${!showOrder && !showNoSplit ? gRight : ''}`} style={{ }}>Lot/F</th>}
                        {showOrder && <th className="text-center py-[1px] px-[6.5px] font-bold text-white whitespace-nowrap" style={{ }}>SOrd</th>}
                        {showOrder && <th className={`text-center py-[1px] px-[6.5px] font-bold text-white bg-[#3a4252] bg-clip-padding whitespace-nowrap ${!showNoSplit ? gRight : ''}`} style={{ }}>Lot/Ord</th>}
                        {showNoSplit && <th className="text-center py-[1px] px-[6.5px] font-bold text-white" style={{ }}>SNon</th>}
                        {showNoSplit && <th className={`text-center py-[1px] px-[6.5px] font-bold text-white bg-[#3a4252] bg-clip-padding whitespace-nowrap ${gRight}`} style={{ }}>Lot/Non</th>}
                      </>
                    )}
                  
                      </React.Fragment>
                      );
                    })}</tr>
                </thead>
                <tbody>
                  {tableMaxRows === 0 || transactionData.size === 0 ? (
                    // If no data, show "No Data Available" message
                    <tr className="border-b-2 border-white">
                      <td
                        colSpan={netTotalCols}
                        className="text-center py-[2.06px] text-muted-foreground font-bold"
                        style={{ width: '100%', maxWidth: '100%', minWidth: 0 }}
                      >
                        No Data Available
                      </td>
                    </tr>
                  ) : rowIndices.length > 0 ? rowIndices.map((rowIdx: number) => {
                    return (
                      <tr key={rowIdx} style={{ height: COLUMN_WIDTHS.ROW_HEIGHT }}>
                        {!showOnlyTotal && !isMultiRangeDisplayed && visibleDates.map((date: string, dateIndex: number) => {
                          // Get Net Buy data at this row index for this date
                          // CRITICAL: Data is already filtered in loadTransactionData
                          const netBuyDataForDate = netBuyStocksByDate.get(date) || [];
                          const netBuyRowData = netBuyDataForDate[rowIdx];

                          // Get Net Sell data at this row index for this date
                          // CRITICAL: Data is already filtered in loadTransactionData
                          const netSellDataForDate = netSellStocksByDate.get(date) || [];
                          const netSellRowData = netSellDataForDate[rowIdx];

                          return (
                            <React.Fragment key={date}>
                              {/* Net Buy Columns - from netBuyStocksByDate */}
                              {netBuyRowData ? (
                                <>
                                  {(() => {
                                    const dayData = netBuyRowData.data;
                                    const nbCode = dayData.NBCode || netBuyRowData.stock;
                                    // For Stock pivot: NBCode is a broker code, use getBrokerColorClass
                                    // For Broker pivot: NBCode is a stock code, use getStockColorClass
                                    const nbCodeColor = pivotFilter === 'Stock'
                                      ? getBrokerColorClass(nbCode)
                                      : getStockColorClass(nbCode);
                                    const nbLot = dayData.NBLot || 0;
                                    const nbVal = dayData.NBVal || dayData.NetBuyValue || 0;
                                    // Use NBAvg from data, or calculate from NBLot and NBVal if NBAvg is 0/missing
                                    const nbAvg = (dayData.NBAvg && dayData.NBAvg !== 0)
                                      ? dayData.NBAvg
                                      : (nbLot > 0 ? nbVal / (nbLot * 100) : 0);
                                    const nbFreq = dayData.NBFreq || 0;
                                    // Use value from CSV, no fallback
                                    const nbLotPerFreq = dayData.NBLotPerFreq;
                                    const nbOrdNum = dayData.NBOrdNum || 0;
                                    // Use value from CSV, no fallback
                                    const nbLotPerOrdNum = dayData.NBLotPerOrdNum;

                                    return (
                                      <>
                                        <td className={`text-center py-[1px] px-[7.4px] ${nbCodeColor.className} ${dateIndex === 0 ? 'border-l-2 border-white' : ''}`} style={{ minWidth: '24px', maxWidth: '24px', color: nbCodeColor.color, fontVariantNumeric: 'tabular-nums', ...getRankStyle(nbCode) }}>
                                          {nbCode}
                                        </td>
                                        <td className={`text-right py-[1px] px-[3.5px] text-[#00ff00]`} style={{ fontVariantNumeric: 'tabular-nums', ...getRankStyle(nbCode) }}>
                                          {formatLot(nbLot)}
                                        </td>
                                        <td className={`text-right py-[1px] px-[5.5px] text-[#00ff00]`} style={{ fontVariantNumeric: 'tabular-nums', ...getRankStyle(nbCode) }}>
                                          {formatValue(nbVal)}
                                        </td>
                                        <td className={`text-right py-[1px] px-[5.5px] text-[#00ff00]`} style={{ fontVariantNumeric: 'tabular-nums', ...getRankStyle(nbCode) }}>
                                          {formatAverage(nbAvg ?? 0)}
                                        </td>
                                        {showFrequency && <td className={`text-right py-[1px] px-[5.5px] text-lime-400`} style={{ fontVariantNumeric: 'tabular-nums', ...getRankStyle(nbCode) }}>
                                          {nbFreq}
                                        </td>}
                                        {showFrequency && <td className={`text-right py-[1px] px-[3.5px] text-lime-400`} style={{ fontVariantNumeric: 'tabular-nums', ...getRankStyle(nbCode) }}>
                                          {formatLotPerFreqOrOrdNum(nbLotPerFreq ?? 0)}
                                        </td>}
                                        {showOrder && <td className={`text-right py-[1px] px-[3.5px] text-teal-400`} style={{ fontVariantNumeric: 'tabular-nums', ...getRankStyle(nbCode) }}>
                                          {nbOrdNum}
                                        </td>}
                                        {showOrder && <td className={`text-right py-[1px] px-[3.5px] text-teal-400`} style={{ fontVariantNumeric: 'tabular-nums', ...getRankStyle(nbCode) }}>
                                          {formatLotPerFreqOrOrdNum(nbLotPerOrdNum ?? 0)}
                                        </td>}
                                        {showNoSplit && <td className={`text-right py-[1px] px-[3.5px] text-green-300`} style={{ fontVariantNumeric: 'tabular-nums', ...getRankStyle(nbCode) }}>
                                          {dayData.NBNoSplit ?? 0}
                                        </td>}
                                        {showNoSplit && <td className={`text-right py-[1px] px-[3.5px] text-green-300`} style={{ fontVariantNumeric: 'tabular-nums', ...getRankStyle(nbCode) }}>
                                          {formatLotPerFreqOrOrdNum(dayData.NBLotPerNoSplit ?? 0)}
                                        </td>}
                                      </>
                                    );
                                  })()}
                                </>
                              ) : (
                                // Show empty cells if no Net Buy data at this row index
                                <>
                                  <td className={`text-center py-[1px] px-[7.4px] text-gray-400 ${dateIndex === 0 ? 'border-l-2 border-white' : ''}`} style={{ overflow: 'hidden', fontVariantNumeric: 'tabular-nums' }}>-</td>
                                  <td className={`text-right py-[1px] px-[3.5px] text-gray-400`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>
                                  <td className={`text-right py-[1px] px-[3.5px] text-gray-400`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>

                                  {!showFrequency && !showOrder && !showNoSplit ? (
                                    <td className={`text-right py-[1px] px-[3.5px] text-gray-400`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>
                                  ) : (
                                    <>
                                      <td className={`text-right py-[1px] px-[3.5px] text-gray-400`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>
                                      {showFrequency && <td className={`text-right py-[1px] px-[3.5px] text-gray-400`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                                      {showFrequency && <td className={`text-right py-[1px] px-[3.5px] text-gray-400`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                                      {showOrder && <td className={`text-right py-[1px] px-[3.5px] text-gray-400`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                                      {showOrder && <td className={`text-right py-[1px] px-[3.5px] text-gray-400`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                                      {showNoSplit && <td className={`text-right py-[1px] px-[3.5px] text-gray-400`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                                      {showNoSplit && <td className={`text-right py-[1px] px-[3.5px] text-gray-400`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                                    </>
                                  )}
                                </>
                              )}
                              {/* Separator */}
                              <td className="text-center py-[1px] px-[7.4px] text-white bg-[#3a4252] whitespace-nowrap" style={{ fontVariantNumeric: 'tabular-nums' }}>{rowIdx + 1}</td>
                              {/* Net Sell Columns - from netSellStocksByDate */}
                              {netSellRowData ? (
                                <>
                                  {(() => {
                                    const dayData = netSellRowData.data;
                                    const nsCode = dayData.NSCode || netSellRowData.stock;
                                    // For Stock pivot: NSCode is a broker code, use getBrokerColorClass
                                    // For Broker pivot: NSCode is a stock code, use getStockColorClass
                                    const nsCodeColor = pivotFilter === 'Stock'
                                      ? getBrokerColorClass(nsCode)
                                      : getStockColorClass(nsCode);
                                    const nsLot = dayData.NSLot || 0;
                                    const nsVal = dayData.NSVal || dayData.NetSellValue || 0;
                                    // Use NSAvg from data, or calculate from NSLot and NSVal if NSAvg is 0/missing
                                    const nsAvg = (dayData.NSAvg && dayData.NSAvg !== 0)
                                      ? dayData.NSAvg
                                      : (nsLot > 0 ? nsVal / (nsLot * 100) : 0);
                                    const nsFreq = dayData.NSFreq || 0;
                                    // Use value from CSV, no fallback
                                    const nsLotPerFreq = dayData.NSLotPerFreq;
                                    const nsOrdNum = dayData.NSOrdNum || 0;
                                    // Use value from CSV, no fallback
                                    const nsLotPerOrdNum = dayData.NSLotPerOrdNum;

                                    return (
                                      <>
                                        <td className={`text-center py-[1px] px-[6.5px] ${nsCodeColor.className}`} style={{ minWidth: '24px', maxWidth: '24px', color: nsCodeColor.color, fontVariantNumeric: 'tabular-nums', ...getRankStyle(nsCode) }}>
                                          {nsCode}
                                        </td>
                                        <td className={`text-right py-[1px] px-[3.5px] text-[#ff0000]`} style={{ fontVariantNumeric: 'tabular-nums', ...getRankStyle(nsCode) }}>
                                          {formatLot(nsLot)}
                                        </td>
                                        <td className={`text-right py-[1px] px-[5.5px] text-[#ff0000]`} style={{ fontVariantNumeric: 'tabular-nums', ...getRankStyle(nsCode) }}>
                                          {formatValue(nsVal)}
                                        </td>
                                        {!showFrequency && !showOrder && !showNoSplit ? (
                                          <td className={`text-right py-[1px] px-[5.5px] text-[#ff0000] ${dateIndex < selectedDates.length - 1 ? 'border-r-[10px] border-white' : ''} ${dateIndex === selectedDates.length - 1 ? 'border-r-[10px] border-white' : ''}`} style={{ fontVariantNumeric: 'tabular-nums', ...getRankStyle(nsCode) }}>
                                            {formatAverage(nsAvg ?? 0)}
                                          </td>
                                        ) : (
                                          <>
                                            <td className={`text-right py-[1px] px-[5.5px] text-[#ff0000]`} style={{ fontVariantNumeric: 'tabular-nums', ...getRankStyle(nsCode) }}>
                                              {formatAverage(nsAvg ?? 0)}
                                            </td>
                                            {showFrequency && <td className={`text-right py-[1px] px-[5.5px] text-amber-500`} style={{ fontVariantNumeric: 'tabular-nums', ...getRankStyle(nsCode) }}>
                                              {nsFreq}
                                            </td>}
                                            {showFrequency && <td className={`text-right py-[1px] px-[3.5px] text-amber-500 ${!showOrder && !showNoSplit && dateIndex < selectedDates.length - 1 ? 'border-r-[10px] border-white' : ''} ${!showOrder && !showNoSplit && dateIndex === selectedDates.length - 1 ? 'border-r-[10px] border-white' : ''}`} style={{ fontVariantNumeric: 'tabular-nums', ...getRankStyle(nsCode) }}>
                                              {formatLotPerFreqOrOrdNum(nsLotPerFreq ?? 0)}
                                            </td>}
                                            {showOrder && <td className={`text-right py-[1px] px-[3.5px] text-[#e5c3d1]`} style={{ fontVariantNumeric: 'tabular-nums', ...getRankStyle(nsCode) }}>
                                              {nsOrdNum}
                                            </td>}
                                            {showOrder && <td className={`text-right py-[1px] px-[3.5px] text-[#e5c3d1] ${!showNoSplit && dateIndex < selectedDates.length - 1 ? 'border-r-[10px] border-white' : ''} ${!showNoSplit && dateIndex === selectedDates.length - 1 ? 'border-r-[10px] border-white' : ''}`} style={{ fontVariantNumeric: 'tabular-nums', ...getRankStyle(nsCode) }}>
                                              {formatLotPerFreqOrOrdNum(nsLotPerOrdNum ?? 0)}
                                            </td>}
                                            {showNoSplit && <td className={`text-right py-[1px] px-[3.5px] text-red-300`} style={{ fontVariantNumeric: 'tabular-nums', ...getRankStyle(nsCode) }}>
                                              {dayData.NSNoSplit ?? 0}
                                            </td>}
                                            {showNoSplit && <td className={`text-right py-[1px] px-[3.5px] text-red-300 ${dateIndex < selectedDates.length - 1 ? 'border-r-[10px] border-white' : ''} ${dateIndex === selectedDates.length - 1 ? 'border-r-[10px] border-white' : ''}`} style={{ fontVariantNumeric: 'tabular-nums', ...getRankStyle(nsCode) }}>
                                              {formatLotPerFreqOrOrdNum(dayData.NSLotPerNoSplit ?? 0)}
                                            </td>}
                                          </>
                                        )}
                                      </>
                                    );
                                  })()}
                                </>
                              ) : (
                                // Show empty cells if no Net Sell data at this row index
                                <>
                                  <td className={`text-center py-[1px] px-[6.5px] text-gray-400`} style={{ overflow: 'hidden', fontVariantNumeric: 'tabular-nums' }}>-</td>
                                  <td className={`text-right py-[1px] px-[3.5px] text-gray-400`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>
                                  <td className={`text-right py-[1px] px-[3.5px] text-gray-400`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>
                                  {!showFrequency && !showOrder && !showNoSplit ? (
                                    <td className={`text-right py-[1px] px-[3.5px] text-gray-400 ${dateIndex < selectedDates.length - 1 ? 'border-r-[10px] border-white' : ''} ${dateIndex === selectedDates.length - 1 ? 'border-r-[10px] border-white' : ''}`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>
                                  ) : (
                                    <>
                                      <td className={`text-right py-[1px] px-[3.5px] text-gray-400`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>
                                      {showFrequency && <td className={`text-right py-[1px] px-[3.5px] text-gray-400`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                                      {showFrequency && <td className={`text-right py-[1px] px-[3.5px] text-gray-400 ${!showOrder && !showNoSplit && dateIndex < selectedDates.length - 1 ? 'border-r-[10px] border-white' : ''} ${!showOrder && !showNoSplit && dateIndex === selectedDates.length - 1 ? 'border-r-[10px] border-white' : ''}`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                                      {showOrder && <td className={`text-right py-[1px] px-[3.5px] text-gray-400`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                                      {showOrder && <td className={`text-right py-[1px] px-[3.5px] text-gray-400 ${!showNoSplit && dateIndex < selectedDates.length - 1 ? 'border-r-[10px] border-white' : ''} ${!showNoSplit && dateIndex === selectedDates.length - 1 ? 'border-r-[10px] border-white' : ''}`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                                      {showNoSplit && <td className={`text-right py-[1px] px-[3.5px] text-gray-400`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                                      {showNoSplit && <td className={`text-right py-[1px] px-[3.5px] text-gray-400 ${dateIndex < selectedDates.length - 1 ? 'border-r-[10px] border-white' : ''} ${dateIndex === selectedDates.length - 1 ? 'border-r-[10px] border-white' : ''}`} style={{ fontVariantNumeric: 'tabular-nums' }}>-</td>}
                                    </>
                                  )}
                                </>
                              )}
                            </React.Fragment>
                          );
                        })}
                        {/* Total Column - Calculate NET from B/S Total (Buy - Sell) */}
                        {isMultiRangeDisplayed ? (
                          <React.Fragment>
                            {rangeAggregates.map((agg, rIdx) => (
                              <React.Fragment key={`n-range-${rIdx}`}>
                                {renderNetTotalGroup(rowIdx, agg.netBuyList, agg.netSellList, rIdx === 0 ? 'border-l-2 border-white' : 'border-l-[10px] border-white', 'border-r-[10px] border-white')}
                              </React.Fragment>
                            ))}
                            <React.Fragment key="n-grand">
                              {renderNetTotalGroup(rowIdx, allNetDataForTotalMemo.netBuy, allNetDataForTotalMemo.netSell, 'border-l-[10px] border-white', 'border-r-2 border-white')}
                            </React.Fragment>
                          </React.Fragment>
                        ) : (
                          <React.Fragment>
                            {renderNetTotalGroup(rowIdx, allNetDataForTotalMemo.netBuy, allNetDataForTotalMemo.netSell, showOnlyTotal || selectedDates.length === 0 ? 'border-l-2 border-white' : 'border-l-[10px] border-white', 'border-r-2 border-white')}
                          </React.Fragment>
                        )}
                      </tr>
                    );
                  }) : null}
                  {/* Lazy loading sentinel - triggers load more when scrolled into view */}
                  {visibleRowCount < maxRows && rowIndices.length > 0 && (
                    <tr>
                      <td colSpan={showOnlyTotal && !isMultiRangeDisplayed ? netColsPerDate : (isMultiRangeDisplayed ? (rangeAggregates.length + 1) * netColsPerDate : (visibleDates.length * netColsPerDate + netColsPerDate))} ref={netTableSentinelRef} className="h-10">
                        {/* Invisible sentinel for intersection observer */}
                      </td>
                    </tr>
                  )}
                </tbody>
                {/* Summary Section - Locked at bottom */}
                <tfoot className="sticky bottom-0 z-10">
                                    {/* Header Row for Summary */}
                  <tr className="bg-[#3a4252] border-t-2 border-white">
                    {/* Per-date headers (only shown when not showOnlyTotal) */}
                    {!isMultiRangeDisplayed && !showOnlyTotal && visibleDates.map((date, dateIndex) => (
                      <React.Fragment key={`sumhead-${date}`}>
                        {/* Summary Headers - 4 kolom ala BrokerSummary lama (distribusi 2-2-3-2) */}
                        <th className={`text-center py-[1px] px-[7.4px] font-bold text-white ${dateIndex === 0 ? 'border-l-2 border-white' : ''}`} colSpan={sumSpanT}>TVal</th>
                        <th className="text-center py-[1px] px-[7.4px] font-bold text-white" colSpan={sumSpanN}>{sumNetLabel}</th>
                        <th className="text-center py-[1px] px-[7.4px] font-bold text-white" colSpan={sumSpanL}>TLot</th>
                        <th className={`text-center py-[1px] px-[7.4px] font-bold text-white ${dateIndex < visibleDates.length - 1 ? 'border-r-[10px] border-white' : ''} ${dateIndex === visibleDates.length - 1 ? 'border-r-[10px] border-white' : ''}`} colSpan={sumSpanA}>Avg</th>
                      </React.Fragment>
                    ))}

                    {/* Per-range headers (multi-range mode) */}
                    {isMultiRangeDisplayed && displayedRangesMeta.map((_, rIdx) => (
                      <React.Fragment key={`sumhead-range-${rIdx}`}>
                        <th className={`text-center py-[1px] px-[7.4px] font-bold text-white ${rIdx === 0 ? 'border-l-2 border-white' : 'border-l-[10px] border-white'}`} colSpan={sumSpanT}>TVal</th>
                        <th className="text-center py-[1px] px-[7.4px] font-bold text-white" colSpan={sumSpanN}>{sumNetLabel}</th>
                        <th className="text-center py-[1px] px-[7.4px] font-bold text-white" colSpan={sumSpanL}>TLot</th>
                        <th className="text-center py-[1px] px-[7.4px] font-bold text-white border-r-[10px] border-white" colSpan={sumSpanA}>Avg</th>
                      </React.Fragment>
                    ))}

                    {/* Grand total column headers (selalu 1 grup — range sudah di atas) */}
                    <React.Fragment key="sumhead-grand">
                      <th className={`text-center py-[1px] px-[6.5px] font-bold text-white ${!isMultiRangeDisplayed && (showOnlyTotal || visibleDates.length === 0) ? 'border-l-2 border-white' : 'border-l-[10px] border-white'} border-r-2 border-white`} colSpan={sumSpanT}>TVal</th>
                      <th className="text-center py-[1px] px-[6.5px] font-bold text-white" colSpan={sumSpanN}>{sumNetLabel}</th>
                      <th className="text-center py-[1px] px-[6.5px] font-bold text-white" colSpan={sumSpanL}>TLot</th>
                      <th className="text-center py-[1px] px-[6.5px] font-bold text-white border-r-2 border-white" colSpan={sumSpanA}>Avg</th>
                    </React.Fragment>
                  </tr>

                  {/* Summary Data Row */}
                  <tr className="bg-[#0a0f20]">
                    {/* Per-date summary columns (only shown when not showOnlyTotal) */}
                    {!isMultiRangeDisplayed && !showOnlyTotal && visibleDates.map((date, dateIndex) => {
                      const s = computeTxFooterSummary([date]);
                      const nc = s.netValue == null ? 'text-white' : s.netValue > 0 ? 'text-[#00ff00]' : s.netValue < 0 ? 'text-[#ff0000]' : 'text-white';
                      return (
                        <React.Fragment key={`sum-${date}`}>
                          {/* Summary Data - 4 kolom ala BrokerSummary lama */}
                          <td className={`text-center py-[2px] px-[7.4px] font-bold text-white ${dateIndex === 0 ? 'border-l-2 border-white' : ''}`} style={{ fontVariantNumeric: 'tabular-nums' }} colSpan={sumSpanT}>{formatNumber(s.totalValue)}</td>
                          <td className={`text-center py-[2px] px-[7.4px] font-bold ${nc}`} style={{ fontVariantNumeric: 'tabular-nums' }} colSpan={sumSpanN}>{s.netValue == null ? '' : formatNumber(s.netValue)}</td>
                          <td className="text-center py-[2px] px-[7.4px] font-bold text-white" style={{ fontVariantNumeric: 'tabular-nums' }} colSpan={sumSpanL}>{formatLot(s.totalLot)}</td>
                          <td className={`text-center py-[2px] px-[7.4px] font-bold text-white ${dateIndex < visibleDates.length - 1 ? 'border-r-[10px] border-white' : ''} ${dateIndex === visibleDates.length - 1 ? 'border-r-[10px] border-white' : ''}`} style={{ fontVariantNumeric: 'tabular-nums' }} colSpan={sumSpanA}>{formatAverage(s.avgPrice)}</td>
                        </React.Fragment>
                      );
                    })}

                    {/* Per-range summary (multi-range mode) */}
                    {isMultiRangeDisplayed && displayedAllRangesDates.map((dates, idx) => {
                      const s = computeTxFooterSummary(dates);
                      const nc = s.netValue == null ? 'text-white' : s.netValue > 0 ? 'text-[#00ff00]' : s.netValue < 0 ? 'text-[#ff0000]' : 'text-white';
                      return (
                        <React.Fragment key={`sum-r${idx}`}>
                          <td className={`text-center py-[2px] px-[7.4px] font-bold text-white ${idx === 0 ? 'border-l-2 border-white' : 'border-l-[10px] border-white'}`} style={{ fontVariantNumeric: 'tabular-nums' }} colSpan={sumSpanT}>{formatNumber(s.totalValue)}</td>
                          <td className={`text-center py-[2px] px-[7.4px] font-bold ${nc}`} style={{ fontVariantNumeric: 'tabular-nums' }} colSpan={sumSpanN}>{s.netValue == null ? '' : formatNumber(s.netValue)}</td>
                          <td className="text-center py-[2px] px-[7.4px] font-bold text-white" style={{ fontVariantNumeric: 'tabular-nums' }} colSpan={sumSpanL}>{formatLot(s.totalLot)}</td>
                          <td className="text-center py-[2px] px-[7.4px] font-bold text-white border-r-[10px] border-white" style={{ fontVariantNumeric: 'tabular-nums' }} colSpan={sumSpanA}>{formatAverage(s.avgPrice)}</td>
                        </React.Fragment>
                      );
                    })}

                    {/* Grand total */}
                    {(() => {
                      const gdates = isMultiRangeDisplayed ? displayedAllRangesDates.flat() : (showOnlyTotal ? Array.from(dateSummariesMemo.keys()) : visibleDates);
                      const g = computeTxFooterSummary(gdates);
                      const gnc = g.netValue == null ? 'text-white' : g.netValue > 0 ? 'text-[#00ff00]' : g.netValue < 0 ? 'text-[#ff0000]' : 'text-white';
                      const gLeft = !isMultiRangeDisplayed && (showOnlyTotal || visibleDates.length === 0) ? 'border-l-2 border-white' : 'border-l-[10px] border-white';
                      return (
                        <React.Fragment key="sum-grand">
                          <td className={`text-center py-[2px] px-[6.5px] font-bold text-white ${gLeft}`} style={{ fontVariantNumeric: 'tabular-nums' }} colSpan={sumSpanT}>{formatNumber(g.totalValue)}</td>
                          <td className={`text-center py-[2px] px-[6.5px] font-bold ${gnc}`} style={{ fontVariantNumeric: 'tabular-nums' }} colSpan={sumSpanN}>{g.netValue == null ? '' : formatNumber(g.netValue)}</td>
                          <td className="text-center py-[2px] px-[6.5px] font-bold text-white" style={{ fontVariantNumeric: 'tabular-nums' }} colSpan={sumSpanL}>{formatLot(g.totalLot)}</td>
                          <td className="text-center py-[2px] px-[6.5px] font-bold text-white border-r-2 border-white" style={{ fontVariantNumeric: 'tabular-nums' }} colSpan={sumSpanA}>{formatAverage(g.avgPrice)}</td>
                        </React.Fragment>
                      );
                    })()}
                  </tr>
</tfoot>
              </table>
              {showAIPopup && renderAiStatsTable(aiNetStats, 'Top 10 Net')}
                <div aria-hidden="true" className="w-0 shrink-0 self-stretch border-l-2 border-white" />
              </div>
            </div>
          </div>
        </div>
      );
    };


    return (
      <div className="w-full h-full flex flex-col min-h-0 overflow-hidden pb-2">
        {renderValueTable()}
        {renderNetTable()}
      </div>
    );
  }, [filteredStocks, uniqueStocks, sortedStocksByDate, sortedNetStocksByDate, totalDataByStock, totalNetDataByStock, sortedTotalStocks, sortedTotalNetStocks, transactionData, visibleRowIndices, buyStocksByDate, sellStocksByDate, netBuyStocksByDate, netSellStocksByDate, totalNetBuyDataByStock, totalNetSellDataByStock, isDataReady, selectedDates, activeSectorFilter, selectedSectors, stockToSectorMap, pivotFilter, selectedTickers, selectedBrokers, showFrequency, showOrder, showAIPopup, aiValueStats, aiNetStats, aiDateOverlay, aiDateStats, aiDateValueStats, foreignTxByDate, dateSummariesMemo, appliedInvFilter, isMultiRangeDisplayed, displayedAllRangesDates, displayedSortedRangeLabels, rangeAggregates]); // CRITICAL: tables re-render when column width changes (e.g. after stocks API loads)

  // Helper function to get broker color class based on type
  // Memoize broker color cache for O(1) lookup
  const brokerColorCache = useMemo(() => {
    const cache = new Map<string, { color: string; className: string }>();
    const govSet = new Set(GOVERNMENT_BROKERS);
    const foreignSet = new Set(FOREIGN_BROKERS);

    return {
      get: (brokerCode: string): { color: string; className: string } => {
        if (cache.has(brokerCode)) {
          return cache.get(brokerCode)!;
        }
        let result: { color: string; className: string };
        if (govSet.has(brokerCode)) {
          result = { color: '#00ff00', className: '' }; // Govt = hijau murni (sama kayak BLot)
        } else if (foreignSet.has(brokerCode)) {
          result = { color: '#ff0000', className: '' }; // Foreign = merah murni (sama kayak SLot)
        } else {
          result = { color: '#FFFFFF', className: '' }; // White
        }
        cache.set(brokerCode, result);
        return result;
      }
    };
  }, []);

  const getBrokerColorClass = useCallback((brokerCode: string): { color: string; className: string } => {
    return brokerColorCache.get(brokerCode);
  }, [brokerColorCache]);

  // Memo HASIL render tabel (bukan cuma fungsinya). Tanpa ini, tiap ketikan di
  // kotak Ticker / hover suggestion bikin seluruh tabel VALUE + NET di-render ulang.
  // Harus di sini (setelah semua helper, mis. getBrokerColorClass) karena langsung dipanggil.
  const horizontalView = useMemo(
    () => (!isLoading && !error && isDataReady ? renderHorizontalView() : null),
    [isLoading, error, isDataReady, renderHorizontalView]
  );

  return (
    <div className="w-full flex flex-col h-full overflow-hidden bg-[#0a0f20] relative">

      {/* Top Controls - Compact without Card */}
      <div className={`bg-card border-b border-[#3a4252] ${isDashboard ? 'px-3' : 'px-4'} py-1.5 z-40`}>
          <div ref={menuContainerRef} className={`flex ${isDashboard ? 'flex-row flex-nowrap overflow-x-auto max-w-full [&>*]:shrink-0' : 'flex-col md:flex-row'} items-center gap-1 md:gap-x-4 md:gap-y-0.5`}>
          {/* Broker Selection disembunyikan di NewBrokerSummary (selalu ALL) */}
          {false && (
          <>
          {/* Broker Selection - Dropdown only */}
          <div className="flex flex-col md:flex-row md:items-center gap-2 w-full md:w-auto">
            <label className="text-sm font-medium whitespace-nowrap">Broker:</label>
            <div className="flex items-center gap-2 w-full md:w-auto">
              {/* Broker Input with selected count */}
              <div className="relative flex-1 md:flex-none" ref={dropdownBrokerRef}>
                <div
                  className={`w-full ${isDashboard ? 'md:w-20 px-2' : 'md:w-32 px-3'} h-9 flex items-center justify-between bg-background border border-input rounded-md text-sm text-foreground cursor-pointer hover:bg-accent/50 transition-colors`}
                  onClick={() => setShowBrokerSuggestions(!showBrokerSuggestions)}
                >
                  <span className="truncate">
                    {selectedBrokers.length === 0 ? 'All' : (selectedBrokers.includes('ALL') ? 'All' : selectedBrokers.join(', '))}
                  </span>
                  <ChevronDown className="w-3 h-3 text-muted-foreground opacity-50 ml-1 flex-shrink-0" />
                </div>
                {showBrokerSuggestions && (
                  <div id="broker-suggestions" role="listbox" className="absolute top-full left-0 mt-1 bg-popover border border-[#3a4252] rounded-md shadow-lg z-50 w-48 overflow-hidden flex flex-col">
                    {availableBrokers.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-muted-foreground flex items-center">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Loading brokers...
                      </div>
                    ) : (
                      <>
                        {selectedBrokers.length > 0 && !selectedBrokers.includes('ALL') && (
                          <div className="px-3 py-2 text-xs text-muted-foreground border-b border-[#3a4252] flex items-center justify-between bg-popover">
                            <span>Selected ({selectedBrokers.length})</span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleClearAllBrokers();
                              }}
                              className="text-xs text-destructive hover:text-destructive/80 font-medium"
                            >
                              Clear
                            </button>
                          </div>
                        )}
                        <div className="p-2 border-b border-[#3a4252]">
                          <div className="relative">
                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                            <input
                              type="text"
                              className="w-full h-8 pl-7 pr-2 bg-[#0a0f20] border border-[#3a4252] rounded text-sm text-white focus:outline-none focus:border-primary/50"
                              placeholder="Search broker..."
                              value={brokerInput}
                              onChange={(e) => {
                                setBrokerInput(e.target.value.toUpperCase());
                              }}
                              onClick={(e) => e.stopPropagation()}
                              autoFocus
                            />
                          </div>
                        </div>
                        <div className="max-h-60 overflow-y-auto py-1"
                          onScroll={(e) => {
                            const target = e.target as HTMLElement;
                            const scrollBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
                            if (scrollBottom < 100 && brokerScrollOffset + ITEMS_PER_PAGE < filteredBrokersList.length) {
                              setBrokerScrollOffset(prev => prev + ITEMS_PER_PAGE);
                            }
                          }}
                        >
                          {brokerInput === '' && (
                            <div
                              className={`px-3 py-[2.06px] text-sm cursor-pointer flex justify-between items-center ${selectedBrokers.includes('ALL') || selectedBrokers.length === 0 ? 'text-primary font-medium' : ''}`}
                              onClick={() => handleBrokerSelect('ALL')}
                            >
                              <span>ALL</span>
                              {(selectedBrokers.includes('ALL') || selectedBrokers.length === 0) && <span className="text-primary text-xs">✓</span>}
                            </div>
                          )}
                          {visibleFilteredBrokers.map(broker => {
                            const isSelected = selectedBrokers.includes(broker);
                            const brokerColor = getBrokerColorClass(broker);
                            return (
                              <div
                                key={broker}
                                className={`px-3 py-[2.06px] text-sm cursor-pointer flex justify-between items-center ${isSelected ? 'font-medium bg-accent/50' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (isSelected) {
                                    handleRemoveBroker(broker);
                                  } else {
                                    handleBrokerSelect(broker);
                                  }
                                }}
                              >
                                <span className={brokerColor?.className || ''} style={{ color: brokerColor?.color }}>{broker}</span>
                                {isSelected && <span className="text-primary text-xs">✓</span>}
                              </div>
                            );
                          })}
                          {brokerScrollOffset + ITEMS_PER_PAGE < filteredBrokersList.length && (
                            <div className="px-3 py-2 text-xs text-muted-foreground text-center">
                              Loading more...
                            </div>
                          )}
                          {brokerInput !== '' && visibleFilteredBrokers.length === 0 && (
                            <div className="px-3 py-2 text-sm text-muted-foreground text-center">No brokers found</div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          </>
          )}

          {/* Ticker Multi-Select (Combined) - Dropdown only (hidden in dashboard mode) */}
          {!isDashboard && (
          <div className="flex flex-col md:flex-row md:items-center gap-1 w-full md:w-auto">
            <label className="text-sm font-medium whitespace-nowrap">Ticker:</label>
            <div className="flex items-center gap-1 w-full md:w-auto">
              <div className="relative flex-1 md:flex-none" ref={dropdownTickerRef}>
                <div 
                  className={`w-full md:w-[117px] h-9 px-2 flex items-center justify-between bg-background border border-input rounded-md text-sm text-foreground hover:border-primary/50 focus-within:border-primary/50 transition-colors ${isTrial ? 'opacity-80' : ''}`}
                  onClick={() => {
                    if (isTrial) {
                      showToast({
                        type: 'warning',
                        title: 'Fitur Terbatas',
                        message: 'Dalam masa free trial, Anda tidak dapat memilih ticker spesifik.'
                      });
                    }
                  }}
                >
                  <Search className="w-3.5 h-3.5 text-muted-foreground mr-2 flex-shrink-0" />
                  <input
                    type="text"
                    className={`w-full bg-transparent text-sm text-foreground focus:outline-none placeholder:text-muted-foreground/50 truncate ${selectedStock || isTrial ? 'cursor-not-allowed' : ''}`}
                    placeholder="Select ticker..."
                    value={tickerTextDirty ? tickerInput : ([...selectedTickers, ...selectedSectors].length > 0 ? [...selectedTickers, ...selectedSectors].join(', ') : '')}
                    onChange={(e) => {
                      if (selectedStock || isTrial) return;
                      setTickerInput(e.target.value.toUpperCase());
                      setTickerTextDirty(true);
                      setShowTickerSuggestions(true);
                    }}
                    onFocus={() => {
                      if (isTrial) {
                        showToast({
                          type: 'warning',
                          title: 'Fitur Terbatas',
                          message: 'Dalam masa free trial, Anda tidak dapat memilih ticker spesifik.'
                        });
                        return;
                      }
                      if (!selectedStock) setShowTickerSuggestions(true);
                    }}
                    readOnly={!!selectedStock || isTrial}
                  />
                  {!selectedStock && (
                    <ChevronDown 
                      className="w-3 h-3 text-muted-foreground opacity-50 ml-1 flex-shrink-0 cursor-pointer" 
                      onClick={(e) => {
                        if (isTrial) {
                          e.stopPropagation();
                          showToast({
                            type: 'warning',
                            title: 'Fitur Terbatas',
                            message: 'Dalam masa free trial, Anda tidak dapat memilih ticker spesifik.'
                          });
                          return;
                        }
                        setShowTickerSuggestions(!showTickerSuggestions);
                      }} 
                    />
                  )}
                </div>
                {showTickerSuggestions && (
                  <div id="ticker-suggestions" role="listbox" className="absolute top-full left-0 mt-1 bg-popover border border-[#3a4252] rounded-md shadow-lg z-50 max-h-96 overflow-hidden flex flex-col w-[340px]">
                    {isLoadingStocks || (availableTickers.length === 0 && availableStocksFromAPI.length === 0) ? (
                      <div className="px-3 py-[2.06px] text-sm text-muted-foreground flex items-center">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Loading stocks...
                      </div>
                    ) : (
                      <>
                        {/* Selected Items Header + Chips */}
                        {(liveTickerSelection.tickers.length > 0 || liveTickerSelection.sectors.length > 0) && (
                          <div className="border-b border-[#3a4252] bg-popover">
                            <div className="px-3 py-1.5 text-xs text-muted-foreground flex items-center justify-between">
                              <span>Selected ({liveTickerSelection.tickers.length + liveTickerSelection.sectors.length})</span>
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); handleClearAll(); }}
                                className="text-xs text-destructive hover:text-destructive/80 font-medium"
                              >
                                Clear
                              </button>
                            </div>
                            <div className="flex flex-col">
                              {liveTickerSelection.tickers.map(ticker => (
                                <div key={ticker} className="px-3 py-[2.06px] text-sm text-primary font-medium flex justify-between items-center hover:bg-muted">
                                  <span>{ticker}</span>
                                  <button
                                    type="button"
                                    onMouseDown={(e) => { e.stopPropagation(); handleRemoveTicker(ticker); }}
                                    className="text-muted-foreground hover:text-destructive transition-colors ml-2 text-xs"
                                  >×</button>
                                </div>
                              ))}
                              {liveTickerSelection.sectors.map(sector => (
                                <div key={sector} className="px-3 py-[2.06px] text-sm font-medium flex justify-between items-center hover:bg-muted"
                                  style={{ color: getSectorColor(sector) }}>
                                  <span>{sector}</span>
                                  <button
                                    type="button"
                                    onMouseDown={(e) => { e.stopPropagation(); handleRemoveSector(sector); }}
                                    className="text-muted-foreground hover:text-destructive transition-colors ml-2 text-xs"
                                  >×</button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {/* Search Input (Removed) */}
                        {/* Search Results Section */}
                        <div className="flex flex-row flex-1 overflow-hidden">
                          {/* Left column: Tickers */}
                          <div
                            className="w-[140px] shrink-0 border-r border-[#3a4252] overflow-y-auto"
                            onScroll={(e) => {
                              const target = e.target as HTMLElement;
                              const scrollBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
                              if (scrollBottom < 100 && tickerScrollOffset + ITEMS_PER_PAGE < filteredTickersList.length) {
                                setTickerScrollOffset(prev => prev + ITEMS_PER_PAGE);
                              }
                            }}
                          >
                            {tickerSearchTerm === '' ? (
                              <>
                                <div className="px-3 py-[2.06px] text-xs text-muted-foreground border-b border-[#3a4252] sticky top-0 bg-popover">
                                  Stocks ({filteredTickersList.length})
                                </div>
                                {visibleFilteredTickers.map((ticker, idx) => {
                                  const isSelected = liveTickerSelection.tickers.includes(ticker);
                                  return (
                                    <div
                                      key={`ticker-${ticker}`}
                                      onClick={() => {
                                        if (isSelected) {
                                          handleRemoveTicker(ticker);
                                        } else {
                                          handleTickerSelect(ticker);
                                        }
                                      }}
                                      className={`px-3 py-[2.06px] hover:bg-muted cursor-pointer text-sm flex justify-between items-center ${isSelected ? 'text-primary font-medium bg-accent/50' : ''} ${idx === highlightedTickerIndex ? 'bg-accent' : ''}`}
                                      onMouseEnter={() => setHighlightedTickerIndexOptimized(idx)}
                                    >
                                      <span>{ticker}</span>
                                      {isSelected && <span className="text-primary text-xs">✓</span>}
                                    </div>
                                  );
                                })}
                                {tickerScrollOffset + ITEMS_PER_PAGE < filteredTickersList.length && (
                                  <div className="px-3 py-2 text-xs text-muted-foreground text-center">
                                    Loading more... ({Math.min(tickerScrollOffset + ITEMS_PER_PAGE, filteredTickersList.length)} / {filteredTickersList.length})
                                  </div>
                                )}
                              </>
                            ) : filteredTickersList.length > 0 ? (
                              <>
                                <div className="px-3 py-[2.06px] text-xs text-muted-foreground border-b border-[#3a4252] sticky top-0 bg-popover">
                                  Stocks ({filteredTickersList.length})
                                </div>
                                {visibleFilteredTickers.map((ticker, idx) => {
                                  const isSelected = liveTickerSelection.tickers.includes(ticker);
                                  return (
                                    <div
                                      key={`ticker-${ticker}`}
                                      onClick={() => {
                                        if (isSelected) {
                                          handleRemoveTicker(ticker);
                                        } else {
                                          handleTickerSelect(ticker);
                                        }
                                      }}
                                      className={`px-3 py-[2.06px] hover:bg-muted cursor-pointer text-sm flex justify-between items-center ${isSelected ? 'text-primary font-medium bg-accent/50' : ''} ${idx === highlightedTickerIndex ? 'bg-accent' : ''}`}
                                      onMouseEnter={() => setHighlightedTickerIndexOptimized(idx)}
                                    >
                                      <span>{ticker}</span>
                                      {isSelected && <span className="text-primary text-xs">✓</span>}
                                    </div>
                                  );
                                })}
                                {tickerScrollOffset + ITEMS_PER_PAGE < filteredTickersList.length && (
                                  <div className="px-3 py-2 text-xs text-muted-foreground text-center">
                                    Loading more... ({Math.min(tickerScrollOffset + ITEMS_PER_PAGE, filteredTickersList.length)} / {filteredTickersList.length})
                                  </div>
                                )}
                              </>
                            ) : (
                              <div className="px-3 py-[2.06px] text-xs text-muted-foreground border-b border-[#3a4252] sticky top-0 bg-popover">
                                Stocks (0)
                              </div>
                            )}
                          </div>
                          {/* Right column: Sectors */}
                          <div
                            className="flex-1 overflow-y-auto"
                            onScroll={(e) => {
                              const target = e.target as HTMLElement;
                              const scrollBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
                              if (scrollBottom < 100 && sectorScrollOffset + ITEMS_PER_PAGE < filteredSectorsList.length) {
                                setSectorScrollOffset(prev => prev + ITEMS_PER_PAGE);
                              }
                            }}
                          >
                            {tickerSearchTerm === '' ? (
                              <>
                                <div className="px-3 py-[2.06px] text-xs text-muted-foreground border-b border-[#3a4252] sticky top-0 bg-popover">
                                  Sectors ({filteredSectorsList.length})
                                </div>
                                {visibleFilteredSectors.map((sector, idx) => {
                                  const itemIndex = filteredTickersList.length + idx;
                                  const isSelected = liveTickerSelection.sectors.includes(sector);
                                  return (
                                    <div
                                      key={`sector-${sector}`}
                                      onClick={() => {
                                        if (isSelected) {
                                          handleDeselectSector(sector);
                                        } else {
                                          handleSectorSelect(sector);
                                        }
                                      }}
                                      className={`px-3 py-[2.06px] hover:bg-muted cursor-pointer text-sm flex justify-between items-center ${isSelected ? 'bg-accent/50' : ''} ${itemIndex === highlightedTickerIndex ? 'bg-accent' : ''}`}
                                      style={{ color: getSectorColor(sector) }}
                                      onMouseEnter={() => setHighlightedTickerIndexOptimized(itemIndex)}
                                    >
                                      <span>{sector === 'IDX' ? 'IDX Composite' : sector}</span>
                                      {isSelected && <span className="text-primary text-xs">✓</span>}
                                    </div>
                                  );
                                })}
                                {sectorScrollOffset + ITEMS_PER_PAGE < filteredSectorsList.length && (
                                  <div className="px-3 py-2 text-xs text-muted-foreground text-center">
                                    Loading more... ({Math.min(sectorScrollOffset + ITEMS_PER_PAGE, filteredSectorsList.length)} / {filteredSectorsList.length})
                                  </div>
                                )}
                              </>
                            ) : filteredSectorsList.length > 0 ? (
                              <>
                                <div className="px-3 py-[2.06px] text-xs text-muted-foreground border-b border-[#3a4252] sticky top-0 bg-popover">
                                  Sectors ({filteredSectorsList.length})
                                </div>
                                {visibleFilteredSectors.map((sector, idx) => {
                                  const itemIndex = filteredTickersList.length + idx;
                                  const isSelected = liveTickerSelection.sectors.includes(sector);
                                  return (
                                    <div
                                      key={`sector-${sector}`}
                                      onClick={() => {
                                        if (isSelected) {
                                          handleDeselectSector(sector);
                                        } else {
                                          handleSectorSelect(sector);
                                        }
                                      }}
                                      className={`px-3 py-[2.06px] hover:bg-muted cursor-pointer text-sm flex justify-between items-center ${isSelected ? 'bg-accent/50' : ''} ${itemIndex === highlightedTickerIndex ? 'bg-accent' : ''}`}
                                      style={{ color: getSectorColor(sector) }}
                                      onMouseEnter={() => setHighlightedTickerIndexOptimized(itemIndex)}
                                    >
                                      <span>{sector === 'IDX' ? 'IDX Composite' : sector}</span>
                                      {isSelected && <span className="text-primary text-xs">✓</span>}
                                    </div>
                                  );
                                })}
                                {sectorScrollOffset + ITEMS_PER_PAGE < filteredSectorsList.length && (
                                  <div className="px-3 py-2 text-xs text-muted-foreground text-center">
                                    Loading more... ({Math.min(sectorScrollOffset + ITEMS_PER_PAGE, filteredSectorsList.length)} / {filteredSectorsList.length})
                                  </div>
                                )}
                              </>
                            ) : (
                              <div className="px-3 py-[2.06px] text-xs text-muted-foreground border-b border-[#3a4252] sticky top-0 bg-popover">
                                Sectors (0)
                              </div>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          )}

            {/* Date Range */}
          <div className="flex flex-col md:flex-row md:items-center gap-1 w-full md:w-auto">
            <label className="text-sm font-medium whitespace-nowrap shrink-0">Date Range:</label>
            <div className="flex items-center gap-2 w-full md:w-auto">
              <div
                className={`relative h-9 w-full ${isDashboard ? 'md:w-[125px]' : 'md:w-32'} rounded-md border border-input bg-background cursor-pointer hover:bg-accent/50 transition-colors`}
                onClick={() => triggerDatePicker(startDateRef)}
              >
                <input
                  ref={startDateRef}
                  type="date"
                  value={formatDateForInput(startDate)}
                  onChange={(e) => {
                    const selectedDateStr = e.target.value;
                    const selectedDate = new Date(selectedDateStr);
                    const dayOfWeek = selectedDate.getDay();
                    if (dayOfWeek === 0 || dayOfWeek === 6) {
                      showToast({
                        type: 'warning',
                        title: 'Peringatan',
                        message: 'Tidak bisa memilih hari Sabtu atau Minggu'
                      });
                      return;
                    }

                    // Check if date is before minimum date limit
                    if (selectedDateStr < MIN_DATE) {
                      showToast({
                        type: 'warning',
                        title: 'Tanggal Tidak Valid',
                        message: `Tanggal paling awal yang bisa dipilih adalah 10 Februari 2020. Silakan pilih tanggal setelahnya.`
                      });
                      if (startDateRef.current) {
                        startDateRef.current.value = formatDateForInput(startDate);
                      }
                      return;
                    }

                    // CRITICAL: Reset shouldFetchData silently BEFORE updating dates to prevent auto-load
                    // This ensures data only changes when Show button is clicked
                    shouldFetchDataRef.current = false; // CRITICAL: Update ref first (synchronous) - SILENT
                    setShouldFetchData(false); // SILENT

                    setStartDate(selectedDateStr);
                    if (!endDate || new Date(selectedDateStr) > new Date(endDate)) {
                      setEndDate(selectedDateStr);
                    }
                  }}
                  onKeyDown={(e) => e.preventDefault()}
                  onPaste={(e) => e.preventDefault()}
                  onInput={(e) => e.preventDefault()}
                  max={formatDateForInput(endDate)}
                  min={MIN_DATE}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  style={{ caretColor: 'transparent' }}
                />
                <div className="flex items-center justify-between h-full px-3">
                  <span className="text-sm text-foreground">
                    {startDate ? new Date(startDate).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    }) : 'DD/MM/YYYY'}
                  </span>
                  <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                </div>
              </div>
              <span className="text-sm text-muted-foreground whitespace-nowrap hidden md:inline shrink-0">to</span>
              <div
                className={`relative h-9 w-full ${isDashboard ? 'md:w-[125px]' : 'md:w-32'} rounded-md border border-input bg-background cursor-pointer hover:bg-accent/50 transition-colors`}
                onClick={() => triggerDatePicker(endDateRef)}
              >
                <input
                  ref={endDateRef}
                  type="date"
                  value={formatDateForInput(endDate)}
                  onChange={(e) => {
                    const selectedDateStr = e.target.value;
                    const selectedDate = new Date(selectedDateStr);
                    const dayOfWeek = selectedDate.getDay();
                    if (dayOfWeek === 0 || dayOfWeek === 6) {
                      showToast({
                        type: 'warning',
                        title: 'Peringatan',
                        message: 'Tidak bisa memilih hari Sabtu atau Minggu'
                      });
                      return;
                    }

                    // Check if date is before minimum date limit
                    if (selectedDateStr < MIN_DATE) {
                      showToast({
                        type: 'warning',
                        title: 'Tanggal Tidak Valid',
                        message: `Tanggal paling awal yang bisa dipilih adalah 10 Februari 2020. Silakan pilih tanggal setelahnya.`
                      });
                      if (endDateRef.current) {
                        endDateRef.current.value = formatDateForInput(endDate);
                      }
                      return;
                    }

                    // CRITICAL: Reset shouldFetchData silently BEFORE updating dates to prevent auto-load
                    // This ensures data only changes when Show button is clicked
                    shouldFetchDataRef.current = false; // CRITICAL: Update ref first (synchronous) - SILENT
                    setShouldFetchData(false); // SILENT

                    const newEndDate = e.target.value;
                    setEndDate(newEndDate);

                    // If endDate is before startDate, update startDate
                    if (startDate && new Date(newEndDate) < new Date(startDate)) {
                      setStartDate(newEndDate);
                    }
                  }}
                  onKeyDown={(e) => e.preventDefault()}
                  onPaste={(e) => e.preventDefault()}
                  onInput={(e) => e.preventDefault()}
                  min={formatDateForInput(startDate || MIN_DATE)}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  style={{ caretColor: 'transparent' }}
                />
                <div className="flex items-center justify-between h-full px-3">
                  <span className="text-sm text-foreground">
                    {endDate ? new Date(endDate).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    }) : 'DD/MM/YYYY'}
                  </span>
                  <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                </div>
              </div>
            </div>
            {/* Range 2+: + mengaktifkan Range 2, lalu toggle untuk edit (sama kayak Summary) */}
            <div className="relative" ref={rangeDropdownRef}>
              <button
                onClick={() => {
                  if (!showRange2 && extraRanges.length === 0) {
                    const defaultStart = nextTradingDay(endDate);
                    setRange2StartDate(defaultStart);
                    setRange2EndDate(defaultStart);
                    setShowRange2(true);
                    setShowRangeDropdown(true);
                    setExtraRanges([{ id: 'range2', startDate: defaultStart, endDate: defaultStart }]);
                  } else {
                    if (extraRanges.length === 0 && showRange2) {
                      setExtraRanges([{ id: 'range2', startDate: range2StartDate, endDate: range2EndDate }]);
                    }
                    setShowRangeDropdown(!showRangeDropdown);
                  }
                }}
                className="h-9 w-9 border border-[#3a4252] rounded-md bg-background text-foreground hover:bg-accent/50 transition-colors flex items-center justify-center flex-shrink-0"
                title={showRange2 || extraRanges.length > 0 ? "Edit Range" : "Tambah Range"}
              >
                {(showRange2 || extraRanges.length > 0) ? (
                  <ChevronDown className={`w-4 h-4 transition-transform ${showRangeDropdown ? "rotate-180" : ""}`} />
                ) : (
                  <span className="text-lg font-bold">+</span>
                )}
              </button>
              {showRangeDropdown && (
                <div className="absolute -right-2 top-full mt-1 bg-popover border border-[#3a4252] rounded-md shadow-lg z-50 p-2 flex flex-col gap-2">
                  {allExtraRanges.length > 0 ? (
                    <>
                      {allExtraRanges.map((range, idx) => (
                        <div key={range.id} className="flex items-center gap-1">
                          <span className="text-sm font-medium text-foreground whitespace-nowrap shrink-0">Range {idx + 2}:</span>
                          <div className="relative h-9 w-32 rounded-md border border-input bg-background cursor-pointer hover:bg-accent/50 transition-colors" onClick={() => (document.getElementById(`tx-extra-start-${range.id}`) as HTMLInputElement | null)?.showPicker()}>
                            <input id={`tx-extra-start-${range.id}`} type="date" value={range.startDate} onChange={(e) => {
                              const v = e.target.value;
                              if (v && v < MIN_DATE) return;
                              if (range.endDate && v > range.endDate) return;
                              setExtraRanges(prev => prev.map(r => r.id === range.id ? { ...r, startDate: v, endDate: r.endDate && v > r.endDate ? v : r.endDate } : r));
                              if (idx === 0) { setRange2StartDate(v); if (!range.endDate || v > range.endDate) setRange2EndDate(v); }
                            }} min={MIN_DATE} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" style={{ caretColor: 'transparent' }} />
                            <div className="flex items-center justify-between h-full px-3">
                              <span className="text-sm text-foreground">{range.startDate ? new Date(range.startDate).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'DD/MM/YYYY'}</span>
                              <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                            </div>
                          </div>
                          <span className="text-sm text-muted-foreground whitespace-nowrap shrink-0 px-1">to</span>
                          <div className="relative h-9 w-32 rounded-md border border-input bg-background cursor-pointer hover:bg-accent/50 transition-colors" onClick={() => (document.getElementById(`tx-extra-end-${range.id}`) as HTMLInputElement | null)?.showPicker()}>
                            <input id={`tx-extra-end-${range.id}`} type="date" value={range.endDate} onChange={(e) => {
                              const v = e.target.value;
                              if (v && v < MIN_DATE) return;
                              if (range.startDate && v < range.startDate) return;
                              setExtraRanges(prev => prev.map(r => r.id === range.id ? { ...r, endDate: v } : r));
                              if (idx === 0) setRange2EndDate(v);
                            }} min={range.startDate || MIN_DATE} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" style={{ caretColor: 'transparent' }} />
                            <div className="flex items-center justify-between h-full px-3">
                              <span className="text-sm text-foreground">{range.endDate ? new Date(range.endDate).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'DD/MM/YYYY'}</span>
                              <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                            </div>
                          </div>
                          <button onClick={() => {
                            if (allExtraRanges.length === 1) {
                              setExtraRanges([]);
                              setShowRange2(false);
                              setRange2StartDate('');
                              setRange2EndDate('');
                              setShowRangeDropdown(false);
                            } else {
                              setExtraRanges(prev => prev.filter(r => r.id !== range.id));
                              if (idx === 0) { setShowRange2(false); setRange2StartDate(''); setRange2EndDate(''); }
                            }
                          }} className="h-9 w-9 flex items-center justify-center border border-[#3a4252] rounded-md text-muted-foreground hover:text-red-500 hover:border-red-500 transition-colors text-lg flex-shrink-0" title="Hapus">&times;</button>
                        </div>
                      ))}
                    </>
                  ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground whitespace-nowrap shrink-0">Date Range 2:</span>
                    <div className="relative h-9 w-32 rounded-md border border-input bg-background cursor-pointer hover:bg-accent/50 transition-colors" onClick={() => (document.getElementById('tx-range2-start') as HTMLInputElement | null)?.showPicker()}>
                      <input id="tx-range2-start" type="date" value={range2StartDate} onChange={(e) => {
                        const v = e.target.value;
                        if (v && v < MIN_DATE) return;
                        if (v && endDate && v > endDate) return;
                        setRange2StartDate(v);
                        if (!range2EndDate || new Date(v) > new Date(range2EndDate)) setRange2EndDate(v);
                      }} min={endDate || MIN_DATE} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" style={{ caretColor: 'transparent' }} />
                      <div className="flex items-center justify-between h-full px-3">
                        <span className="text-sm text-foreground">{range2StartDate ? new Date(range2StartDate).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'DD/MM/YYYY'}</span>
                        <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      </div>
                    </div>
                    <span className="text-sm text-muted-foreground whitespace-nowrap shrink-0 px-1">to</span>
                    <div className="relative h-9 w-32 rounded-md border border-input bg-background cursor-pointer hover:bg-accent/50 transition-colors" onClick={() => (document.getElementById('tx-range2-end') as HTMLInputElement | null)?.showPicker()}>
                      <input id="tx-range2-end" type="date" value={range2EndDate} onChange={(e) => {
                        const v = e.target.value;
                        if (v && v < MIN_DATE) return;
                        if (v && range2StartDate && v < range2StartDate) return;
                        setRange2EndDate(v);
                      }} min={range2StartDate || endDate || MIN_DATE} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" style={{ caretColor: 'transparent' }} />
                      <div className="flex items-center justify-between h-full px-3">
                        <span className="text-sm text-foreground">{range2EndDate ? new Date(range2EndDate).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'DD/MM/YYYY'}</span>
                        <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      </div>
                    </div>
                    <button onClick={() => { setShowRange2(false); setRange2StartDate(''); setRange2EndDate(''); setExtraRanges([]); setShowRangeDropdown(false); }} className="h-9 w-9 flex items-center justify-center border border-[#3a4252] rounded-md text-muted-foreground hover:text-red-500 hover:border-red-500 transition-colors text-lg flex-shrink-0" title="Hapus">&times;</button>
                  </div>
                  )}

                  <button onClick={() => {
                    const lastEnd = extraRanges.length > 0 ? extraRanges[extraRanges.length - 1]!.endDate : range2EndDate;
                    const base = lastEnd || endDate;
                    let nextStart = '';
                    let nextEnd = '';
                    if (base) {
                      nextStart = nextTradingDay(base);
                      nextEnd = nextStart;
                    }
                    const newId = `range${extraRanges.length + 3}`;
                    setExtraRanges(prev => [...prev, { id: newId, startDate: nextStart, endDate: nextEnd }]);
                  }} className="h-9 w-full flex items-center justify-center border border-dashed border-[#3a4252] rounded-md text-muted-foreground hover:text-foreground hover:border-[#4a5568] transition-colors text-lg font-bold" title="Tambah Range">+</button>
                </div>
              )}
            </div>
          </div>

          {/* Pivot Filter disembunyikan di NewBrokerSummary (selalu mode Broker) */}
          {false && (
          <div className="flex flex-col md:flex-row md:items-center gap-2 w-full md:w-auto">
            <label className="text-sm font-medium whitespace-nowrap">Output:</label>
            <select
              value={pivotFilterDisplayLabel}
              onChange={(e) => {
                handlePivotFilterChange(e.target.value);
                // CRITICAL: Keep existing data visible - no auto-fetch, no hide tables
                // User must click Show button to fetch new data
              }}
              className={`h-9 ${isDashboard ? 'px-2' : 'px-3'} border border-[#3a4252] rounded-md bg-background text-foreground text-sm w-full md:w-auto`}
            >
              <option value="Stock">Stock</option>
              <option value="Broker">Broker</option>
            </select>
          </div>
          )}

          {/* F/D Filter (Foreign/Domestic) - Investor Type */}
          <div className="flex flex-col md:flex-row md:items-center gap-1 w-full md:w-auto">
            <label className="text-sm font-medium whitespace-nowrap">F/D:</label>
            <select
              value={invFilter}
              onChange={(e) => {
                setInvFilter(e.target.value as 'F' | 'D' | '');
                // CRITICAL: Keep existing data visible - no auto-fetch, no hide tables
                // User must click Show button to fetch new data
              }}
              className={`h-9 ${isDashboard ? 'px-2' : 'px-2'} border border-[#3a4252] rounded-md bg-background text-foreground text-sm w-full md:w-[80px]`}
            >
              <option value="">All</option>
              <option value="F">Foreign</option>
              <option value="D">Domestic</option>
            </select>
          </div>

          {/* Board Filter (RG/TN/NG) - Board Type */}
          <div className="flex flex-col md:flex-row md:items-center gap-1 w-full md:w-auto">
            <label className="text-sm font-medium whitespace-nowrap">Board:</label>
            <select
              value={boardFilter}
              onChange={(e) => {
                setBoardFilter(e.target.value as 'RG' | 'TN' | 'NG' | '');
                // CRITICAL: Keep existing data visible - no auto-fetch, no hide tables
                // User must click Show button to fetch new data
              }}
              className={`h-9 ${isDashboard ? 'px-2' : 'px-2'} border border-[#3a4252] rounded-md bg-background text-foreground text-sm w-full md:w-[62px]`}
            >
              <option value="">All</option>
              <option value="RG">RG</option>
              <option value="TN">TN</option>
              <option value="NG">NG</option>
            </select>
          </div>

          {/* Toggle Frequency, Order, NoSplit — 2×2 grid */}
          {(() => {
            const allOff = !draftShowFrequency && !draftShowOrder && !draftShowNoSplit;
            return (
              <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 justify-items-start">
                <label className="flex items-center gap-1.5 cursor-pointer hover:opacity-80 transition-opacity">
                  <input
                    type="checkbox"
                    checked={draftShowFrequency}
                    onChange={(e) => setDraftShowFrequency(e.target.checked)}
                    className="w-3.5 h-3.5 rounded border-[#3a4252] text-primary focus:ring-primary cursor-pointer"
                  />
                  <span className="text-xs text-foreground whitespace-nowrap">Frequency</span>
                </label>
                <label
                  className="flex items-center gap-1.5 cursor-pointer hover:opacity-80 transition-opacity"
                  title="Non Split — menu khusus bootcamp"
                  onClick={(e) => {
                    if (!hasBootcampAccess(profile)) {
                      e.preventDefault();
                      showToast({
                        type: 'warning',
                        title: 'Menu Khusus Bootcamp',
                        message: 'Fitur Non Split hanya tersedia untuk user yang sudah mengikuti bootcamp.'
                      });
                    }
                  }}
                >
                  <input
                    type="checkbox"
                    checked={hasBootcampAccess(profile) ? draftShowNoSplit : false}
                    onChange={(e) => {
                      if (!hasBootcampAccess(profile)) {
                        e.preventDefault();
                        return;
                      }
                      setDraftShowNoSplit(e.target.checked);
                    }}
                    className="w-3.5 h-3.5 rounded border-[#3a4252] text-primary focus:ring-primary cursor-pointer"
                  />
                  <span className="text-xs text-foreground whitespace-nowrap">Non Split</span>
                </label>
                <label
                  className="flex items-center gap-1.5 cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={(e) => {
                    if (isTrial) {
                      e.preventDefault();
                      showToast({
                        type: 'warning',
                        title: 'Fitur Terbatas',
                        message: 'Dalam masa free trial, fitur pilihan Order tidak tersedia.'
                      });
                    }
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isTrial ? false : draftShowOrder}
                    onChange={(e) => {
                      if (isTrial) {
                        e.preventDefault();
                        return;
                      }
                      setDraftShowOrder(e.target.checked);
                    }}
                    className="w-3.5 h-3.5 rounded border-[#3a4252] text-primary focus:ring-primary cursor-pointer"
                  />
                  <span className="text-xs text-foreground whitespace-nowrap">Order No.</span>
                </label>
                <label className="flex items-center cursor-pointer hover:opacity-80 transition-opacity">
                  <input
                    type="checkbox"
                    checked={allOff}
                    onChange={() => {
                      setDraftShowFrequency(false);
                      setDraftShowOrder(false);
                      setDraftShowNoSplit(false);
                    }}
                    className="w-3.5 h-3.5 rounded border-[#3a4252] text-primary focus:ring-primary cursor-pointer"
                  />
                </label>
              </div>
            );
          })()}

          {/* Block Filter - Default = auto top-5, Manual = pilih item (aturan sama kayak Summary) */}
          <div className="flex flex-col md:flex-row md:items-center gap-1 w-full md:w-auto">
            <label className="text-sm font-medium whitespace-nowrap">Track:</label>
            <div className="relative flex-1 md:flex-none" ref={blockDropdownRef}>
              <div
                className={`w-full md:w-[92px] px-2 h-9 flex items-center justify-between bg-background border border-[#3a4252] rounded-md text-sm text-foreground cursor-pointer hover:bg-accent/50 transition-colors`}
                onClick={() => setShowBlockDropdown(!showBlockDropdown)}
              >
                <span className="truncate">
                  {blockFilter === 'Default' ? 'Default' : selectedBlockItems.join(', ')}
                </span>
                <ChevronDown className="w-3 h-3 text-muted-foreground opacity-50 ml-1 flex-shrink-0" />
              </div>
              {showBlockDropdown && (
                <div id="block-suggestions" role="listbox" className="absolute top-full left-0 mt-1 bg-popover border border-[#3a4252] rounded-md shadow-lg z-50 w-36 max-h-60 overflow-y-auto flex flex-col">
                  {selectedBlockItems.length > 0 && blockFilter === 'Manual' && (
                    <div className="px-3 py-2 text-xs text-muted-foreground border-b border-[#3a4252] flex items-center justify-between bg-popover">
                      <span>Selected ({selectedBlockItems.length}/3)</span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedBlockItems([]);
                          setBlockFilter('Default');
                        }}
                        className="text-xs text-destructive hover:text-destructive/80 font-medium"
                      >
                        Clear
                      </button>
                    </div>
                  )}
                  <div className="p-2 border-b border-[#3a4252]">
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                      <input
                        type="text"
                        className="w-full h-8 pl-7 pr-2 bg-[#0a0f20] border border-[#3a4252] rounded text-sm text-white focus:outline-none focus:border-primary/50"
                        placeholder={pivotFilter === 'Stock' ? 'Search broker...' : 'Search stock...'}
                        value={blockSearchInput}
                        onChange={(e) => {
                          setBlockSearchInput(e.target.value.toUpperCase());
                        }}
                        onClick={(e) => e.stopPropagation()}
                        autoFocus
                      />
                    </div>
                  </div>
                  <div
                    className={`px-3 py-[2.06px] text-sm cursor-pointer flex justify-between items-center ${blockFilter === 'Default' ? 'text-primary font-medium' : ''}`}
                    onClick={() => { setBlockFilter('Default'); setSelectedBlockItems([]); setBlockSearchInput(''); setShowBlockDropdown(false); }}
                  >
                    <span>Default</span>
                    {blockFilter === 'Default' && <span className="text-primary text-xs">✓</span>}
                  </div>
                  {filteredBlockItems.map(code => {
                    const isSelected = selectedBlockItems.includes(code);
                    const isDisabled = !isSelected && selectedBlockItems.length >= 3;
                    const codeColor = pivotFilter === 'Stock' ? getBrokerColorClass(code) : null;
                    return (
                      <div
                        key={code}
                        className={`px-3 py-[2.06px] text-sm cursor-pointer flex justify-between items-center ${isSelected ? 'font-medium bg-accent/50' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isSelected) {
                            setSelectedBlockItems(prev => prev.filter(b => b !== code));
                            if (selectedBlockItems.length === 1) setBlockFilter('Default');
                          } else {
                            if (isDisabled) return;
                            setBlockFilter('Manual');
                            setSelectedBlockItems(prev => [...prev, code]);
                          }
                        }}
                      >
                        <span className={codeColor ? (codeColor.className || '') : 'text-white font-semibold'} style={codeColor ? { color: codeColor.color } : undefined}>{code}</span>
                        {isSelected && <span className="text-primary text-xs">✓</span>}
                      </div>
                    );
                  })}
                  {filteredBlockItems.length === 0 && (
                    <div className="px-3 py-2 text-sm text-muted-foreground text-center">No items found</div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 ml-auto">
            <button
              onClick={() => {
                if (!canUseAiFeatures) {
                  showToast({ type: 'warning', title: 'Menu khusus bootcamp' });
                  return;
                }
                setShowAIPopup(!showAIPopup);
              }}
              className="w-8 h-8 rounded-full bg-purple-500/20 border border-[#3a4252] flex items-center justify-center hover:bg-accent/50 transition-all duration-200 shrink-0"
              title="AI Insights — menu khusus bootcamp"
            >
              <span className="text-[11px] inline-flex items-end leading-none rounded-[3px] bg-purple-500/30 px-0.5"><span className="font-bold bg-gradient-to-r from-[#00ff00] to-[#ff0000] bg-clip-text text-transparent drop-shadow-[0_0_4px_rgba(168,85,247,0.9)]">Ai</span></span>
            </button>

            <button
              onClick={() => {
              // Auto-commit ketikan: "AADI, BKSL" langsung kepilih tanpa klik suggestion
              const typedSel = getBaseSelection();
              const effTickers = typedSel.tickers;
              const effSectors = typedSel.sectors;
              const typedText = tickerInput.trim();
              if (tickerTextDirty && typedText !== '' && typedSel.matched === 0) {
                // Ketikan tidak cocok saham/sektor mana pun — JANGAN lanjut (data lama ikut ke-wipe)
                showToast({ type: 'warning', title: 'Ticker tidak dikenal', message: `"${typedText.toUpperCase()}" tidak ada di daftar. Pilih dari suggestion.` });
                return;
              }
              if (tickerTextDirty) {
                applySelection(effTickers, effSectors);
                setShowTickerSuggestions(false);
              }
              // Commit draft toggles to effective states
              setShowFrequency(draftShowFrequency);
              setShowOrder(draftShowOrder);
              setShowNoSplit(draftShowNoSplit);

              // Commit draft selections to effective states
              // NewBrokerSummary: broker selalu ALL, pivot selalu Stock (rows = broker)
              setAppliedSelectedBrokers(['ALL']);
              setAppliedSelectedTickers([...effTickers]);
              setAppliedSelectedSectors([...effSectors]);
              setAppliedPivotFilter('Stock');
              setAppliedInvFilter(invFilter);
              setAppliedBoardFilter(boardFilter);

              const preferences: Partial<UserPreferences> = {
                selectedBrokers: ['ALL'],
                selectedTickers: effTickers,
                selectedSectors: effSectors,
                pivotFilter: 'Stock',
                invFilter,
                boardFilter,
                showFrequency: draftShowFrequency,
                showOrder: draftShowOrder,
                showNoSplit: draftShowNoSplit,
              };

              // Only save dates if they are explicitly selected AND not in dashboard
              if (startDate && endDate && !isDashboard) {
                preferences.startDate = startDate;
                preferences.endDate = endDate;
              }

              savePreferences(effectivePageId, preferences);

              // CRITICAL: Update selectedDates from startDate and endDate when Show button is clicked
              // This is the ONLY place where selectedDates should be updated
              let datesToUse: string[] = [];
              if (startDate && endDate) {
                // Parse dates as local dates (YYYY-MM-DD format) to avoid timezone issues
                const startParts = startDate.split('-').map(Number);
                const endParts = endDate.split('-').map(Number);

                if (startParts.length === 3 && endParts.length === 3) {
                  const startYear = startParts[0];
                  const startMonth = startParts[1];
                  const startDay = startParts[2];
                  const endYear = endParts[0];
                  const endMonth = endParts[1];
                  const endDay = endParts[2];

                  if (startYear !== undefined && startMonth !== undefined && startDay !== undefined &&
                    endYear !== undefined && endMonth !== undefined && endDay !== undefined) {
                    const start = new Date(startYear, startMonth - 1, startDay);
                    const end = new Date(endYear, endMonth - 1, endDay);

                    // Check if range is valid
                    if (start <= end) {
                      // Generate date array (only trading days)
                      const dateArray: string[] = [];
                      const currentDate = new Date(start);

                      while (currentDate <= end) {
                        const dayOfWeek = currentDate.getDay();
                        // Skip weekends (Saturday = 6, Sunday = 0)
                        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
                          // Format as YYYY-MM-DD in local timezone
                          const year = currentDate.getFullYear();
                          const month = String(currentDate.getMonth() + 1).padStart(2, '0');
                          const day = String(currentDate.getDate()).padStart(2, '0');
                          const dateString = `${year}-${month}-${day}`;
                          dateArray.push(dateString);
                        }
                        // Move to next day
                        currentDate.setDate(currentDate.getDate() + 1);
                      }

                      // Sort by date (oldest first) for display
                      datesToUse = dateArray.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
                    }
                  }
                }
              }

              // Multi-range (sama kayak Summary): gabungkan tanggal semua range.
              // Extra ranges hanya ikut fetch & tampil setelah Show diklik.
              const legacyExtra = showRange2 && range2StartDate && range2EndDate
                ? [{ id: 'range2', startDate: range2StartDate, endDate: range2EndDate }]
                : [];
              const effectiveExtras = allExtraRanges.length > 0 ? allExtraRanges : legacyExtra;
              const completeExtras = effectiveExtras.filter(r => r.startDate && r.endDate);
              if (startDate && endDate && completeExtras.length > 0) {
                // Auto-urutkan: range paling awal jadi R1 (sinkron ke state biar konsisten)
                const unsortedMetas = [{ id: 'range1', startDate, endDate }, ...completeExtras];
                const sortedMetas = [...unsortedMetas].sort((a, b) => a.startDate.localeCompare(b.startDate) || a.endDate.localeCompare(b.endDate));
                const isAlreadySorted = JSON.stringify(sortedMetas.map(m => m.startDate + '|' + m.endDate)) === JSON.stringify(unsortedMetas.map(m => m.startDate + '|' + m.endDate));
                if (!isAlreadySorted) {
                  const first = sortedMetas[0]!;
                  if (first.startDate !== startDate) setStartDate(first.startDate);
                  if (first.endDate !== endDate) setEndDate(first.endDate);
                  const rest = sortedMetas.slice(1);
                  setExtraRanges(rest.map((r, idx) => ({ id: `range${idx + 2}`, startDate: r.startDate, endDate: r.endDate })));
                  if (rest.length > 0) {
                    setShowRange2(true);
                    setRange2StartDate(rest[0]!.startDate);
                    setRange2EndDate(rest[0]!.endDate);
                  }
                  showToast({ type: 'info', title: 'Range Diurutkan', message: `Range diurutkan otomatis: paling awal jadi R1 (${formatRangeLabel(first.startDate, first.endDate)}).` });
                }
                const sortedAllDates = sortedMetas.map(r => generateTradingDays(r.startDate, r.endDate)).flat();
                const merged = Array.from(new Set([...datesToUse, ...sortedAllDates])).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
                datesToUse = merged;
                setDisplayedRangesMeta(sortedMetas.filter(r => r.startDate && r.endDate));
              } else if (startDate && endDate) {
                setDisplayedRangesMeta([{ id: 'range1', startDate, endDate }]);
              } else {
                setDisplayedRangesMeta([]);
              }

              // Determine active sector filter from selectedSectors
              // If multiple sectors selected, use first one (or could combine logic)
              // For now, if any sector selected, use first one; otherwise 'All'
              const newActiveSectorFilter: string = effSectors.length > 0 ? (effSectors[0] ?? 'All') : 'All';

              // CRITICAL: Always update activeSectorFilter first - sector filter should always work
              // Update activeSectorFilter from selectedSectors BEFORE checking for changes
              setActiveSectorFilter(newActiveSectorFilter);

              // Check if only sector filter changed (and data already exists)
              // CRITICAL: Also check if tickers changed - if tickers changed, need to refetch data
              const lastParams = lastFetchParamsRef.current;
              const previousTickers = lastParams?.tickers || [];
              const previousSectors = lastParams?.sectors || [];
              const tickersChanged = !lastParams ||
                JSON.stringify(previousTickers.sort()) !== JSON.stringify([...effTickers].sort());
              const sectorsChanged = !lastParams ||
                JSON.stringify(previousSectors.sort()) !== JSON.stringify([...effSectors].sort());

              // IMPORTANT: selectedTickers can be empty (length === 0) which means "show all tickers"
              // This is handled in loadTransactionData where filtering only happens if selectedTickers.length > 0

              // CRITICAL: Update selectedDates FIRST before checking for changes
              // This ensures that date comparison works correctly
              setSelectedDates(datesToUse);

              // CRITICAL: Check if only sector filter changed (and data already exists)
              // This allows sector filter to work without re-fetching data
              // Compare dates using datesToUse (what user selected) - now that selectedDates is updated
              const datesUnchanged = lastParams &&
                JSON.stringify(lastParams.dates.sort()) === JSON.stringify([...datesToUse].sort());

              const brokersUnchanged = lastParams &&
                JSON.stringify(lastParams.brokers.sort()) === JSON.stringify([...selectedBrokers].sort());

              const pivotUnchanged = lastParams && lastParams.pivot === pivotFilter;
              const invUnchanged = lastParams && lastParams.inv === invFilter;
              const boardUnchanged = lastParams && lastParams.board === boardFilter;

              const anyParamChanged = !lastParams ||
                tickersChanged || sectorsChanged || !brokersUnchanged || !datesUnchanged ||
                !pivotUnchanged || !invUnchanged || !boardUnchanged;

              const onlySectorChanged = lastParams &&
                isDataReady &&
                !tickersChanged && // Tickers must not have changed
                sectorsChanged && // Sectors must have changed
                brokersUnchanged && // Brokers must not have changed
                datesUnchanged && // Dates must not have changed (compare with datesToUse)
                pivotUnchanged && // Pivot must not have changed
                invUnchanged && // Inv must not have changed
                boardUnchanged; // Board must not have changed

              if (onlySectorChanged) {
                // Only sector filter changed (and tickers didn't change) - filter existing data without re-fetching
                // CRITICAL: We need to re-filter rawTransactionData based on new selectedSectors

                // OPTIMIZED: No need to manual filter. useMemo 'transactionData' will automatically re-calc
                // based on changed 'selectedSectors' when render cycle updates.

                // Update last fetch params to track sector change
                lastFetchParamsRef.current = {
                  ...lastParams,
                  sectors: [...effSectors],
                  dates: [...datesToUse] // Update dates in case they changed
                };
                // CRITICAL: Ensure data is ready and visible after sector filter change
                setIsDataReady(true);
                return;
              }

              // NEW: If no functional parameters changed (only toggles might have), and data is already ready, 
              // we can return early as we already committed the toggles at the start of this handler.
              if (!anyParamChanged && isDataReady) {
                return;
              }

              // Brokers, dates, market, tickers, or sectors changed - need to fetch new data from API
              // NOTE: If selectedTickers is empty, it will show all tickers (no filtering)

              // Update last fetch params
              // NewBrokerSummary: broker selalu ALL, pivot selalu Stock
              lastFetchParamsRef.current = {
                brokers: ['ALL'],
                dates: [...datesToUse],
                pivot: 'Stock', // Track pivotFilter
                inv: invFilter || '', // Track invFilter
                board: boardFilter || '', // Track boardFilter
                tickers: [...effTickers], // Track selectedTickers
                sectors: [...effSectors] // Track selectedSectors
              };

              // Clear existing data before fetching new data

              setRawTransactionData(new Map()); // Clear raw data too
              setIsDataReady(false);

              // CRITICAL: Reset column width sync flag when clearing data
              hasSyncedColumnWidthsRef.current = false;
              columnWidthsRef.current = [];
              // Mulai lagi dari 20 row agar Show berikutnya ringan (lazy-load ulang)
              setVisibleRowCount(MAX_DISPLAY_ROWS);
              // Set ref first (synchronous), then state (async)
              shouldFetchDataRef.current = true;
              setShouldFetchData(true);
              fetchGenRef.current += 1;
              setFetchGen(fetchGenRef.current);
            }}
            disabled={isLoading ||
              (pivotFilter === 'Broker' && selectedBrokers.length === 0 && selectedSectors.length === 0) ||
              // Pakai liveTickerSelection (bukan parse teks saja): setelah commit di mousedown teks kosong,
              // kalau dihitung dari teks tombol jadi disabled sebelum click → Show tidak jalan.
              // Teks yang sedang diketik tetap boleh klik supaya warning "Ticker tidak dikenal" muncul.
              (pivotFilter === 'Stock' && liveTickerSelection.tickers.length === 0 && liveTickerSelection.sectors.length === 0 &&
                !(tickerTextDirty && tickerInput.trim() !== '')) ||
              !startDate || !endDate}
            // NOTE: For Stock pivot, either selectedTickers or selectedSectors must be selected
            // selectedTickers can be empty if selectedSectors is selected (for sector/IDX data)
            className="h-8 text-sm px-4 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium whitespace-nowrap flex items-center justify-center"
          >
            Show
          </button>
            <Button
              onClick={() => {
                setExtraRanges([]);
                setShowRange2(false);
                setRange2StartDate('');
                setRange2EndDate('');
                setShowRangeDropdown(false);
                setBlockFilter('Default');
                setSelectedBlockItems([]);
                setBlockSearchInput('');
                showToast({ type: 'info', title: 'Reset', message: 'Range & Block direset. Klik Show untuk apply.' });
              }}
              variant="outline"
              size="sm"
              className="flex items-center gap-2 h-8"
              title="Reset range & block"
            >
              <Undo2 className="w-4 h-4 rotate-180" />
              Reset
            </Button>
          </div>
        </div>
      </div>



      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-16 pt-4">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <span className="text-sm text-muted-foreground">Loading transaction data...</span>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <div className="flex items-center justify-center py-8 pt-4">
          <div className="text-sm text-destructive">{error}</div>
        </div>
      )}

      {/* Main Data Display */}
      <div className="flex-1 overflow-hidden pt-2">
        {horizontalView}
      </div>
    </div>
  );
}